import { BUILD } from "./build";
import { bagFill, bagWeightTip, fmtCn } from "./bagweight";
import { Game } from "./game";
import { sameItem } from "./pile";
import { howManyLayout, presetLots } from "./howmany";
import { wirePullToReload } from "./loginpull";
import { lockPageZoom } from "./zoomlock";
import { discordInvite, followPickedWorld, PingMeter, WorldPicker } from "./worlds";
import { characterHash, characterInHash } from "./charlinks";
import { copyText } from "./clipboard";
import { Net, PROTOCOL_VERSION } from "./net";
import { CLICK_RANGE, MAX_ROUTE_STEPS, RoutePlanner, routeSteps } from "./pathfind";
import { MarketPanel } from "./market";
import { npcEntries } from "./npcmenu";
import { showPopMenu, type PopItem } from "./popmenu";
import type { BagSort } from "./bag";
import { PlayersPanel, nameSpells, putInChat } from "./players";
import { MAX_DPR, Renderer, setLabelFont } from "./render";
import { creatureSprite, inkTop, itemSprite, NO_GEAR, prewarmTiles } from "./sprites";
import { longPress, longPressedAt, stickDirFor, TOUCH_SLOP, twoFingerMode, twoFingers, twoFingerStep, type PressAt, type TwoFingerMode, type TwoFingers } from "./touch";
import type { Changes, Character, CharLine, Highscores, Item, ItemDef, SpellView, Trade, You } from "./types";
import { UI, availableGold, levelName, tiles, type HowMany, type SlotMenu } from "./ui";
import { viewGlyph } from "./viewicons";
import { markOpen, placeIconFollows } from "./urgenticon";
import { Sounds } from "./sound";
import { attackForbids, nextStance } from "./stance";
import { nextAutoStance } from "./autostance";
import { num } from "./numfmt";
import type { ClientCreature, Picked } from "./game";
import { keptContext } from "./paper";

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;

// To the world picked last time, if this is another (see worlds.ts).
followPickedWorld();

// The opening of the server's refusal for a stale bundle, and the flag that
// stops the reload it triggers from happening twice.
const STALE_CLIENT = "Your game client is out of date.";
const RELOADED = "tinytibia.reloaded";

/**
 * Fetch the current bundle, because this page is not the one the server is
 * serving any more.
 *
 * The guard is against one thing: a reload that comes back with the very
 * bundle it was meant to replace — a proxy serving yesterday's file, a
 * build id that does not match its own bundle — where a page that reloaded
 * every time would spin instead of saying so. So what is remembered across
 * the reload is the build it was made from, and the reload is refused only
 * when this page is still that build; then the page says what to do by
 * hand. It used to be a flag that only a welcome cleared (2026-10-07: "i'm
 * still seeing "client is out of date", why doesn't it reload
 * automatically?"): a tab that reloaded for one deploy and was not logged
 * in before the next sat on the second one, refusing to reload because it
 * already had once.
 *
 * Before the reload the page, the stylesheet and the bundle are fetched
 * again past the browser's cache, so the reload finds the fresh copies
 * even where something between here and the server held on to the old.
 *
 * It is a helper rather than two copies because there are two ways to find
 * out, and they must behave the same way: the status poll notices before
 * anybody types anything, and the login refusal catches a server that was
 * replaced between the two.
 */
function reloadForNewBuild(why: string): void {
  let from: string | null = null;
  try {
    from = sessionStorage.getItem(RELOADED);
  } catch {
    // Storage is off: nothing is remembered, and the reload goes ahead.
  }
  if (from === BUILD) {
    loginErr.textContent = `${why} Reloading by itself did not bring the new version: reload the page yourself (Ctrl+Shift+R, or pull down on a phone).`;
    return;
  }
  try {
    sessionStorage.setItem(RELOADED, BUILD);
  } catch {
    // A browser with storage turned off reloads every time, or never;
    // either way the message on screen says what to do.
  }
  keepLoginAcrossReload();
  loginErr.textContent = why + " Fetching it now…";
  const fresh = ["./", "style.css", "dist/app.js"].map((u) => fetch(u, { cache: "reload" }).catch(() => undefined));
  void Promise.all(fresh).finally(() => location.reload());
}

// A reload that brought a different bundle did its work: the guard is
// cleared at once, so the next deploy reloads too, logged in or not.
try {
  const from = sessionStorage.getItem(RELOADED);
  if (from !== null && from !== BUILD) sessionStorage.removeItem(RELOADED);
} catch {
  // Storage is off; there is no guard to clear.
}

/**
 * The name and password last logged in with, so the login screen can be
 * put back in front of somebody ready to log straight back in. Asked for on
 * 2026-09-23: "when returned to the login screen, keep the password that
 * was used ready to log straight back in."
 *
 * Coming back to the form without the page going away already kept it —
 * nothing clears the boxes — so the case this is for is the one that took
 * it: a deploy. A server that stops puts everybody back at the form, the
 * form's status check notices the new build and reloads the page, and a
 * reload empties a password box. That was on purpose until now ("a
 * credential written into storage to save one click is a bad trade"), and
 * it cost a retyped password on every deploy, which is the trade the
 * request took the other side of.
 *
 * So the credential crosses that one reload and nothing else, on the
 * narrowest shelf there is. It goes into sessionStorage — this tab only,
 * gone when the tab closes, never written to disk as localStorage is —
 * only on the game's own reload for a new build, and is taken back off the
 * moment the new page starts (restoreLoginAfterReload), so it sits there
 * for the length of one page load. It fills the form and does not press
 * the button: logging in is still the player's click, which is what makes
 * a refusal after the reload a refusal they see rather than one that
 * happened to them.
 */
const RELOGIN = "tinytibia.relogin";
let lastLogin: { name: string; password: string } | null = null;

function keepLoginAcrossReload(): void {
  const name = $<HTMLInputElement>("username").value || lastLogin?.name || "";
  const password = $<HTMLInputElement>("password").value || lastLogin?.password || "";
  if (!name || !password) return;
  try {
    sessionStorage.setItem(RELOGIN, JSON.stringify({ name, password }));
  } catch {
    // No storage: the reload empties the box, as it always did.
  }
}

/** Put the kept name and password back in the form, once, and forget them. */
function restoreLoginAfterReload(): void {
  let kept: { name?: string; password?: string } | null = null;
  try {
    const raw = sessionStorage.getItem(RELOGIN);
    sessionStorage.removeItem(RELOGIN);
    kept = raw ? JSON.parse(raw) : null;
  } catch {
    return;
  }
  if (!kept?.name || !kept.password) return;
  $<HTMLInputElement>("username").value = kept.name;
  $<HTMLInputElement>("password").value = kept.password;
  lastLogin = { name: kept.name, password: kept.password };
  $<HTMLInputElement>("password").focus(); // Enter logs straight back in
}

const game = new Game();
const net = new Net();
const canvas = $<HTMLCanvasElement>("game");
const renderer = new Renderer(canvas, game);
const ui = new UI(
  game,
  // The owner goes with the id on every click that names an item: a bag can
  // hold two people's skulls, and the id alone no longer says which pile
  // was meant. It is absent from everything that has no owner, which is
  // everything but a skull, and omitted fields cost nothing on the wire.
  (slot, it) => net.send({ t: "use", slot, id: it.id, owner: it.owner, var: it.var }),
  (slot, it, depot) => net.send({ t: depot ? "lookdepot" : "lookslot", slot, id: it.id, owner: it.owner, var: it.var }),
  // A count to drop so many, since the quantity dialog (2026-09-27); nought
  // is the whole of the kind.
  (slot, it, count) => net.send(count > 0 ? { t: "drop", slot, id: it.id, owner: it.owner, var: it.var, count } : { t: "drop", slot, id: it.id, owner: it.owner, var: it.var, all: true }),
  // count is how many to move and nought is a square's worth, which is
  // what a tap sends; omitempty leaves it off the wire in that case.
  (slot, it, count) => net.send({ t: "deposit", slot, id: it.id, owner: it.owner, var: it.var, count }),
  (slot, it, count) => net.send({ t: "withdraw", slot, id: it.id, owner: it.owner, var: it.var, count }),
  (m) => showSlotMenu(m),
  (it, count) => net.send({ t: "tradeoffer", id: it.id, owner: it.owner, var: it.var, count }),
  // An icon in the bottom corner saying what is lying in reach, pressed.
  // It is the same pickup the left button on that tile sends and the same
  // one the right button sends, with the same coordinates: the icon is a
  // bigger target for a gesture the game already had, not a new one,
  // which is why there is nothing here but the message. The server picks
  // what comes off the top of the pile, as it does for the other two.
  (x, y) => net.send({ t: "pickup", x, y }),
  // A spell button. What it sends is the incantation, said into the world
  // exactly as though it had been typed into the chat box — there is no
  // "cast this" message and there should not be one, because saying the
  // words is what casting is, everyone standing near you hears them, and a
  // second route into the spell would be a second place for the rules to
  // be checked. It goes out whether or not the button was red: the colour
  // is the server's own verdict from a tick ago, and the refusal that
  // matters is the one that comes back in the console.
  //
  // The exception is a spell that wants a word after the incantation, which
  // the server says by sending an `arg`. "tiro" on its own is not a
  // sentence and the world would answer "Find whom?" every single time, so
  // the words go into the chat box with the cursor after them instead and
  // the player finishes it. Nothing is guessed here about which spells
  // those are: the server said.
  //
  // A spell that wants somebody standing in sight ("near") offers them
  // instead: the players on your floor that this client can see, nearest
  // first, one button each, and the chat box as the last resort. Asked for
  // on 2026-09-23: "when casting blood bond, pop up names of players
  // nearby and select one." See pickNearby.
  (sp) => {
    if (sp.arg === "near") {
      showSlotMenu(pickNearby(sp));
      return;
    }
    // A spell said with a place after it (the Eagles, 2026-10-10) offers
    // the places the server says it can be said with.
    if (sp.arg === "place") {
      showSlotMenu(pickPlace(sp));
      return;
    }
    // A spell said with a creature after it (summon creature, 2026-11-02:
    // "a menu appears and you choose the creature") offers the kinds the
    // server says the caster's pool could hold.
    if (sp.arg === "creature") {
      showSlotMenu(pickCreature(sp));
      return;
    }
    // A spell said at somebody anywhere in the world by name (find person)
    // offers everybody on the Players panel (2026-10-15: "Clicking the
    // find person spell should show a menu to choose the person").
    if (sp.arg === "name") {
      showSlotMenu(pickPerson(sp));
      return;
    }
    if (sp.arg) {
      chat.value = `${sp.words} `;
      chat.focus();
      return;
    }
    net.send({ t: "say", text: sp.words });
  },
  // Reply on a line of the log (2026-10-08): their name in the chat box at
  // the caret, with a comma after it, and the box open to carry on typing.
  (name) => putInChat(chat, `${name},`),
  // Message, or Reply on a private line (2026-10-17): a private message
  // to them started in the chat box.
  (name) => startTell(name),
  // Look on a line of the log: the shift-click on where they stand.
  (x, y) => net.send({ t: "look", x, y }),
  // A long press on the light's button or its status icon: put it out.
  () => net.send({ t: "luxout" }),
);
// A click in the Vendor panel buys one lot: the server's buy checks that
// Grubb is beside you, the purse and the bag, and says why when it will not.
// A number from the quantity dialog goes out marked as the panel's, so the
// server does not hold it to a right-click's ten; a click is one lot.
ui.onBuy = (id, lots) => net.send({ t: "buy", id, lots, panel: lots !== undefined });
ui.onLookVendor = (id) => net.send({ t: "lookvendor", id });
// A place on the Equipment panel (2026-10-06: "Add a panel for equipped
// items ... selecting a slot lets you choose from valid items from your
// inventory"): what in the bag goes there, the bag's own choice back, and
// nothing. The server says what goes where (You.worn); this only asks.
//
// It is the small menu beside the square, a picture and a name a row,
// since 2026-10-09: "show a context menu for inventory slot item selection
// instead of the fullscreen thing. long pressing on an option gives more
// details, show just the bare minimum in the menu". It was the box in the
// middle of a darkened screen until then, every choice with its numbers
// under it; the numbers are a row's hover now, and its long press or
// right-click. The main ones came back as a short line under each name on
// 2026-10-19, and all of them on 2026-11-02 (see the choice's detail).
ui.onEquipSlot = (w, at) => {
  const name = (id: number) => game.itemDefs.get(id)?.name ?? "something";
  // A pile in the bag, by kind and variant: what names a numbered Ring.
  const pile = (id: number, v?: string) => (game.you?.inv ?? []).find((it) => sameItem(it, { id, var: v }));
  const equip = (id: number, v?: string) => net.send({ t: "equip", place: w.place, id, var: v || undefined });
  // The place's main number first, highest first, then what one of it is
  // worth rather than the pile (2026-10-27: "the equipment menu should
  // show items that fit the slot ordered by invididual item value
  // descending", then "order by the most important stat first (atk for
  // main hand weapon, block/parry for offhand, etc.), then by value").
  // The off hand counts block and parry together, a shield blocking and a
  // knife parrying; a ring has no one number and goes by worth alone.
  // Equal ones keep the server's order, which is by name.
  const main = (d: ItemDef | undefined) =>
    !d ? 0 :
    w.place === "weapon" ? d.attack ?? 0 :
    w.place === "shield" ? (d.shield ?? 0) + (d.defense ?? 0) :
    w.place === "helm" ? d.helm ?? 0 :
    w.place === "armor" ? d.armor ?? 0 :
    w.place === "cloak" ? d.cloak ?? 0 :
    w.place === "belt" ? d.belt ?? 0 :
    0;
  const choices = w.options
    .map((id, i) => {
      const v = w.optionvars?.[i] || undefined;
      const d = game.defOf(pile(id, v) ?? { id });
      return { id, v, main: main(d), worth: d?.price ?? 0 };
    })
    .sort((a, b) => b.main - a.main || b.worth - a.worth);
  const items: PopItem[] = choices.map(({ id, v }) => {
    const c = document.createElement("canvas");
    c.width = c.height = 32;
    keptContext(c).drawImage(itemSprite(id, undefined, game.defOf(pile(id, v) ?? { id })?.make), 0, 0);
    return {
      label: pile(id, v)?.name ?? name(id),
      icon: c,
      // As many as the bag holds of it that are anybody's to wear: a skull
      // with a name on it is never offered (see fitsHere).
      count: pile(id, v)?.count ?? 0,
      checked: id === w.id && (v ?? "") === (w.var ?? ""),
      // All its numbers under the name, so the choices can be weighed
      // without a hover or a hold on each (2026-11-02: "show the details
      // of items in the equipment panel context menu without having to
      // right click or long press"). Only the main ones were there from
      // 2026-10-19 ("show basic stats of the choices"), the rest behind the row's hover and hold; the whole line is a
      // superset of them, so it takes their place rather than repeat them.
      detail: ui.itemFacts(id, pile(id, v)?.def),
      open: true,
      go: () => equip(id, v),
    };
  });
  if (w.auto && w.pick !== "auto") items.push({ label: "The best in my bag", checked: false, go: () => equip(0) });
  if (w.id !== 0) items.push({ label: w.place === "weapon" ? "Bare hands" : "Take it off", checked: false, go: () => equip(-1) });
  if (!items.length) items.push({ label: "Nothing in your bag goes here", disabled: true, go: () => {} });
  const head =
    w.ammo ? `${w.label}: ${ammoLeft(name(w.ammo), w.ammocount ?? 0)}` :
    w.busy ? `${w.label}: both hands are on the weapon` :
    w.label;
  showPopMenu({ label: w.label, head, items }, at.getBoundingClientRect(), { back: at, on: at });
};
// Equip and Unequip in the bag's quantity dialog send the menu's own equip.
ui.onEquip = (place, id, v) => net.send({ t: "equip", place, id, var: v || undefined });
// A long press or a right-click on a place says what is in it, beside it
// (2026-10-09: "long-pressing or right clicking an item in the equipment
// panel should show item information"): its name, its numbers, what a look
// at it says, and why it is there. A press still chooses.
ui.onEquipInfo = (w, at) => {
  const shown = w.ammo || w.id;
  const worn = w.id ? (game.you?.inv ?? []).find((it) => sameItem(it, { id: w.id, var: w.var })) : undefined;
  const def = w.ammo ? game.itemDefs.get(w.ammo) : w.id ? game.defOf(worn ?? { id: w.id }) : undefined;
  const why =
    w.ammo ? `${w.ammocount ?? 0} in your bag to shoot.` :
    w.busy ? "Both hands are on the weapon." :
    w.pick === "chosen" ? "Chosen by you." :
    w.pick === "off" ? (w.auto ? "Taken off by you." : "Nothing on.") :
    w.id ? "The best in your bag." :
    w.options.length ? "Nothing on." : "Nothing in your bag goes here.";
  const about = def ? [ui.itemFacts(shown, w.ammo ? undefined : worn?.def), def.look ?? "", why].filter((l) => l) : [why];
  showPopMenu({ label: w.label, head: def ? `${w.label}: ${def.name}` : w.label, about, items: [] }, at.getBoundingClientRect(), { back: at, on: at });
};
/** What the off hand's head line says while it holds a bow's arrows or a crossbow's bolts. */
function ammoLeft(ammo: string, left: number): string {
  return left ? `${left} ${left === 1 ? ammo : ammo + "s"} left` : `no ${ammo}s left`;
}
// Grubb's market (2026-10-07: "add a market feature Grubb. you can buy and
// sell items to other players"): the Market panel, drawn from the state's
// market, and its buttons sent as they are. See market.ts.
const market = new MarketPanel($("market"), {
  send: (m) => net.send(m),
  nameOf: (it) => game.defOf(it)?.name ?? "something",
  icon: (it) => {
    const c = document.createElement("canvas");
    c.width = c.height = 32;
    keptContext(c).drawImage(itemSprite(it.id, it.color, game.defOf(it)?.make), 0, 0);
    return c;
  },
  hold: (el, fire) => longPress(el, fire, (ev) => !(ev.target as Element | null)?.closest?.("button, input")),
});
// --- market dialog ----------------------------------------------------------

const marketDlg = $("marketdlg");
const marketBox = $("marketbox");

/** The dialog the Market panel stands in while it is open: see layoutPanels. */
function showMarketDlg(open: boolean) {
  if (marketDlg.hidden === !open) return;
  marketDlg.hidden = !open;
  if (open) {
    held.clear();
    $("marketclose").focus();
  } else if (document.activeElement === document.body || marketDlg.contains(document.activeElement)) canvas.focus();
}
/** Shut the Market, as a second press on its icon does. */
function shutMarket(_open = false) {
  showPanel($("marketpanel"), false, false);
}
$("marketclose").addEventListener("click", () => shutMarket());
marketDlg.addEventListener("mousedown", (ev) => {
  if (ev.target === marketDlg) shutMarket();
});

// --- depot screen -----------------------------------------------------------

/*
 * The depot screen (2026-10-17: "opening the depot should show a
 * market/depot/inventory management screen, propose how that could look",
 * then "implement the depot screen"): pressing the depot's chest icon opens
 * one screen over the world with the bag, the depot and Grubb's market side
 * by side, where the Depot panel stood beside the world until then and the
 * market was a dialog of its own behind a stall icon of its own. The
 * squares and the market are the panels' own, moved in here while it is
 * open and back when it shuts, so everything they do they still do: a
 * click on the bag stores, the long press asks how many. A click on a
 * depot square picks it, for the bar under the depot that takes it out or
 * lists it with Grubb. One filter names all three; the foot has the gold
 * in each and the buttons that empty a bag after a hunt. On a phone the
 * depot stands over the bag and the market is a second tab.
 */
const depotDlg = $("depotdlg");
const depotBox = $("depotbox");
const depotFilter = $("depotfilter") as HTMLInputElement;
/** The pieces lent to the screen, each with the place it goes back to. */
const depotLent: [HTMLElement, HTMLElement, HTMLElement][] = [
  [$("inv"), $("invbody"), $("depotbagslot")],
  [$("depot"), $("depotbody"), $("depotgoodsslot")],
  [$("market"), $("marketbody"), $("depotmarketslot")],
];
/*
 * The screen is in the view, over the world only (2026-10-19: "make the
 * depot dialog pop up over the game view, not the whole screen, so we can
 * still see the message log"), so the log under it and the panels beside
 * it stay in sight. The item dialog (#selldlg) is in the view after it, so
 * a right-click or a long press on the bag or the depot opens it over the
 * screen (2026-10-18: "right clicking items in the depot dialog isn't
 * showing the dialog ..."); while the screen covered the whole page the
 * dialog had to be moved out over it.
 */
/** The depot square picked, by kind and owner, or "" for none. */
let depotPicked = "";

function showDepotDlg(open: boolean) {
  if (depotDlg.hidden === !open) return;
  // One screen over the world at a time: this or the shop.
  if (open && !vendorDlg.hidden) {
    shutVendor();
    if (depotDlg.hidden === !open) return;
  }
  depotDlg.hidden = !open;
  paintDrawer();
  for (const [piece, home, there] of depotLent) (open ? there : home).append(piece);
  if (open) {
    held.clear();
    fitDepotDlg();
    $("depotsellslot").append(market.formElement);
    $("depotclose").focus();
  } else {
    pickDepot(undefined);
    $("market").append(market.formElement);
    depotFilter.value = "";
    filterDepot();
    if (document.activeElement === document.body || depotDlg.contains(document.activeElement)) canvas.focus();
  }
  if (open && game.you) paintDepot(game.you);
}
/*
 * On a phone it covers everything but the log (2026-10-19: "on phone, the
 * depot interface show over eveyrthing except the chat log"): the world,
 * the bars over it and the panels' shelf under it, down to the top of the
 * log, which keeps its own height wherever it is dragged to. style.css
 * stops it --depotfoot from the bottom of the window, this far.
 */
function fitDepotDlg() {
  const top = $("console").getBoundingClientRect().top;
  depotDlg.style.setProperty("--depotfoot", `${Math.max(0, window.innerHeight - top)}px`);
}
new ResizeObserver(fitDepotDlg).observe($("console"));
window.addEventListener("resize", fitDepotDlg);
function shutDepot(_open = false) {
  showPanel($("depotpanel"), false, false);
}
$("depotclose").addEventListener("click", () => shutDepot());
depotDlg.addEventListener("mousedown", (ev) => {
  if (ev.target === depotDlg) shutDepot();
});
for (const tab of [$("depottabgoods"), $("depottabmarket")]) {
  tab.addEventListener("click", () => {
    depotBox.dataset.tab = tab.dataset.tab;
    for (const t of [$("depottabgoods"), $("depottabmarket")]) t.setAttribute("aria-selected", String(t === tab));
  });
}

// --- shop window ------------------------------------------------------------

/*
 * The shop is a window over the world (2026-10-19: "make trade a dialog
 * over the game view"), where the Vendor panel stood beside it until then:
 * choosing Trade on a shopkeeper, or pressing the coins icon beside one,
 * opens the shopkeeper's goods over the world only, on a phone as well, so
 * the log and the panels stay in sight. The bag is not in it (2026-10-19:
 * "selling should be over the game view, with no bag panel. the bag panel
 * can be used to sell with the context menu"): it stays in its own panel,
 * whose menu sells, and a square of it dragged onto the goods sells too.
 * That panel is shown for as long as the shop is (2026-10-27), the way it
 * is for a trade with a player: see holdBag.
 * The goods are the Vendor panel's own squares, moved in while it is open
 * and back when it shuts, so a click still buys and a long press asks how
 * many.
 */
const vendorDlg = $("vendordlg");
const vendorBox = $("vendorbox");
const vendorLent: [HTMLElement, HTMLElement, HTMLElement][] = [
  [$("vendor"), $("vendorbody"), $("vendorgoodsslot")],
];

function showVendorDlg(open: boolean) {
  if (vendorDlg.hidden === !open) return;
  if (open && !depotDlg.hidden) {
    shutDepot();
    if (vendorDlg.hidden === !open) return;
  }
  vendorDlg.hidden = !open;
  for (const [piece, home, there] of vendorLent) (open ? there : home).append(piece);
  holdBag();
  if (open) {
    held.clear();
    $("vendorclose").focus();
    if (game.you) paintVendor(game.you);
  } else if (document.activeElement === document.body || vendorDlg.contains(document.activeElement)) canvas.focus();
}
function shutVendor(_open = false) {
  showPanel($("vendorpanel"), false, false);
}
$("vendorclose").addEventListener("click", () => shutVendor());
vendorDlg.addEventListener("mousedown", (ev) => {
  if (ev.target === vendorDlg) shutVendor();
});

/** The name over it and the gold it can be paid from, from the state. */
function paintVendor(you: You) {
  if (vendorDlg.hidden) return;
  $("vendortitle").textContent = you.vendorname ?? "Trade";
  $("vendorgoodshead").textContent = `${you.vendorname ?? "The vendor"} sells`;
  $("vendorgold").textContent = num(availableGold(you));
}

/** Hide the squares whose names the filter leaves out, and filter the lots. */
function filterDepot() {
  const f = depotFilter.value.trim().toLowerCase();
  for (const grid of [$("inv"), $("depot")]) {
    for (const el of Array.from(grid.children) as HTMLElement[]) {
      if (el.dataset.name === undefined) continue;
      el.hidden = !!f && !el.dataset.name.toLowerCase().includes(f);
    }
  }
  market.setFilter(depotFilter.value);
}
depotFilter.addEventListener("input", filterDepot);
// The squares are drawn again as the bag and the depot change: the filter
// and the pick are put back on them each time.
new MutationObserver(() => {
  if (depotDlg.hidden) return;
  filterDepot();
  markDepotPick();
}).observe($("inv"), { childList: true });
new MutationObserver(() => {
  if (depotDlg.hidden) return;
  filterDepot();
  markDepotPick();
}).observe($("depot"), { childList: true });

function markDepotPick() {
  for (const el of Array.from($("depot").children) as HTMLElement[]) el.classList.toggle("picked", !!depotPicked && el.dataset.key === depotPicked);
}

/** Pick a depot square for the bar under the depot, or put the pick down. */
function pickDepot(it: Item | undefined) {
  depotPicked = it ? `${it.id}:${it.owner ?? ""}:${it.var ?? ""}` : "";
  markDepotPick();
  paintDepotPick();
}

function pickedDepotItem(): Item | undefined {
  if (!depotPicked) return undefined;
  return game.you?.depot?.find((d) => `${d.id}:${d.owner ?? ""}:${d.var ?? ""}` === depotPicked);
}

function paintDepotPick() {
  const it = pickedDepotItem();
  if (depotPicked && !it) depotPicked = "";
  $("depotpick").hidden = !it;
  if (!it) {
    market.pick(undefined);
    return;
  }
  const name = game.defOf(it)?.name ?? "something";
  $("depotpickwhat").textContent = it.count > 1 ? `${name} ×${num(it.count)}` : name;
  $("depottake").textContent = it.count > 1 ? `Take all ${num(it.count)}` : "Take";
  // What Grubb will not keep (gold, Gandalf's letter) is not among the
  // market's goods, and has no form.
  const good = game.you?.market?.goods.find((g) => sameItem(g, it));
  market.pick(good);
}
$("depottake").addEventListener("click", () => {
  const it = pickedDepotItem();
  if (!it) return;
  const s = game.you?.depot?.indexOf(it) ?? 0;
  net.send({ t: "withdraw", slot: s, id: it.id, owner: it.owner, var: it.var, count: 0 });
});
ui.onDepotPick = (_slot, it) => {
  if (depotDlg.hidden) return false;
  pickDepot(depotPicked === `${it.id}:${it.owner ?? ""}:${it.var ?? ""}` ? undefined : it);
  return true;
};
$("depotstoreloot").addEventListener("click", () => net.send({ t: "depositall", text: "loot" }));
$("depotstoregold").addEventListener("click", () => net.send({ t: "depositall", text: "gold" }));
$("depotcollect").addEventListener("click", () => net.send({ t: "marketcollect" }));

/** The heads and the foot, from the state. */
function paintDepot(you: You) {
  if (depotDlg.hidden) return;
  const goldIn = (items: Item[] | undefined) => (items ?? []).reduce((n, it) => n + (it.id === 1 ? it.count : 0), 0); // 1 is ItemGold
  $("depotbagload").textContent = `${fmtCn(you.cn ?? 0)} / ${fmtCn(you.maxcn ?? 0)} cn`;
  const kinds = you.depot?.length ?? 0;
  $("depotkinds").textContent = `${kinds} ${kinds === 1 ? "kind" : "kinds"}`;
  $("depotbaggold").textContent = num(goldIn(you.inv));
  $("depotgold").textContent = num(goldIn(you.depot));
  const keeping = you.market?.held ?? 0;
  $("depotheldline").hidden = keeping <= 0;
  $("depotheld").textContent = num(keeping);
  $("depotheldline").firstChild!.textContent = `${you.market?.keeper ?? "Grubb"} is holding `;
  // Without a market (no Grubb in the world) there is no column for it.
  depotBox.classList.toggle("nomarket", !you.market);
  paintDepotPick();
}

// A pile dropped on the Vendor panel is sold the same way, as lots of what
// he takes at a time. panel says the number came from the dialog, so it is
// not held to a right-click's ten.
ui.onSell = (slot, it, lots) => net.send({ t: "sell", slot, id: it.id, owner: it.owner, var: it.var, lots, panel: true });
ui.onHowMany = (ask) => askHowMany(ask);
ui.onPin = (it, on) => net.send({ t: "pin", id: it.id, on });
ui.onCastOne = (it) => net.send({ t: "cast", id: it.id });
ui.onPinSpell = (sp, on) => net.send({ t: "pinspell", text: sp.name, on });
ui.onDestroy = (slot, it, count) => net.send({ t: "destroy", slot, id: it.id, owner: it.owner, var: it.var, count });

/**
 * How many to buy or sell: one dialog for both sides of the counter. The
 * count is in things, not lots, since that is what is in the bag, and it
 * moves a lot at a time: three rocks to Grubb's one coin means the slider
 * and the box step in threes, and the line under them says what the number
 * fetches or costs.
 *
 * The four buttons over the slider only move it, to one lot, the other
 * side of where it is, half of all there is to trade, and all of it; the
 * Sell or Buy button, or Enter, trades whatever the slider says. The second
 * was "A stack", a square's worth, until 2026-10-04: "Remove item stacking
 * and reintroduce encumbrance. Replace the stack count helper button with
 * invert". Invert reads the slider as it stands and puts it at the most
 * less that, never under one (see presetLots). Escape, Cancel or a
 * click outside lets it go. Asked for on 2026-09-27: "The sell and buy
 * dialogs should allow selecting useful quantities, like 1, a stack, half
 * of all possible and all. The buy and sell commitment should work on the
 * current value set, not sell all like the all button currently does. That
 * should instead set the bar to the highest possible value". All used to
 * sell every lot there and then.
 */
const sellDlg = $<HTMLDivElement>("selldlg");
const sellRange = $<HTMLInputElement>("sellrange");
const sellCount = $<HTMLInputElement>("sellcount");
let asking: HowMany | null = null;

function showHowMany(open: boolean) {
  sellDlg.hidden = !open;
  if (open) {
    held.clear();
    // Opened by a finger's long press, the box's button takes the focus
    // rather than the number, which would raise a phone's keyboard over
    // the box (2026-10-01: "Don't autofocus on the count input field when
    // long pressing an item in the inventory"). A right-click lands in the
    // number, where typing one is the quick way.
    if (performance.now() - longPressedAt < 1000) $("sellok").focus();
    else {
      sellCount.focus();
      sellCount.select();
    }
  } else {
    asking = null;
    canvas.focus();
  }
}

/** The number in the dialog as whole lots, kept within what can be traded. */
function askedLots(): number {
  if (!asking) return 0;
  const n = Math.floor(Number(sellCount.value) / asking.lot);
  return Math.max(1, Math.min(asking.lots, Number.isFinite(n) ? n : 1));
}

function paintHowMany(lots: number) {
  if (!asking) return;
  lots = Math.max(1, Math.min(asking.lots, lots));
  sellRange.value = String(lots);
  sellCount.value = String(lots * asking.lot);
  const n = lots * asking.lot;
  const pay = $("sellpay");
  pay.textContent = asking.says ? asking.says(lots) : `${num(n)} ${asking.name}${n > 1 ? "s" : ""} for ${num(lots * asking.price)} gold`;
  // A line with nothing to add is left out rather than left blank.
  pay.hidden = !pay.textContent;
  $<HTMLButtonElement>("sellok").disabled = asking.can ? !asking.can(lots) : false;
  // Buttons that say what this many fetch follow the slider.
  if (asking.detail) labelButton($("sellok"), asking.verb, asking.detail(lots));
  for (const [b, m] of moreButtons) if (m.detail) labelButton(b, m.verb, m.detail(lots));
}

/** A button's verb, and after it in a slightly muted colour what it does
 *  for the number asked (see HowMany.detail). */
function labelButton(b: HTMLElement, verb: string, detail: string) {
  if (!detail) {
    b.textContent = verb;
    return;
  }
  const d = document.createElement("span");
  d.className = "selldetail";
  d.textContent = detail;
  b.replaceChildren(verb, " ", d);
}

/** The ask's further buttons as made for it, to relabel as the number moves. */
let moreButtons: [HTMLButtonElement, NonNullable<HowMany["more"]>[number]][] = [];

function askHowMany(ask: HowMany) {
  asking = ask;
  $("selltitle").textContent = ask.title ?? `${ask.verb} ${ask.name}s`;
  $("sellwhat").textContent = ask.what;
  $("sellwhat").hidden = !ask.what;
  // What the thing is, when the dialog came off a press on the bag.
  const look = $("selllook");
  look.textContent = ask.look ?? "";
  look.hidden = !ask.look;
  // And what a look at it says, on a line of its own (see HowMany.flavour).
  const flavour = $("sellflavour");
  flavour.textContent = ask.flavour ?? "";
  flavour.hidden = !ask.flavour;
  $("sellok").textContent = ask.verb;
  // The second way of letting go of the same number: Drop beside Sell.
  const also = $<HTMLButtonElement>("sellalso");
  also.hidden = !ask.also;
  also.textContent = ask.also?.verb ?? "";
  // And any after it, made afresh for each ask and put before Cancel.
  for (const b of Array.from(document.querySelectorAll("#sellbtns .sellmore"))) b.remove();
  moreButtons = [];
  for (const m of ask.more ?? []) {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "sellmore";
    // Coloured like the spell or slot menu's action worth a second's
    // thought, as Destroy is.
    if (m.heavy) b.classList.add("all");
    b.textContent = m.verb;
    b.addEventListener("click", () => {
      m.go(askedLots());
      showHowMany(false);
    });
    $("sellbtns").insertBefore(b, $("sellcancel"));
    moreButtons.push([b, m]);
  }
  // One lot draws the slider full and greys Invert and Half, which have
  // nowhere to put it but where it is (see howManyLayout).
  const layout = howManyLayout(ask.lots);
  sellRange.min = String(layout.min);
  sellRange.max = String(layout.max);
  for (const b of Array.from($("sellpresets").querySelectorAll<HTMLButtonElement>("button"))) {
    b.disabled = layout.off.includes(b.dataset.q ?? "");
  }
  sellCount.min = String(ask.lot);
  sellCount.max = String(ask.lots * ask.lot);
  sellCount.step = String(ask.lot);
  // Selling starts at all of it, which is what a pile dragged onto the
  // counter usually means; buying starts at one, since all is every coin.
  // A press on the bag starts at the pile pressed (see dropFromBag).
  paintHowMany(ask.start ?? (ask.verb === "Sell" ? ask.lots : 1));
  showHowMany(true);
}

sellRange.addEventListener("input", () => paintHowMany(Number(sellRange.value)));
sellCount.addEventListener("change", () => paintHowMany(askedLots()));
for (const b of Array.from(document.querySelectorAll<HTMLButtonElement>("#sellpresets button"))) {
  b.addEventListener("click", () => {
    if (asking) paintHowMany(presetLots(b.dataset.q ?? "all", asking.lots, Number(sellRange.value)));
  });
}
$("sellbox").addEventListener("submit", (ev) => {
  ev.preventDefault();
  if (asking) asking.go(askedLots());
  showHowMany(false);
});
$("sellalso").addEventListener("click", () => {
  if (asking?.also) asking.also.go(askedLots());
  showHowMany(false);
});
$("sellcancel").addEventListener("click", () => showHowMany(false));
// A press on the dark round the box, and not the mouse's echo of a finger
// lifting: over the depot screen the dialog covers the whole page, so the
// finger that held a square to open it lets go over the dark, and the
// mousedown the browser makes of that shut it as it opened.
sellDlg.addEventListener("pointerdown", (ev) => {
  if (ev.target === sellDlg) showHowMany(false);
});
const chat = $<HTMLInputElement>("chat");
// The yell toggle at the head of the chat line (2026-10-08: "add the
// ability to yell, a small toggle at the beginning of the enter chat
// line. it puts the say in all caps, and can be heard by players 30 tiles
// away"). It stays on until pressed again, as a toggle does; while it is
// on the box shows what you type in capitals, as it will be heard, and
// says so in its placeholder. The capitals themselves are the server's.
const yellBtn = $<HTMLButtonElement>("yellbtn");
let yelling = false;
function setYelling(on: boolean) {
  yelling = on;
  yellBtn.setAttribute("aria-pressed", String(on));
  chat.classList.toggle("yelling", on);
  chat.placeholder = on ? "Press Enter to yell" : "Press Enter to chat";
}
// Pressing it leaves the typing where it was: a mousedown that does not
// take the focus, so a half-typed line is not blurred out from under you.
yellBtn.addEventListener("mousedown", (e) => e.preventDefault());
yellBtn.addEventListener("click", () => setYelling(!yelling));

/**
 * The chat line folded away on a phone (2026-10-10: "on phone, hide the
 * chat entry line behind a long press on the chat log", then "Implement
 * the visible chat button"), giving its row to the log. The speech bubble
 * in the log's corner opens it. A phone here is a touch screen whose narrow side is a
 * phone's, so one held sideways folds it too and a tablet does not.
 *
 * Folded, the row is shrunk to nothing rather than taken out of the page,
 * so whatever puts the focus in the chat box — Enter, Reply on a line, a
 * spell that wants a word after it — still can, and the focus arriving
 * is what opens it. A hold on the log asks for it too: off every line it opens
 * as the finger lifts, since iOS raises its keyboard only for a focus
 * given while a finger is doing something, and a timer's is not; on a
 * line, the line's menu has Chat first. It folds again once it is left
 * empty: the line sent, Esc, or the keyboard put away with nothing typed.
 * A press on Types or Filters takes the focus out of it without folding
 * it, and so does the Filters menu while it is open; the next press
 * anywhere else folds it.
 */
const PHONE_MEDIA = "(pointer: coarse) and (max-width: 720px), (pointer: coarse) and (max-height: 500px)";
const phoneScreen = window.matchMedia(PHONE_MEDIA);
const chatRow = $("chatrow");
// The speech bubble in the log's corner, shown while the line is folded
// (2026-10-10: "Implement the visible chat button"). A tap is a press,
// so the focus it gives raises iOS's keyboard as a hold's release does.
// The line is opened before the focus goes in, so the box the browser
// is asked to keep above the keyboard is the box, not a folded speck.
const chatOpenBtn = $<HTMLButtonElement>("chatopen");
chatOpenBtn.addEventListener("click", () => {
  openChat(true);
  chat.focus();
});
let chatOpen = false;
let chatRowPressedAt = -Infinity;
function foldChat() {
  const fold = phoneScreen.matches && !chatOpen;
  if (chatRow.classList.contains("folded") !== fold) ui.holdLogView(() => chatRow.classList.toggle("folded", fold));
  chatOpenBtn.hidden = !fold;
  keyboardOverChat(phoneScreen.matches && chatOpen);
}
function openChat(open: boolean) {
  chatOpen = open;
  foldChat();
}

/**
 * The phone's keyboard kept off the chat line (2026-10-14: "When the chat
 * button is clicked on phone, the contents of the chat box are not
 * visible"; 2026-10-17: "The fix to make sure the chat input field is
 * visible isn't working properly"). The line is at the foot of the screen,
 * where the keyboard comes up, and the browser left it under the keyboard:
 * the box has the focus and what is typed goes in, out of sight.
 *
 * So while the line is open on a phone the page is shortened by the
 * keyboard (--kbd under #app in the stylesheet): the world gives up the
 * room and the log and the line sit on top of the keyboard. How tall the
 * keyboard is comes from whichever the browser has. Chrome says it outright
 * (navigator.virtualKeyboard, which also works in full screen, where Chrome
 * makes no room of its own). Safari has no such thing: there the keyboard
 * shrinks the visual viewport and leaves the page as tall as it was, so what
 * it covers is the page's height less the visual viewport's, and Safari's
 * own scroll to the box, which slid the whole game up past the top of the
 * glass, is put back once the page fits. The 2026-10-14 version listened to
 * Chrome alone, so on an iPhone it did nothing. Only while the line is open,
 * so the login and the other boxes that are typed into keep the browser's
 * own way with the keyboard.
 */
type VirtualKeyboard = EventTarget & { overlaysContent: boolean; readonly boundingRect: DOMRectReadOnly };
const virtualKeyboard = (navigator as Navigator & { virtualKeyboard?: VirtualKeyboard }).virtualKeyboard;
let keyboardOverChatOn = false;
function keyboardOverChat(on: boolean) {
  if (keyboardOverChatOn === on) return;
  keyboardOverChatOn = on;
  if (virtualKeyboard) virtualKeyboard.overlaysContent = on;
  fitAboveKeyboard();
}
function fitAboveKeyboard() {
  let px = 0;
  if (keyboardOverChatOn) {
    const r = virtualKeyboard?.overlaysContent ? virtualKeyboard.boundingRect : null;
    // From the keyboard's top edge to the bottom of the window, which is
    // what it covers, whatever of it is under the system's own bars.
    if (r && r.height > 0) px = window.innerHeight - r.top;
    const vv = window.visualViewport;
    if (vv) px = Math.max(px, document.documentElement.clientHeight - vv.height);
  }
  px = Math.max(0, Math.round(px));
  const was = document.documentElement.style.getPropertyValue("--kbd");
  const now = px ? `${px}px` : "";
  if (was !== now) ui.holdLogView(() => document.documentElement.style.setProperty("--kbd", now));
  if (px && (window.scrollY !== 0 || (window.visualViewport?.offsetTop ?? 0) !== 0)) window.scrollTo(0, 0);
}
virtualKeyboard?.addEventListener("geometrychange", fitAboveKeyboard);
window.visualViewport?.addEventListener("resize", fitAboveKeyboard);
window.visualViewport?.addEventListener("scroll", fitAboveKeyboard);
function foldChatIfLeft() {
  if (!chatOpen || chat.value !== "" || document.activeElement === chat || !$("filtersmenu").hidden) return;
  if (performance.now() - chatRowPressedAt < 500) return;
  // A button on the line keeps the focus after it is pressed, Filters
  // once its menu shuts, and a press out on the world need not take it.
  if (chatRow.contains(document.activeElement)) (document.activeElement as HTMLElement).blur();
  openChat(false);
}
phoneScreen.addEventListener("change", foldChat);
chatRow.addEventListener("focusin", () => openChat(true));
chatRow.addEventListener("pointerdown", () => (chatRowPressedAt = performance.now()), true);
chat.addEventListener("blur", () => setTimeout(foldChatIfLeft));
document.addEventListener("pointerdown", () => setTimeout(foldChatIfLeft), true);
ui.chatFolded = () => chatRow.classList.contains("folded");
ui.onChat = (onRelease) => {
  if (!onRelease) {
    chat.focus();
    return;
  }
  openChat(true);
  const done = (ev: Event) => {
    for (const type of ["touchend", "pointerup", "pointercancel"]) removeEventListener(type, done, true);
    if (ev.type !== "pointercancel") chat.focus();
  };
  for (const type of ["touchend", "pointerup", "pointercancel"]) addEventListener(type, done, true);
};
foldChat();
lockPageZoom(window);
const login = $<HTMLDivElement>("login");
const loginErr = $<HTMLDivElement>("loginerr");
wirePullToReload(login, $("loginpull"), () => location.reload());
const help = $<HTMLDivElement>("help");

// Character names are a single capitalised word; mirrors nameRe on the server.
const NAME_RE = /^[A-Z][a-z]{1,19}$/;
const NAME_HINT = "Name must be a single word of 2-20 letters: Bob, not Bob Smith.";
// Every name is a capital and then small letters, so what is typed is put
// that way before it is checked or sent: bob and BOB are both Bob, and a
// character is found in any capitals (2026-09-24: "match player names
// regardless of capitalization").
const nameAsSpelt = (typed: string) => typed.charAt(0).toUpperCase() + typed.slice(1).toLowerCase();

prewarmTiles();

// Handy in the browser console, and harmless: the game state is the
// client's own view of the world, nothing secret. The panel goes with it,
// since the useful thing to do with a state in a console is to feed it to
// the panel and see what the panel makes of it.
(window as unknown as { game: Game; ui: UI }).game = game;
(window as unknown as { game: Game; ui: UI }).ui = ui;
// And the connection, so a message can be fed to the client's handler
// from the console as if the server had sent it — which is how a window
// is looked at without a server that will open it.
(window as unknown as { net: Net }).net = net;

// --- is there a world, and who is in it ------------------------------------

/**
 * Whether the server is up, and how many people are in it, on the login
 * screen. Both off the same request, because a count from a server that
 * has since gone is worse than no count at all.
 *
 * This used to be one fetch on load, for the count alone, and the failure
 * it could not describe is the one that matters most: a login screen in
 * front of a server that is not running looks exactly like a login screen
 * in front of one that is, right up until you type a name and press the
 * button and the socket refuses. There is nothing on the page to check
 * first. So there is now.
 *
 * Three states, not two. "Down" before anybody has asked is a guess
 * dressed as a fact, and the first request takes a moment on a cold
 * connection, so the line starts out saying it is finding out.
 */
const STATUS_EVERY = 5000; // ms between checks while the login screen is up
const STATUS_TIMEOUT = 4000; // ms before a silent server counts as down
const loginStatus = $("loginstatus");
const loginOnline = $("loginonline");
const loginRing = $("loginring");
const loginRate = $("loginrate");
let statusTimer = 0;
/** The worlds to choose between, over the name box: see worlds.ts. */
const worldPicker = new WorldPicker($("loginworlds"), STATUS_TIMEOUT);
/** Join us on Discord opens on this world's channel. */
$<HTMLAnchorElement>("logindiscord").href = discordInvite(location.host);
/** This world's ping on the login screen: see PingMeter. */
const statusPing = new PingMeter();

/** The One Ring as /status has it: see RingStatus in ringdoom.go. */
type RingStatus = { state: "lost" | "found" | "destroyed"; left?: number };

/** A wait as the login screen says it: "6d 23h", "13h 20m", "4m". */
function ringWait(secs: number): string {
  const mins = Math.ceil(secs / 60);
  const d = Math.floor(mins / 1440);
  const h = Math.floor(mins / 60) % 24;
  const m = mins % 60;
  if (d > 0) return h > 0 ? `${d}d ${h}h` : `${d}d`;
  if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  return `${m}m`;
}

/**
 * The One Ring's line on the login screen (2026-10-08: "on the login
 * screen, show the current status of the one ring, including whether it
 * has been found, and how long it has to be destroyed"). Never who has it.
 */
function ringLine(r?: RingStatus): string {
  if (!r) return "";
  const left = r.left ?? 0;
  switch (r.state) {
    case "found":
      return left > 0
        ? `The One Ring has been found. It must be destroyed within ${ringWait(left)}.`
        : "The One Ring has been found, and its week is up: it will betray its bearer to their death.";
    case "destroyed":
      return `The One Ring has been destroyed. The Gladden Fields give it up again in ${ringWait(left)}.`;
    default:
      return left > 0
        ? `The One Ring is lost in the Gladden Fields, and can be fished up in ${ringWait(left)}.`
        : "The One Ring is lost in the Gladden Fields.";
  }
}

/**
 * Put text in an element only when it says something new. The status lines
 * sit in the login form, over the name and password boxes, and they are
 * written every five seconds: rewriting them with the same words was a
 * change to the form each time for a password manager watching it to
 * notice, while its list of logins was open under one of the boxes
 * (2026-10-24: "password autofill often fails if i switch between the name
 * and password fields").
 */
function setText(el: HTMLElement, text: string) {
  if (el.textContent !== text) el.textContent = text;
}

/** The ping beside "Server is up": one span, kept, its words changed in place. */
const loginPing = document.createElement("span");
loginPing.title = "How long the server takes to answer the status check, there and back: the quickest of the last three, checked every five seconds, not counting setting up the connection. In the game the Ping row measures the same through the world itself.";

/** The server's rates as /status has them: see game/serverrate.go. */
interface ServerRates {
  rate?: number;
  rateleft?: number;
  zonesoff?: boolean;
}

function showServerUp(up: boolean, online?: number, ms?: number, ring?: RingStatus, rates?: ServerRates) {
  setText(loginRing, up ? ringLine(ring) : "");
  // A temporary rate while an admin has one up (2026-10-22: "allow admin
  // to modify a server training/exp multiplier. it should show up on the
  // login screen"; 2026-11-03: "/temprate N D ... the condition status
  // shows N to players"), and for how long.
  const rateLine = (rate?: number, left?: number) =>
    rate && rate !== 1 ? `Experience and skill training are at x${rate}${left ? ` for ${ringWait(left)}` : ""}.` : "";
  setText(
    loginRate,
    up && rates
      ? [rateLine(rates.rate, rates.rateleft), rates.zonesoff ? "The protection zones are off: nowhere is safe." : ""]
          .filter(Boolean)
          .join(" ")
      : "",
  );
  const state = up ? "up" : "down";
  if (loginStatus.className !== state) loginStatus.className = state;
  const said = up ? "Server is up" : "Server is down";
  if (loginStatus.firstChild?.nodeType !== Node.TEXT_NODE || loginStatus.firstChild.textContent !== said) {
    loginStatus.textContent = said;
  }
  // And how long it took to answer, there and back (2026-10-06: "Show
  // server ping on the login page"), coloured as the Ping row in the game
  // is: under 100 nothing notices, past 300 a step and a swing both arrive
  // late (see setPing). Only beside "up": a server that is down has no
  // ping, and a number left over from when it was up is the page arguing
  // with itself again.
  if (up && typeof ms === "number") {
    const grade = `ping ${ms < 100 ? "good" : ms < 300 ? "fair" : "bad"}`;
    if (loginPing.className !== grade) loginPing.className = grade;
    setText(loginPing, ` · ${ms} ms`);
    if (loginPing.parentNode !== loginStatus) loginStatus.append(loginPing);
  } else loginPing.remove();
  // The count goes with it. A stale number under a line that says the
  // server is down is the page arguing with itself, and the number is the
  // half that is wrong.
  if (!up || typeof online !== "number") setText(loginOnline, "");
  else setText(loginOnline, online === 1 ? "1 player is online." : `${online} players are online.`);
}

async function checkServer() {
  try {
    // A deadline as well as a failure. A server that has stopped answering
    // without closing the socket — the machine asleep, the process wedged
    // — would otherwise leave this hanging for as long as the browser
    // felt like waiting, and a line that says it is still checking five
    // minutes later is saying nothing.
    // Timed to the answer's arrival, before its body is read: the round
    // trip is the ping, and parsing a few bytes of JSON is not. The
    // connection is set up beforehand rather than counted in it, and the
    // ping shown is the lowest of the last three trips: see PingMeter.
    await statusPing.warmUp("status", STATUS_TIMEOUT);
    const sent = performance.now();
    const r = await fetch("status", { signal: AbortSignal.timeout(STATUS_TIMEOUT), cache: "no-store" });
    const trip = Math.round(performance.now() - sent);
    const ms = statusPing.add(trip);
    const st = r.ok ? ((await r.json()) as { online?: number; build?: string; ring?: RingStatus } & ServerRates) : null;
    // While somebody is in the name or password box the lines are left as
    // they are, unless the server has come or gone: a password manager's
    // list of logins can be open under the box, and the ping changing
    // every five seconds is not worth it moving.
    if (!typingLogin() || loginStatus.className !== (st ? "up" : "down")) {
      showServerUp(st !== null, st?.online, ms, st?.ring, st ?? undefined);
      worldPicker.showHere(st !== null, st?.online, ms);
    }
    // And whether this page is still the one the server is serving. The
    // build id is on the status for exactly this — see Status.Build — and
    // the poll is already running here while the login screen is up, so
    // the page can find out before anybody has typed a password rather
    // than by being refused after they have.
    //
    // That refusal used to be the only way it was ever noticed, which is
    // why logging in the first time after a deploy failed and the second
    // time worked: the reload fetched the new bundle but the attempt that
    // triggered it was gone, and the password box was empty again. Asked
    // for on 2026-09-21: "Can we make it so the tinytibia tab or resources
    // reload automatically if they don't match the server?"
    //
    // The password is deliberately not carried across the reload to
    // resubmit the login by itself. It would have to be written into
    // storage to survive the page going away, and a credential parked in
    // sessionStorage to save one click is a poor trade. Reloading before
    // the login instead means there is no attempt to resubmit.
    if (st?.build && st.build !== BUILD) {
      reloadForNewBuild("The game has been updated.");
    }
  } catch {
    statusPing.reset();
    showServerUp(false);
    worldPicker.showHere(false);
  }
  if (!typingLogin()) worldPicker.checkOthers();
}

/** Whether the name or the password box on the login screen has the focus. */
function typingLogin(): boolean {
  const at = document.activeElement;
  return !!at && (at.id === "username" || at.id === "password");
}

/**
 * Checking runs while the login screen is showing and stops while you are
 * playing. The socket is the better answer once there is one — it knows
 * the moment the world goes, where a poll would take up to five seconds to
 * notice — and polling a server you are already talking to is asking a
 * question you have the answer to.
 *
 * It is driven from here rather than from a timer left running because the
 * login screen comes back on its own: a server that shuts down puts
 * everybody in front of it again, and that is exactly the moment somebody
 * wants to know whether it is worth waiting for.
 */
function watchServer(on: boolean) {
  clearInterval(statusTimer);
  if (!on) return;
  void checkServer();
  statusTimer = setInterval(checkServer, STATUS_EVERY) as unknown as number;
}
watchServer(true);

// --- login background -----------------------------------------------------

// The login screen wears whatever images are dropped into web/static/bg/,
// tiled into one darkened collage behind the form. The server lists the
// directory at bg/index.json; nothing there means the plain background.
//
// The tiling is the justified kind a photo gallery uses: images are laid in
// rows, each row scaled to exactly fill the width, and the row height chosen
// so the rows together fill the height. Every image keeps its own shape, so
// nothing is cropped and every one of them is shown - which is the whole
// point of dropping them in the directory. A short list is repeated until it
// covers the screen rather than being stretched.
const BG_GAP = 2;

type Shot = { src: string; ar: number };

// pack lays the shots out in rows at a target height: a row takes images
// until they overflow the width, then is scaled to fit it exactly. The
// scaled height is what the row ends up at, which is near the target and
// never far from it.
function packRows(shots: Shot[], target: number, w: number) {
  const rows: { h: number; items: Shot[] }[] = [];
  const close = (items: Shot[]) => {
    const ars = items.reduce((t, s) => t + s.ar, 0);
    rows.push({ h: (w - BG_GAP * (items.length - 1)) / ars, items });
  };
  let items: Shot[] = [];
  let width = 0;
  for (const s of shots) {
    items.push(s);
    width += s.ar * target + BG_GAP;
    if (width - BG_GAP >= w) {
      close(items);
      items = [];
      width = 0;
    }
  }
  if (items.length > 0) close(items);
  return rows;
}

const rowsHeight = (rows: { h: number }[]) =>
  rows.reduce((t, r) => t + r.h, 0) + BG_GAP * Math.max(0, rows.length - 1);

// bestRows picks the layout that comes closest to filling the box exactly.
// Rows can only be packed whole, so the total height of a packing jumps as
// images move between rows and rarely lands on the box height; a bisection
// finds the two packings either side of it, and the closest of those over a
// few repeats of the list wins. Whatever the chosen packing is out by is
// then taken up by the cover crop, so the contest is really over how much of
// each image survives. Repeating is what covers a screen with three images
// in the directory, so it is allowed but dearly paid for: showing every
// image once and large beats showing them all four times and small, and only
// a packing that would otherwise be cropped to slivers is worth a second
// copy of the list.
function bestRows(shots: Shot[], w: number, h: number) {
  let best: { h: number; items: Shot[] }[] = [];
  let bestScore = Infinity;
  for (let copies = 1; copies <= 4; copies++) {
    const list: Shot[] = [];
    for (let i = 0; i < copies; i++) list.push(...shots);
    // Taller rows hold fewer images each and so stack higher: the total
    // climbs with the target, which is what makes it bisectable.
    let lo = 4;
    let hi = Math.max(w, h);
    for (let i = 0; i < 40; i++) {
      const mid = (lo + hi) / 2;
      if (rowsHeight(packRows(list, mid, w)) < h) lo = mid;
      else hi = mid;
    }
    for (const target of [lo, hi]) {
      const rows = packRows(list, target, w);
      const total = rowsHeight(rows);
      if (total <= 0) continue;
      const score = Math.abs(Math.log(h / total)) + 0.5 * (copies - 1);
      if (score < bestScore) {
        best = rows;
        bestScore = score;
      }
    }
  }
  return best;
}

function paintLoginBackground(bg: HTMLElement, shots: Shot[]) {
  const w = login.clientWidth;
  const h = login.clientHeight;
  if (w === 0 || h === 0 || shots.length === 0) return;
  const rows = bestRows(shots, w, h);
  const total = rowsHeight(rows);
  if (rows.length === 0 || total <= 0) return;
  // Whatever the chosen packing is left over or short by is taken out of the
  // row heights alone, so the rows still fill the width and the stack fills
  // the height. The widths stay as they were measured, so each image is
  // squeezed by that one factor - near enough to 1 that the crop the cover
  // takes is a sliver.
  const fit = h / total;
  bg.textContent = "";
  for (const row of rows) {
    const el = document.createElement("div");
    el.className = "bgrow";
    el.style.height = `${row.h * fit}px`;
    for (const s of row.items) {
      const img = document.createElement("img");
      img.src = s.src;
      img.alt = "";
      img.style.width = `${s.ar * row.h}px`;
      el.appendChild(img);
    }
    bg.appendChild(el);
  }
}

// aspectOf loads an image only far enough to learn its shape. One that never
// arrives is treated as a landscape photo, which is what most of them are.
const aspectOf = (src: string) =>
  new Promise<number>((resolve) => {
    const probe = new Image();
    probe.onload = () => resolve(probe.naturalWidth / probe.naturalHeight || 1.5);
    probe.onerror = () => resolve(1.5);
    probe.src = src;
  });

fetch("bg/index.json")
  .then((r) => (r.ok ? r.json() : []))
  .then(async (names: string[]) => {
    if (!Array.isArray(names) || names.length === 0) return;
    const shots: Shot[] = await Promise.all(
      names.map(async (src) => ({ src, ar: await aspectOf(src) })),
    );
    const bg = document.createElement("div");
    bg.id = "loginbg";
    login.prepend(bg);
    paintLoginBackground(bg, shots);
    // The best tiling depends on the shape of the window, so it is worked
    // out again whenever that changes.
    let pending = 0;
    addEventListener("resize", () => {
      clearTimeout(pending);
      pending = setTimeout(() => paintLoginBackground(bg, shots), 100);
    });
  })
  .catch(() => {});

// --- full screen --------------------------------------------------------------

/**
 * Filling the screen on a phone. A browser will not do this on its own and
 * there is no setting for it: the page has to ask, and it may only ask
 * from inside a real tap — synchronously, in the handler, or the request
 * is refused. So this hangs off a button and does nothing clever before
 * the call.
 *
 * It asks for the screen and nothing else. It used to turn the phone
 * landscape as well, which is a thing a game may do and not a thing it
 * should: which way round somebody holds their own telephone is their
 * business, the view fits either way, and a screen that spins under you
 * because you tapped a button is a rude answer to a simple request.
 *
 * The button is hidden where the API is not there at all, which today
 * means Safari on an iPhone: element full screen has never existed there,
 * and a button that cannot work is worse than no button. On that phone the
 * way in is the manifest — add the game to the home screen and it opens
 * with no browser around it, which is the better trick anyway. `hidden` is
 * the whole of that, and the stylesheet has a rule whose only job is to
 * keep the button's own `display` from beating it.
 *
 * It sits in the top right corner of the world now, rather than eleven
 * buttons down the Controls panel (asked for on 2026-09-22: "show the
 * fullscreen/windowed button in the top right of the main view, with an
 * appropriate glyph and tooltip"). Two things follow from being there
 * rather than in a panel, and both are the reason the code below looks the
 * way it does.
 *
 * The first is that there is no room for a word, so the button is a
 * drawing: the four corners everything else on a screen uses for this,
 * pushed out to fill and drawn in to give back. See viewicons.ts. The word
 * is not gone — it is on the title for a pointer and on the aria-label for
 * anything that reads aloud — but it is no longer on the face of the
 * button, and a picture with no words anywhere is a button nobody can
 * name.
 *
 * The second is that a finger cannot hover, and the corner of the world is
 * exactly where somebody playing on a telephone meets it first. So a hold
 * opens the same box a hold opens everywhere else on this page — the one
 * the bag slots and the spells use — with the name, the words the pointer
 * gets on the title, and a button that does the thing. That is the whole
 * of the touchscreen's answer to the tooltip, and it goes through longPress
 * in touch.ts rather than a timer of our own, because that helper is what
 * knows Android answers a hold with a contextmenu and iOS answers it with
 * nothing, and its swallow is what keeps the release of the hold off the
 * click listener below. A hold must not toggle the screen: that is a
 * request for words answered by the view changing shape under you.
 */
// Sound on and off, from the same strip over the world (asked for on
// 2026-09-27: "add sound effects. in particular a wilhelm scream when a
// player dies"). See sound.ts. It is remembered in this browser.
const sounds = new Sounds();

/**
 * The narrow breakpoint: a window no wider than this is a narrow screen, a
 * phone's, where the open panels stack under the world; anything wider puts
 * them in rows beside it. The same width as style.css's `@media (max-width:
 * 720px)`, which is this breakpoint for everything else a narrow screen
 * does differently. Named on 2026-10-01, when it was asked what it was
 * called and it had no name. It is also where the info panel stops being a
 * column down the left of the page and becomes a drawer (2026-10-06), so
 * it is written here, before the drawer, which reads it as the page loads.
 */
const NARROW_MEDIA = "(max-width: 720px)";
const narrowScreen = window.matchMedia(NARROW_MEDIA);

/**
 * The info drawer (2026-09-29: "Add a stats button top left that pops the
 * stats and player panels from the left", then "The skills panel should
 * be on the left too. Skills, stats and players should all pop out
 * together and stack vertically. It should be a resizable panel", then on
 * 2026-09-30 "The stats panel should not appear with inventory and
 * controls. Have just one bottom to pop out the info sidebar (stats,
 * skills and players)"). Stats over Skills over Players.
 *
 * Since 2026-10-06 ("Always show the info panel tiled on the left at the
 * full screen height") it is, wider than the narrow breakpoint, a column
 * down the left of the page from top to bottom, with the bars, the world,
 * the panels and the log all to its right, covering nothing; its right
 * edge drags its width. For a few hours it was always out there, with no
 * button, until the same day: "Don't show it always in horizontal mode
 * ... The button to hide it doesn't appear anymore". So the button in the
 * top left corner of the world shows and hides it at every width, as does
 * Escape, and a wide screen remembers which it was left as. On a narrow
 * screen a full-height column would leave no world, so there it comes out
 * above the world, pushing it down, and starts shut whatever was
 * remembered. See #drawer in style.css.
 *
 * The three panels live in the drawer and in no column. It is not a
 * window: nothing about the keyboard or the world stops while it is out,
 * and you can walk with it out.
 */
const statsBtn = $<HTMLButtonElement>("statsbtn");
const drawer = $("drawer");
const drawerGrip = $("drawergrip");
// The page the drawer is a column of, and the box its width is a share of.
const drawerPage = $("gamewrap");
// Since 2026-09-29 ("The skills panel should be on the left too. Skills,
// stats and players should all pop out together and stack vertically")
// the drawer holds all three at once, and since 2026-09-30 ("The stats
// panel should not appear with inventory and controls. Have just one
// bottom to pop out the info sidebar") all three live there, in no
// column, and one button brings it out and puts it away.
/** Whether a wide screen last had the panel out, in this browser. */
const DRAWER_OPEN_KEY = "tinytibia.draweropen";
let drawerOpen = false;
try {
  drawerOpen = !narrowScreen.matches && localStorage.getItem(DRAWER_OPEN_KEY) === "1";
} catch {
  /* storage unavailable: it starts shut */
}

/** Whether the drawer is out. The depot screen puts it away while it is
 *  up (2026-10-26: "The depot dialog should hide the info drawer") without
 *  forgetting it was out, so it comes back when the screen shuts. */
function drawerShows(): boolean {
  return drawerOpen && depotDlg.hidden;
}

/** Bring the drawer out, or put it away, and on a wide screen remember
 *  which for the next visit. */
function showDrawer(open: boolean) {
  drawerOpen = open;
  paintDrawer();
  if (narrowScreen.matches) return;
  try {
    localStorage.setItem(DRAWER_OPEN_KEY, open ? "1" : "0");
  } catch {
    /* storage unavailable: it is not remembered */
  }
}

/** Say whether the drawer is out, to the eye and to a screen reader. */
function paintDrawer() {
  drawer.classList.toggle("open", drawerShows());
  drawer.setAttribute("aria-hidden", String(!drawerShows()));
  statsBtn.setAttribute("aria-expanded", String(drawerOpen));
  statsBtn.classList.toggle("on", drawerOpen);
}
paintDrawer();
narrowScreen.addEventListener("change", paintDrawer);
statsBtn.replaceChildren(viewGlyph("stats"));
statsBtn.setAttribute("aria-label", "Info: stats, skills and players");
statsBtn.addEventListener("click", () => showDrawer(!drawerShows()));

/**
 * The drawer's width, dragged by the grip on its right edge or moved by
 * the arrows with the grip focused (2026-09-29: "It should be a resizable
 * panel"), and remembered in this browser. It is set as --drawerw on the
 * page, which the drawer and the grip both stand by, so they move as one;
 * and it is never more than half the page, however narrow the page
 * becomes after it was set, so the world always keeps the other half to
 * share with its panels. On a narrow screen the drawer is the screen's
 * width, and the same grip, under it there, drags its height instead: see
 * setDrawerHeight.
 */
const DRAWER_W_KEY = "tinytibia.drawerw";
const DRAWER_MIN_PX = 180;
const DRAWER_STEP_PX = 16;
function drawerMaxPx(): number {
  return Math.max(DRAWER_MIN_PX, drawerPage.clientWidth / 2);
}
/** The width last asked for, if any, for the grip to report again when the
 *  window widens past the breakpoint (see paintDrawerGrip). */
let drawerWAsked: number | undefined;
function setDrawerWidth(px: number, save: boolean) {
  drawerWAsked = px;
  const w = Math.round(Math.min(Math.max(px, DRAWER_MIN_PX), drawerMaxPx()));
  drawerPage.style.setProperty("--drawerw", `min(${w}px, 50%)`);
  drawerGrip.setAttribute("aria-valuemin", String(DRAWER_MIN_PX));
  drawerGrip.setAttribute("aria-valuemax", String(Math.round(drawerMaxPx())));
  drawerGrip.setAttribute("aria-valuenow", String(w));
  if (!save) return;
  try {
    localStorage.setItem(DRAWER_W_KEY, String(w));
  } catch {
    /* storage unavailable: the width lasts the session */
  }
}
try {
  const saved = Number(localStorage.getItem(DRAWER_W_KEY));
  if (saved > 0) setDrawerWidth(saved, false);
} catch {
  /* storage unavailable: the drawer's own width it is */
}
drawerGrip.addEventListener("pointerdown", (ev) => {
  if (ev.button !== 0 || narrowScreen.matches) return;
  ev.preventDefault();
  const startX = ev.clientX;
  const startW = drawer.offsetWidth;
  drawerGrip.classList.add("dragging");
  const move = (m: PointerEvent) => setDrawerWidth(startW + m.clientX - startX, false);
  const end = () => {
    window.removeEventListener("pointermove", move);
    window.removeEventListener("pointerup", end);
    window.removeEventListener("pointercancel", end);
    drawerGrip.classList.remove("dragging");
    setDrawerWidth(drawer.offsetWidth, true);
  };
  window.addEventListener("pointermove", move);
  window.addEventListener("pointerup", end);
  window.addEventListener("pointercancel", end);
});
drawerGrip.addEventListener("keydown", (ev) => {
  if (narrowScreen.matches || (ev.key !== "ArrowLeft" && ev.key !== "ArrowRight")) return;
  ev.preventDefault();
  ev.stopPropagation(); // the arrows move the edge here, not the character
  setDrawerWidth(drawer.offsetWidth + (ev.key === "ArrowRight" ? DRAWER_STEP_PX : -DRAWER_STEP_PX), true);
});

/**
 * The ⋯ in the top right strip and the menu it opens: sound, the mark set
 * once and left, which had a square in the strip until 2026-09-29
 * ("Propose a way to reduce the map navigation button clutter", then
 * "implement it, fading area title, sound in the menu"). Full screen went
 * in with it and came back out on 2026-10-05 ("The fullscreen/windowed
 * button should be visible on phones and not hidden in the menu"). A press on the ⋯ opens and shuts it; a press anywhere else, a
 * choice from it, or Escape shuts it. Like the drawer it is not a window:
 * the world goes on and the keys still walk.
 */
const moreBtn = $<HTMLButtonElement>("morebtn");
const moreMenu = $("moremenu");
function moreOpen(): boolean {
  return !moreMenu.hidden;
}
function showMore(open: boolean) {
  if (moreOpen() === open) return;
  moreMenu.hidden = !open;
  moreBtn.setAttribute("aria-expanded", String(open));
  moreBtn.classList.toggle("on", open);
  if (open) fitMore();
}
/**
 * The menu reaches no further down than the window, and scrolls when it is
 * longer than that (2026-10-12: "The pop up menu needs to be scrollable if
 * it doesn't fit the screen"): a phone on its side has about 340 pixels of
 * height, and the menu's rows at 44 come to more than 400. It may hang
 * past the bottom of the view over the log, as it always has; only the
 * window's edge stops it.
 */
function fitMore() {
  if (!moreOpen()) return;
  moreMenu.style.maxHeight = "";
  const top = moreMenu.getBoundingClientRect().top;
  moreMenu.style.maxHeight = `${Math.max(80, Math.floor(window.innerHeight - top - 6))}px`;
}
addEventListener("resize", fitMore);
/** The word beside a drawing in the ⋯ menu. */
function menuWord(text: string): HTMLSpanElement {
  const w = document.createElement("span");
  w.textContent = text;
  return w;
}
// A burger since 2026-10-08 ("give the menu button in the top right a
// burger or some other icon, rather than ellipsis"); it was the three dots.
moreBtn.replaceChildren(viewGlyph("menu"));
// A drawing beside each of the game's buttons in the menu, as full screen
// and sound have (2026-09-30: "Add icons to the new menu buttons like log
// out"). Put in front of the word, which is a span of its own so the words
// that change as the game runs can be written without taking it away.
for (const [id, glyph] of [
  ["dbgbtn", "debug"],
  ["scoresbtn", "scores"],
  ["changesbtn", "changes"],
  ["helpbtn", "help"],
  ["logoutbtn", "logout"],
  // And on the Controls (2026-10-01: "Add icons to the controls").
  ["targetbtn", "target"],
  ["playerbtn", "targetplayer"],
  ["stopbtn", "stop"],
  ["pvpbtn", "stancewhite"],
  ["autobtn", "auto"],
  ["chasebtn", "chase"],
] as const) {
  $(id).prepend(viewGlyph(glyph));
}
moreBtn.setAttribute("aria-label", "More");
moreBtn.addEventListener("click", () => showMore(!moreOpen()));
// A choice from it shuts it, after the choice's own handler has run: the
// click bubbles here from the line pressed.
moreMenu.addEventListener("click", () => showMore(false));
document.addEventListener("pointerdown", (e) => {
  const t = e.target as Node | null;
  if (moreOpen() && t && !moreMenu.contains(t) && !moreBtn.contains(t)) showMore(false);
});

// The pixel font: the whole game in Departure Mono, or not (2026-10-01: "Add
// a setting in the menu that uses departure mono as the font"). One class on
// the root, which style.css hangs its font tokens on, and the font the names
// over the world are drawn in; remembered per browser, like the sound. The
// canvas cannot wait for a font the way a page can, so the names switch
// over once the file has arrived, and keep the UI font until then.
const PIXELFONT_KEY = "tinytibia.pixelfont";
{
  const fontBtn = $<HTMLButtonElement>("fontbtn");
  let on = false;
  try {
    on = localStorage.getItem(PIXELFONT_KEY) === "1";
  } catch {
    /* storage unavailable: the UI font */
  }
  const apply = () => {
    // The log rewraps in the other font: following, it stays on its end,
    // and scrolled back, on the line being read.
    ui.holdLogView(() => document.documentElement.classList.toggle("pixelfont", on));
    setLabelFont(null);
    if (on) {
      document.fonts.load('11px "Departure Mono"').then(
        () => on && setLabelFont('"Departure Mono", ui-monospace, monospace'),
        () => {},
      );
    }
    const name = on ? "Pixel font: on" : "Pixel font: off";
    fontBtn.title = on ? "The game is in Departure Mono, a pixel font. Click for the ordinary font" : "Click to set the game in Departure Mono, a pixel font";
    fontBtn.setAttribute("aria-label", name);
    fontBtn.classList.toggle("on", on);
    fontBtn.replaceChildren(viewGlyph("font"), menuWord(name));
  };
  apply();
  fontBtn.addEventListener("click", () => {
    on = !on;
    try {
      localStorage.setItem(PIXELFONT_KEY, on ? "1" : "0");
    } catch {
      /* storage unavailable: the choice lasts this session */
    }
    apply();
  });
}

// Pixel scaling (2026-11-02: "add a toggle for the pixel scaling thing we
// optimized. to enable/disable it"): the cap from 3bda8a6 that draws the
// world at no more than MAX_DPR backing pixels to a CSS pixel, which is
// what holds a phone's frame rate up. Full, the screen's own ratio, is the
// default, as it was before the cap (later that day: "default the pixel
// scaling to full, as it was before the optimization change"); capped is
// faster on a phone whose ratio is past 2 and the same as full on one that
// is not. Remembered per browser, like the font.
const PIXELCAP_KEY = "tinytibia.pixelcap";
{
  const capBtn = $<HTMLButtonElement>("pixelcapbtn");
  let capped = false;
  try {
    capped = localStorage.getItem(PIXELCAP_KEY) === "1";
  } catch {
    /* storage unavailable: full */
  }
  const apply = () => {
    renderer.dprCapped = capped;
    const ratio = window.devicePixelRatio || 1;
    const screen = `This screen has ${Math.round(ratio * 100) / 100} pixels to a point`;
    const name = capped ? "Pixel scaling: capped" : "Pixel scaling: full";
    capBtn.title = capped
      ? `The world is drawn at most ${MAX_DPR} pixels to a point, which is faster on a phone. ${screen}. Click to draw it at the screen's full sharpness`
      : `The world is drawn at the screen's full sharpness. ${screen}. Click to cap it at ${MAX_DPR}, which is faster on a phone`;
    capBtn.setAttribute("aria-label", name);
    capBtn.classList.toggle("on", capped);
    capBtn.replaceChildren(viewGlyph("pixelcap"), menuWord(name));
  };
  apply();
  capBtn.addEventListener("click", () => {
    capped = !capped;
    try {
      localStorage.setItem(PIXELCAP_KEY, capped ? "1" : "0");
    } catch {
      /* storage unavailable: the choice lasts this session */
    }
    apply();
  });
}

// How big the panels' squares and buttons are (2026-10-12: "In the menu,
// add an option to set panel button size. 32, 38, 44"): --hit, which
// style.css sets to 44 and to the two smaller sizes under a class on the
// root. A row in the ⋯ menu with the three to choose from, remembered per
// browser like the font. A choice leaves the menu open, so each size can be
// seen before it is kept. Every panel, the spells' included, lays itself
// out again by itself, since its squares are --hit wide.
const HITSIZE_KEY = "tinytibia.hitsize";
const HIT_SIZES = [32, 38, 44] as const;
{
  const row = $("hitsize");
  let size: number = 44;
  try {
    const saved = Number(localStorage.getItem(HITSIZE_KEY));
    if ((HIT_SIZES as readonly number[]).includes(saved)) size = saved;
  } catch {
    /* storage unavailable: 44 */
  }
  const label = document.createElement("span");
  label.className = "hitlabel";
  label.append(viewGlyph("hitsize"), menuWord("Button size"));
  const choices = HIT_SIZES.map((n) => {
    const b = document.createElement("button");
    b.type = "button";
    b.setAttribute("role", "menuitemradio");
    b.textContent = String(n);
    b.title = n === 44 ? "Panel buttons 44 pixels: a fingertip, the most to press" : `Panel buttons ${n} pixels: more in a panel, more of the world`;
    b.addEventListener("click", (e) => {
      e.stopPropagation(); // the menu stays open to show the size
      size = n;
      try {
        localStorage.setItem(HITSIZE_KEY, String(n));
      } catch {
        /* storage unavailable: the choice lasts this session */
      }
      apply();
    });
    return b;
  });
  row.replaceChildren(label, ...choices);
  const apply = () => {
    for (const n of HIT_SIZES) document.documentElement.classList.toggle(`hit${n}`, n !== 44 && n === size);
    HIT_SIZES.forEach((n, i) => choices[i].setAttribute("aria-checked", String(n === size)));
  };
  apply();
}

const soundBtn = $<HTMLButtonElement>("soundbtn");
{
  const paintSound = () => {
    const name = sounds.on ? "Sound off" : "Sound on";
    soundBtn.title = sounds.on ? "Sound off: silence the world's noises" : "Sound on: hear the world's noises";
    soundBtn.setAttribute("aria-label", name);
    // A drawing and its word, since it is a line in the ⋯ menu now.
    soundBtn.replaceChildren(viewGlyph(sounds.on ? "sound" : "mute"), menuWord(name));
  };
  const go = () => {
    sounds.set(!sounds.on);
    paintSound();
  };
  paintSound();
  soundBtn.addEventListener("click", go);
  longPress(soundBtn, () =>
    showSlotMenu({ title: sounds.on ? "Sound off" : "Sound on", look: "The world's noises: blows, spells, and the Wilhelm scream when a player dies. The music has its own switch", actions: [{ label: sounds.on ? "Sound off" : "Sound on", go }] }),
  );
}

// The music, switched apart from the noises (2026-10-10: "add a separate
// music on/off in the menu").
const musicBtn = $<HTMLButtonElement>("musicbtn");
{
  const paintMusic = () => {
    const name = sounds.musicOn ? "Music off" : "Music on";
    musicBtn.title = sounds.musicOn ? "Music off: stop the music" : "Music on: music for the ground, the heights and the caves";
    musicBtn.setAttribute("aria-label", name);
    musicBtn.replaceChildren(viewGlyph(sounds.musicOn ? "music" : "musicoff"), menuWord(name));
  };
  const go = () => {
    sounds.setMusic(!sounds.musicOn);
    paintMusic();
  };
  paintMusic();
  musicBtn.addEventListener("click", go);
  longPress(musicBtn, () =>
    showSlotMenu({ title: sounds.musicOn ? "Music off" : "Music on", look: "Quiet music under the noises: a country tune on the ground, bells and wind above it, a drone and dripping water under it. The noises have their own switch", actions: [{ label: sounds.musicOn ? "Music off" : "Music on", go }] }),
  );
}

// The world's corner button, and its twin on the depot screen, which covers
// that corner (2026-10-26: "Show a fullscreen icon in the depot dialog"):
// one toggle, drawn and labelled the same on both.
const fullBtns = [$<HTMLButtonElement>("fullbtn"), $<HTMLButtonElement>("depotfull")];
if (document.documentElement.requestFullscreen) {
  for (const fullBtn of fullBtns) fullBtn.hidden = false;
  // What the button is offering to do next, which is the opposite of the
  // state the window is in. Both halves are written here rather than in
  // the markup because they are a pair and a pair kept in two files drifts.
  const words = () =>
    document.fullscreenElement !== null
      ? {
          name: "Windowed",
          says: "Give the screen back. Escape, F11 and the back gesture do the same",
        }
      : {
          name: "Fullscreen",
          says: "Fill the screen. Escape, F11 and the back gesture leave it again",
        };
  const label = () => {
    const w = words();
    for (const fullBtn of fullBtns) {
      fullBtn.title = `${w.name}: ${w.says}`;
      fullBtn.setAttribute("aria-label", w.name);
      // replaceChildren rather than a second element kept hidden: there is
      // one drawing on this button at a time, and two svgs taking it in
      // turns is two things to keep in step for no gain.
      // A drawing alone: it is in the strip again since 2026-10-05, where
      // there is no room for the word the ⋯ menu wrote beside it.
      fullBtn.replaceChildren(viewGlyph(document.fullscreenElement !== null ? "contract" : "expand"));
    }
  };
  const toggle = () => {
    if (document.fullscreenElement) {
      void document.exitFullscreen();
      return;
    }
    document.documentElement.requestFullscreen({ navigationUI: "hide" }).catch(() => {
      // Refused: the button has said its piece and the game carries on in
      // the tab, which is where it was already playable.
    });
  };
  for (const fullBtn of fullBtns) fullBtn.addEventListener("click", toggle);
  // The hold, for a screen with no hover. The menu is built when the press
  // fires rather than up front, so it names the state the window is
  // actually in at that moment — and the action in it is the same toggle
  // the click runs, so there is one way into full screen and not two. A
  // button press inside the menu is a user gesture in its own right, which
  // is the only reason asking for the screen from in there works at all.
  for (const fullBtn of fullBtns)
    longPress(fullBtn, () => {
      const w = words();
      showSlotMenu({ title: w.name, look: w.says, actions: [{ label: w.name, go: toggle }] });
    });
  // The back gesture, Escape and F11 all leave full screen without going
  // anywhere near the button, so the drawing follows the browser rather
  // than the last thing that was pressed: a button that swapped its own
  // mark on click would be showing the wrong one for the rest of the
  // session the first time somebody pressed Escape. The canvas needs no
  // telling: it measures itself every frame.
  // Pressed from the depot screen, the focus stays in the screen.
  document.addEventListener("fullscreenchange", () => {
    label();
    if (depotDlg.hidden) canvas.focus();
  });
  label();
}


// --- help ---------------------------------------------------------------------

function showHelp(open: boolean) {
  help.hidden = !open;
  if (open) {
    held.clear(); // don't keep walking while the dialog is up
    $("helpclose").focus();
  } else canvas.focus();
}

$("helpbtn").addEventListener("click", () => showHelp(true));
$("helpclose").addEventListener("click", () => showHelp(false));
help.addEventListener("mousedown", (ev) => {
  if (ev.target === help) showHelp(false); // click the backdrop to dismiss
});
// The contents line scrolls the help to its section without putting the
// section's name in the address bar.
for (const a of Array.from(help.querySelectorAll<HTMLAnchorElement>(".helptoc a"))) {
  a.addEventListener("click", (ev) => {
    ev.preventDefault();
    document.getElementById(a.hash.slice(1))?.scrollIntoView({ block: "start" });
  });
}

/**
 * A panel's own help, off its link in the main help's Panel icons entry.
 *
 * Every panel has one, and they are the same dialog in every respect but
 * their contents, so they are wired here off the markup rather than one
 * block each: the button names its dialog in aria-controls, and the
 * dialog's Close button is its id with "close" on the end. The words are
 * in index.html with the rest of the help. What used to be on the titles
 * — a paragraph on the Skills toggle's tooltip, a bracketed caption on
 * the Depot's, six lines of clicks on the bag — is in here instead, since
 * a title has room for a name and a control, and an explanation that has
 * to fit beside one is an explanation with most of it left out.
 *
 * Closing puts the focus back on the link that opened it rather than on the
 * canvas: it is where the keyboard was when the dialog opened, and the
 * keys that walk you are read off the window and not off the canvas, so
 * nothing is lost by leaving it there.
 */
/*
 * The selector is every question mark on the page that names a dialog,
 * and not the panel titles it used to be. One of them is not on a title
 * at all: the bag's, which went into the box the bag icon opens (asked
 * for on 2026-09-22: "remove the inventory panel and make it a bag icon
 * in the bottom right corner. clicking it pops up the inventory"), and
 * which travels into the Inventory panel's title on the screens where
 * the squares live there. It is still the same button opening the same
 * dialog and it is still wired by this one loop, which is the whole
 * point of the loop — the alternative was a special case written out by
 * hand, which is exactly what this replaced when the bag's and the map's
 * help were special cases before.
 *
 * The spells' question mark left the column for a few hours the same day
 * and is back on the panel's title with the panel, which cost this loop
 * nothing at all: it was never looking for titles.
 */
/*
 * Since 2026-10-10 there are no question marks left on any title ("remove
 * the panel help button from the drawer panels"): every dialog is opened
 * from a link in the main help, so the loop looks for those alone.
 */
const panelHelps: [HTMLElement, (open: boolean) => void][] = [];
for (const btn of Array.from(document.querySelectorAll<HTMLButtonElement>(".helplink[aria-controls]"))) {
  const dlg = $(btn.getAttribute("aria-controls") ?? "");
  const close = $(dlg.id + "close");
  const show = (open: boolean) => {
    dlg.hidden = !open;
    if (open) {
      held.clear(); // don't keep walking while the dialog is up
      close.focus();
    } else btn.focus();
  };
  btn.addEventListener("click", () => show(true));
  close.addEventListener("click", () => show(false));
  dlg.addEventListener("mousedown", (ev) => {
    if (ev.target === dlg) show(false); // click the backdrop to dismiss
  });
  panelHelps.push([dlg, show]);
}

/**
 * Shut a menu when the ground outside it is pressed — and only when it is
 * really pressed.
 *
 * This listens for pointerdown rather than mousedown, and the difference
 * is the whole of why it exists. A menu opened by a long press appears
 * while the finger is still down on the thing that was held: the press has
 * not finished, and letting go of it mints the mouse events a touchscreen
 * mints for a gesture nothing claimed. Those are hit-tested where the
 * finger is, which — for a panel in the strip along the bottom of a phone,
 * with the menu drawn in the middle of the screen — is the dark ground
 * round the box. Read as a mousedown outside the menu, that is a menu
 * which shuts in the same instant it opened, every time, and a menu you
 * can never read.
 *
 * They are echoes and not presses, and the way to tell is that they come
 * alone: the pointerdown of that gesture happened long ago, on the button
 * underneath, before this menu existed. A finger that really does come
 * down on the ground outside sends a pointerdown of its own, and so does a
 * mouse, so nothing is lost by asking for one. The swallow in touch.ts
 * cannot cover this case itself — its listeners are on the element that
 * was held, and these events never reach it.
 *
 * There are two shapes of "outside" and this covers both from one place.
 * A menu with a backdrop is its own outside: the element fills the screen,
 * the box is a child of it, and a press whose target is the element itself
 * landed on the dark ground round the box. A box with no backdrop — the
 * bag, which opens in the corner of the world over a world that is still
 * being played — has no such element, so its outside is the rest of the
 * document, and what has to be excluded instead is the box and the button
 * that opened it. Without that second exclusion the press on the bag icon
 * would shut the bag in the same instant the click opened it, which is the
 * self-closing menu this function already exists to prevent, arrived at
 * from the other direction.
 */
function dismissOnOutsidePress(menu: HTMLElement, hide: () => void, keep?: HTMLElement[]) {
  if (!keep) {
    menu.addEventListener("pointerdown", (ev) => {
      if (ev.target === menu) hide();
    });
    return;
  }
  document.addEventListener("pointerdown", (ev) => {
    if (menu.hidden) return;
    const at = ev.target as Node | null;
    if (at && (menu.contains(at) || keep.some((k) => k.contains(at)))) return;
    hide();
  });
}

/**
 * What to do with one thing in the bag: the touchscreen's right-click.
 *
 * A mouse has three answers for a slot — click to use, right-click to drop
 * one, ctrl+right-click to drop the pile — and a finger has two, a tap
 * and a long press. The long press used to be spent on dropping one, which
 * left no gesture at all for the whole pile: forty rocks went on the floor
 * one tap at a time, or not at all. So the long press opens this instead,
 * and every drop a mouse can make is a button in it.
 *
 * It carries the item's description as well, because that is the other
 * thing a finger cannot do: the slot's tooltip only ever existed for a
 * pointer hovering over it.
 */
const slotMenu = $("slotmenu");
const slotMenuActs = $("slotmenuacts");
function hideSlotMenu() {
  slotMenu.hidden = true;
  canvas.focus();
}
function showSlotMenu(m: SlotMenu | null) {
  if (!m) {
    hideSlotMenu();
    return;
  }
  $("slotmenutitle").textContent = m.title;
  $("slotmenulook").textContent = m.look;
  slotMenuActs.replaceChildren(
    ...m.actions.map((a) => {
      const b = document.createElement("button");
      b.type = "button";
      b.textContent = a.label;
      if (a.heavy) b.classList.add("all");
      b.disabled = !!a.disabled;
      if (a.why) b.title = a.why;
      b.addEventListener("click", () => {
        hideSlotMenu();
        a.go();
      });
      return b;
    }),
  );
  slotMenu.hidden = false;
  held.clear();
  // The first action rather than the Cancel: a menu that opens with the
  // way out under the thumb is a menu you have to reach past to use.
  (slotMenuActs.querySelector("button:not(:disabled)") as HTMLElement | null)?.focus();
}
/**
 * What the console is showing: the list of kinds, opened from the button
 * at the end of the chat line. The switches themselves are built and kept
 * by the panel code, which owns the muted set and the log; all that lives
 * here is the opening and the shutting, the way it does for the other two
 * menus. It is wired like them rather than like a panel's question mark
 * because it is a menu of live switches and not a page of words.
 */
const filtersMenu = $("filtersmenu");
function showFilters(open: boolean) {
  filtersMenu.hidden = !open;
  if (open) {
    held.clear(); // don't keep walking while the menu is up
    // The first switch rather than the Close, for the slot menu's reason:
    // a menu that opens with the way out under the thumb is a menu you
    // have to reach past to use.
    ($("filtersmenuacts").firstElementChild as HTMLElement | null)?.focus();
  } else $("filtersbtn").focus();
}
$("filtersbtn").addEventListener("click", () => showFilters(true));
$("filtersmenuclose").addEventListener("click", () => showFilters(false));
filtersMenu.addEventListener("mousedown", (ev) => {
  if (ev.target === filtersMenu) showFilters(false);
});

$("slotmenuclose").addEventListener("click", hideSlotMenu);
dismissOnOutsidePress(slotMenu, hideSlotMenu);

/**
 * What to do with another player: the right-click on a person, and the
 * touchscreen's long press, which are the same gesture here as they are on
 * a bag slot. A plain click on a player is one thing — follow them, or
 * attack them when they are fair game — and a right-click used to be a
 * look, which left trading with nowhere to go and the two clicks to be
 * learned by trying them. The menu gathers everything a player is for
 * under the one gesture that asks "what can I do with this?".
 *
 * Each button sends exactly what the older ways send: Look is the look a
 * right-click was, Follow and Attack are the click, Stop following is the
 * Stop button. Attack is on the menu whether or not it is allowed, greyed
 * with the reason when it is not, so the rule about fighting people is
 * learned from the menu rather than by wondering where the button went.
 */
const playerMenu = $("playermenu");
const playerMenuActs = $("playermenuacts");

/**
 * The spell said at somebody standing here, if this character holds one:
 * found by what it asks for rather than by its name, since the client
 * keeps no list of the world's spells. It is the blood bond today.
 */
function nearSpell(): SpellView | undefined {
  return game.you?.spells?.find((s) => s.arg === "near");
}

/** The words that cast sp at somebody by name. */
function sayAt(sp: SpellView, name: string) {
  net.send({ t: "say", text: `${sp.words} ${name}` });
}

/**
 * Who a spell said at somebody in sight could be said at: every other
 * player on your floor that this client is drawing, nearest first, as a
 * menu of names. It is the same set the server allows — bondNear is the
 * same floor and the view ring, and the view ring is exactly what the
 * server sends — so a name on this list is a name the words will take,
 * and the refusals that are left (a body with nobody at the reins, not
 * enough mana) come back in the console as they would for typed words.
 *
 * The person already carried is on the list like anybody else and says
 * so, because saying the words at them again takes the bond off, for
 * nothing. The chat box is kept as the last line, for a name the client
 * cannot see and a player who would rather type.
 */
function pickNearby(sp: SpellView): SlotMenu {
  const me = game.me();
  const here = me?.z ?? 0;
  const near = [...game.creatures.values()]
    .filter((c) => c.kind === "player" && c.id !== game.myId && c.hp > 0 && (c.z ?? 0) === here)
    .sort((a, b) => {
      const d = (c: ClientCreature) => (me ? Math.max(Math.abs(c.x - me.x), Math.abs(c.y - me.y)) : 0);
      return d(a) - d(b) || a.name.localeCompare(b.name);
    });
  const carrying = game.you?.bondwith ?? "";
  const actions: SlotMenu["actions"] = near.map((c) => ({
    label: c.name === carrying ? `${c.name} — take it off` : c.name,
    go: () => sayAt(sp, c.name),
  }));
  actions.push({
    label: "Type a name…",
    go: () => {
      chat.value = `${sp.words} `;
      chat.focus();
    },
  });
  const name = sp.name.charAt(0).toUpperCase() + sp.name.slice(1);
  return {
    title: `${name} on whom?`,
    look: near.length
      ? `Anybody standing in sight of you. ${sp.mana} mana${sp.why ? ` — ${sp.why}` : ""}`
      : "Nobody is in sight of you. It is laid standing together.",
    actions,
  };
}
/**
 * Where a spell said with a place after it can take you: the places the
 * server sent with it, one button each, then the ones you have not stood
 * in yet greyed out, so the list shows everywhere it goes (2026-10-10),
 * and the chat box as the last resort. The refusals left (a fight, a skull, under the ground, not
 * enough mana) come back in the console as they would for typed words.
 */
function pickPlace(sp: SpellView): SlotMenu {
  const places = sp.places ?? [];
  const actions: SlotMenu["actions"] = places.map((p) => ({
    label: p.name,
    go: () => sayAt(sp, p.key),
  }));
  for (const p of sp.unvisited ?? []) {
    actions.push({
      label: p.name,
      disabled: true,
      why: `You have not stood in ${p.name} yet.`,
      go: () => {},
    });
  }
  actions.push({
    label: "Type a place…",
    go: () => {
      chat.value = `${sp.words} `;
      chat.focus();
    },
  });
  const name = sp.name.charAt(0).toUpperCase() + sp.name.slice(1);
  return {
    title: `${name} to where?`,
    look: places.length
      ? `Anywhere you have stood in. ${sp.mana} mana${sp.why ? ` — ${sp.why}` : ""}`
      : sp.moot ?? sp.why ?? "Nowhere yet but where you are.",
    actions,
  };
}
/**
 * What a spell said with a creature after it can call up: the kinds the
 * server sent, cheapest first, each with its price, and the ones dearer
 * than the mana in hand greyed out with what they cost. The chat box is the
 * last resort, as for the Eagles.
 */
function pickCreature(sp: SpellView): SlotMenu {
  const kinds = sp.creatures ?? [];
  const mana = game.you?.mana ?? 0;
  const actions: SlotMenu["actions"] = kinds.map((k) => {
    const short = k.mana > mana;
    return {
      label: `${k.name} · ${k.mana}`,
      disabled: short,
      why: short ? `${k.name} costs ${k.mana} mana and you have ${mana}.` : undefined,
      go: () => sayAt(sp, k.key),
    };
  });
  actions.push({
    label: "Type a creature…",
    go: () => {
      chat.value = `${sp.words} `;
      chat.focus();
    },
  });
  return {
    title: "Summon which creature?",
    look: kinds.length
      ? `It fights for you for three minutes and costs its experience in mana.${sp.why ? ` — ${sp.why}` : ""}`
      : sp.why ?? "Nothing your pool could hold yet.",
    actions,
  };
}
/**
 * Who a spell said at somebody by name, wherever they are, could be said
 * at: everybody else on the Players panel, in its order, one button each
 * (2026-10-15: "Clicking the find person spell should show a menu to
 * choose the person"). The panel is everybody the server says is in the
 * world for this reader, which is who the words can find; somebody gone
 * /hide is off it, and the chat box is kept as the last line for them and
 * for a player who would rather type.
 */
function pickPerson(sp: SpellView): SlotMenu {
  const names = players.others();
  const actions: SlotMenu["actions"] = names.map((n) => ({
    label: n,
    go: () => sayAt(sp, n),
  }));
  actions.push({
    label: "Type a name…",
    go: () => {
      chat.value = `${sp.words} `;
      chat.focus();
    },
  });
  const name = sp.name.charAt(0).toUpperCase() + sp.name.slice(1);
  return {
    title: `${name} on whom?`,
    look: names.length
      ? `Anybody in the world. ${sp.mana} mana${sp.why ? ` — ${sp.why}` : ""}`
      : "Nobody else is in the world just now.",
    actions,
  };
}
function hidePlayerMenu() {
  playerMenu.hidden = true;
  canvas.focus();
}
function showPlayerMenu(c: ClientCreature) {
  if (!game.you) return;
  $("playermenutitle").textContent = c.name;
  $("playermenulook").textContent = c.level ? `Level ${c.level}.` : "";
  const following = game.you.target === c.id && !c.hostile;
  // The server's rule, which it has already asked: hostile is "your
  // stance allows them" (2026-10-08: the stances) and they are not under
  // half your level who is not protection zone locked (2026-10-09).
  const mayFight = c.hostile;
  const actions: MenuAction[] = [
    { label: "Look", title: "Their level and the gear they are using", go: () => net.send({ t: "look", x: c.x, y: c.y }) },
    following
      ? { label: "Stop following", title: "Let go: what the Stop button does", go: () => net.send({ t: "attack", id: 0 }) }
      : {
          label: "Follow",
          title: c.hostile
            ? "You may fight them, so a click on them is an attack: there is no following somebody you may fight"
            : "Walk after them wherever they go, until you stop",
          disabled: c.hostile,
          go: () => pickTarget(c.id),
        },
    {
      label: "Attack",
      heavy: true,
      title: mayFight
        ? "Fight them, chased from this click. They may hit back whatever their own stance says"
        : attackForbids(game.you.stance, game.you.level, c.level, c.locked),
      disabled: !mayFight,
      go: () => pickTarget(c.id),
    },
    { label: "Trade", title: "Ask them to trade. Both of you see both offers, and nothing moves until both of you accept", go: () => net.send({ t: "trade", id: c.id }) },
    messageAction(c.name),
  ];
  // The spell said at somebody in sight, when this character holds it:
  // right-click or hold a player and it is one press (2026-09-23, "allow
  // long pressing or right clicking on a player and clicking bond"). On
  // somebody already carried it takes the bond off, which is free and
  // never refused, so it is never greyed; otherwise it is greyed with the
  // server's own reason when the spell would be refused.
  // Athelas crushed for them (2026-10-21: "make it so athelas can be used
  // to heal other players, including poison"), while you carry any: the
  // leaf's heal and cure, done to them, standing beside them. The server
  // says why when it will not go; the one thing greyed here is the
  // distance, which the client can see.
  const leaf = game.you.inv.find((it) => {
    const d = game.itemDefs.get(it.id);
    return !!d?.cures && !!d.heal;
  });
  if (leaf) {
    const leafName = game.itemDefs.get(leaf.id)?.name ?? "athelas";
    const me = game.me();
    const beside = !!me && me.z === c.z && Math.max(Math.abs(c.x - me.x), Math.abs(c.y - me.y)) <= 1;
    actions.push({
      label: `Tend with ${leafName}`,
      title: beside
        ? `Crush ${leafName} for them: it heals them as it would you, and draws out any poison. Uses one of yours`
        : `Stand beside ${c.name} to tend them`,
      disabled: !beside,
      go: () => net.send({ t: "tend", id: c.id }),
    });
  }
  const sp = nearSpell();
  if (sp) {
    const carrying = game.you.bondwith === c.name;
    const name = sp.name.charAt(0).toUpperCase() + sp.name.slice(1);
    actions.push({
      label: carrying ? `Take the ${sp.name} off` : name,
      title: carrying
        ? `Say the words at ${c.name} again: their wounds are their own, and it costs nothing`
        : sp.why || `${sp.look} ${sp.mana} mana.`,
      disabled: !carrying && !!sp.why,
      go: () => sayAt(sp, c.name),
    });
  }
  fillPlayerMenu(actions);
}

/** One button on the player menu. */
type MenuAction = { label: string; title?: string; disabled?: boolean; heavy?: boolean; go: () => void };

/** Puts actions on the player menu as its buttons, opens it, and gives the first of them the keyboard. */
function fillPlayerMenu(actions: MenuAction[]) {
  playerMenuActs.replaceChildren(
    ...actions.map((a) => {
      const b = document.createElement("button");
      b.type = "button";
      b.textContent = a.label;
      if (a.title) b.title = a.title;
      if (a.heavy) b.className = "all";
      b.disabled = !!a.disabled;
      b.addEventListener("click", () => {
        hidePlayerMenu();
        a.go();
      });
      return b;
    }),
  );
  playerMenu.hidden = false;
  held.clear();
  (playerMenuActs.firstElementChild as HTMLElement | null)?.focus();
}
$("playermenuclose").addEventListener("click", hidePlayerMenu);
dismissOnOutsidePress(playerMenu, hidePlayerMenu);

/**
 * What can be asked of one of the world's people: the menu a click on an
 * NPC opens. Asked for on 2026-10-05: "Make clicking an NPC provide
 * options like starting quests, vendor, etc". Talking to them was typing
 * their words within three tiles of them, and nothing said which words;
 * now their topics are choices, as the server lists them in the welcome
 * (see Game.npcs).
 *
 * Quests first, marked with a "!" until they are done and a tick after, then Trade for a shopkeeper — which
 * asks for the Vendor panel, so it opens within reach of them — then the
 * rest of their topics, then Look (see npcEntries). A topic sends an
 * "ask", which the server answers within three tiles of them (2026-10-08:
 * "make it so you must be within 3 tiles of an npc to interact with
 * them"; it was beside them from 2026-10-07).
 *
 * It is the small menu that pops up beside them, with their name over
 * it, rather than a box in the middle of a darkened screen (2026-10-08:
 * "clicking an npc when they're within range of interacting, should not
 * move toward them. and use a context menu that pops up over them, like
 * we're supposed to have for message log interactions."): the world goes on round it, and a press
 * anywhere else or Escape shuts it. And it opens only once you are within
 * reach of them (2026-10-08: "when an npc is clicked, don't show the
 * interaction menu until we arrive within range"): the click asks the
 * server to walk you up (see npcMenuFor), and this is called when it says
 * you have arrived.
 */
function showNpcMenu(c: ClientCreature) {
  if (!game.you) return;
  const menu = game.npcs.get(c.kind);
  const items = npcEntries(menu, game.topicsDone.get(c.kind)).map((e): PopItem => {
    switch (e.kind) {
      case "ask":
        return {
          label: e.label,
          title: e.quest
            ? `A quest: ask ${c.name} about it`
            : e.done
              ? `A quest you have finished: ask ${c.name} about it`
              : `Ask ${c.name} about it`,
          quest: e.quest,
          go: () => net.send({ t: "ask", id: c.id, topic: e.topic }),
        };
      case "trade":
        return {
          label: e.label,
          title: `See what ${c.name} sells and buys: the Vendor panel opens within three tiles of them`,
          go: () => {
            askVendor(c.name);
            // Walked up to, once, which is nothing for somebody already
            // within three tiles of them: never followed (2026-10-07: "don't allow npcs
            // to be followed"). See approachNPC in talk.go.
            net.send({ t: "approach", id: c.id });
          },
        };
      case "look":
        return { label: e.label, title: "What a shift-click says of them", go: () => net.send({ t: "look", x: c.x, y: c.y }) };
    }
  });
  held.clear();
  showPopMenu({ label: c.name, head: c.name, items }, renderer.tileClientRect(c.x, c.y, c.z), { side: "right", back: canvas, on: canvas });
}

/**
 * The NPC whose menu is waiting on the server's word that you are within
 * reach of them ("reached"), or nought. A click on one sets it and sends
 * the walk up; the answer opens the menu if it is still for them. A walk
 * up that is called off — a key, a click somewhere else, Stop — never
 * gets its answer, so the menu never opens; and Trade on an open menu
 * sends the same walk up with this at nought, so its answer is not taken
 * for a second click.
 */
let npcMenuFor = 0;

/** A click on one of the world's people: walked up to, and their menu once within reach. */
function clickNpc(c: ClientCreature) {
  npcMenuFor = c.id;
  net.send({ t: "approach", id: c.id });
}

// --- the players panel ---------------------------------------------------------

// Everybody in the world with their levels and skulls, kept up by the
// server rather than asked for (2026-09-27: "show a panel with online
// players and their levels, and skull status"). See players.ts.
//
// A row is somebody's name, so a click on it opens the menu a right-click
// on them in the world opens. That menu is about somebody you can reach,
// and it needs them on the screen to act on, so somebody out of sight gets
// the spells said at them by name instead, which reach anywhere: a click
// on a name always opens something (2026-10-15: "Make it obvious that you
// can click or long press the names in the player list").
const players = new PlayersPanel(
  $("players"),
  () => game.you?.name ?? "",
  (name, level) => {
    const c = [...game.creatures.values()].find((c) => c.kind === "player" && c.name === name && c.id !== game.myId);
    if (c) showPlayerMenu(c);
    else showNameMenu(name, level, true);
  },
  (name, text) => showSlotMenu({ title: name, look: text, actions: [] }),
  {
    // While the chat box is open for typing a click on a name types it
    // there instead (2026-10-07). Open is focused: Enter opens it, and
    // Enter or Escape shuts it.
    chat: { active: () => document.activeElement === chat, put: (name) => putInChat(chat, name) },
    menu: showNameMenu,
  },
);

/**
 * The spells a right-click or a long press on a name in the Players panel
 * offers (2026-10-07: "right clicking or long-pressing a players name in
 * the player list allows to cast blood bond or find person from a menu"),
 * in the player menu's box. Each says the words and the name into the
 * world exactly as typing them would — saying the words is the casting,
 * and there is no other way in — and is greyed, with why on its hover,
 * when the Spells panel would dim it: not held yet, refused, or cooling.
 * See nameSpells.
 */
function showNameMenu(name: string, level: string, away = false) {
  if (!game.you) return;
  const off = game.clockOffset;
  const spells = nameSpells(game.you.spells, name, game.you.bondwith ?? "", off === null ? null : performance.now() - off);
  $("playermenutitle").textContent = name;
  $("playermenulook").textContent = `${level}${away ? ", not in sight of you" : ""}. Find person reaches them anywhere in the world; a blood bond is laid standing together.`;
  fillPlayerMenu([messageAction(name), ...spells.map((s) => ({ label: s.label, title: s.title, disabled: s.disabled, go: () => net.send({ t: "say", text: s.text }) }))]);
}

/** Message on a player's menu: a private message to them started in the
 *  chat box (see startTell). */
function messageAction(name: string): MenuAction {
  return { label: "Message", title: `Send ${name} a message nobody else sees, wherever they are`, go: () => startTell(name) };
}

/**
 * A private message to name started in the chat box (2026-10-17: "support
 * direct/private messaging of players in game"): "/w Name " ahead of
 * whatever was being typed, with the caret at the end to carry on. A line
 * already addressed with /w, /tell, /msg or /r is readdressed rather than
 * addressed twice. The focus going in is what opens the folded phone line,
 * as for Reply.
 */
function startTell(name: string) {
  const rest = chat.value.replace(/^\/(?:w|tell|msg)\s+\S+\s*|^\/r\s+/i, "");
  const line = `/w ${name} ${rest}`.slice(0, chat.maxLength > 0 ? chat.maxLength : undefined);
  chat.value = line;
  chat.focus();
  chat.setSelectionRange(line.length, line.length);
}

// --- highscores ---------------------------------------------------------------

const scores = $("scores");

function showScores(open: boolean) {
  scores.hidden = !open;
  if (open) {
    held.clear();
    $("scoresclose").focus();
  } else if ($("login").hidden) canvas.focus();
  else $<HTMLInputElement>("username").focus(); // shut from the login screen, go back to the form
}

$("scoresbtn").addEventListener("click", () => {
  if (game.ready) net.send({ t: "highscores" });
});

// The same boards from the login screen, where there is no connection to
// ask over: the server publishes them at /highscores for anyone at all.
// A scoreboard you have to make a character to read is one nobody deciding
// whether to make a character ever sees.
$("loginscores").addEventListener("click", () => {
  const btn = $<HTMLButtonElement>("loginscores");
  btn.disabled = true;
  fetch("highscores")
    .then((r) => (r.ok ? r.json() : null))
    .then((hs: Highscores | null) => {
      if (hs?.boards?.length) fillScores(hs);
      else btn.textContent = "The boards are empty: nobody has played yet.";
    })
    .catch(() => {
      btn.textContent = "The highscores are out of reach just now.";
    })
    .finally(() => {
      btn.disabled = false;
    });
});
// And the changelog, on exactly the same argument and over the same sort
// of endpoint. What has changed is most of what somebody coming back after
// a while wants to know, and knowing it before typing a name is the point:
// a log you have to log in to read is one people find out about after
// deciding not to bother.
//
// Nothing is marked as new here, and that is not an omission. New is
// measured against when a particular character was last in the world, and
// at the login screen there is no character to measure against — so the
// server hands over the whole log unmarked rather than guessing at a
// reader it has not met.
$("loginchanges").addEventListener("click", () => {
  const btn = $<HTMLButtonElement>("loginchanges");
  btn.disabled = true;
  fetch("changes")
    .then((r) => (r.ok ? r.json() : null))
    .then((ch: Changes | null) => {
      if (ch?.entries?.length) fillChanges(ch);
      else btn.textContent = "There is no changelog to read.";
    })
    .catch(() => {
      btn.textContent = "The changelog is out of reach just now.";
    })
    .finally(() => {
      btn.disabled = false;
    });
});
// An address ending #changes opens the changelog as the page comes up, as
// if the button had been pressed: it is what the server's "up" post on
// Discord links to (2026-10-28: "should link to the changes page for the
// server"). The hash comes off, so a reload is the plain login screen.
if (location.hash === "#changes") {
  history.replaceState(history.state, "", location.pathname + location.search);
  $("loginchanges").click();
}
$("scoresclose").addEventListener("click", () => showScores(false));
scores.addEventListener("mousedown", (ev) => {
  if (ev.target === scores) showScores(false);
});

/** How many rows of a highscore board show at a time. */
const HS_PAGE = 10;

/** One row of a highscore board: rank, name, race on boards of characters, and score. */
function scoreRow(board: Highscores["boards"][number], r: Highscores["boards"][number]["rows"][number], i: number, myName: string): HTMLTableRowElement {
  const tr = document.createElement("tr");
  // Your own row is picked out — on the boards that are about people.
  // The deadliest board lists monsters, and a dragon that shares a
  // name with the reader is not the reader.
  if (!board.of && r.name === myName) tr.className = "me";
  // The race goes beside the name on the Level and skill boards
  // (2026-10-16: "Add races to the highscores"; 2026-10-18: "only skills
  // and level care about the race of the scorer"), blank for somebody
  // who has not chosen one so the columns stay lined up.
  const cells: [string, string][] = [
    ["rank", `${i + 1}.`],
    ["", r.name],
  ];
  if (board.races) cells.push(["race", r.race ?? ""]);
  cells.push(["val", r.text ?? num(r.value)]);
  for (const [cls, text] of cells) {
    const td = document.createElement("td");
    if (cls) td.className = cls;
    // A character's name opens their page (2026-10-17: "clicking a
    // character name in the highscores shows the character page,
    // which lists deaths and kills"). Monsters, spells and races
    // have none.
    if (cls === "" && !board.of) td.appendChild(charLink(text));
    else td.textContent = text;
    tr.appendChild(td);
  }
  return tr;
}

function fillScores(msg: Highscores) {
  const body = $("scoresbody");
  body.replaceChildren();
  // Whoever is reading: the character in the world, or — at the login
  // screen, where there is nobody in it yet — the name typed in the form,
  // so you can look yourself up before you go in.
  const myName = game.you?.name ?? game.me()?.name ?? $<HTMLInputElement>("username").value.trim();
  for (const board of msg.boards) {
    const table = document.createElement("table");
    const cap = document.createElement("caption");
    cap.textContent = board.name;
    table.appendChild(cap);
    // A board with nothing on it yet: an older server sends null rather
    // than an empty list, and one board that threw here took the whole
    // window with it — the button opened nothing at all in a world where
    // nobody had been killed by anything.
    const rows = board.rows ?? [];
    const tbody = document.createElement("tbody");
    table.appendChild(tbody);
    // Ten to a page, each board with its own next and previous (2026-10-18:
    // "limit the monsters killed highscores to 10. make sure there's a
    // paging mechanism for each score panel"). The server sends every row
    // it ranks, up to a hundred, so turning a page asks it nothing.
    const pages = Math.max(1, Math.ceil(rows.length / HS_PAGE));
    let page = 0;
    const show = () => {
      tbody.replaceChildren();
      const from = page * HS_PAGE;
      rows.slice(from, from + HS_PAGE).forEach((r, j) => tbody.appendChild(scoreRow(board, r, from + j, myName)));
      // A short last page keeps the board its full height, so the page
      // buttons stay under the finger while they are pressed.
      if (pages > 1) {
        for (let k = rows.length - from; k < HS_PAGE; k++) {
          const tr = document.createElement("tr");
          tr.className = "blank";
          const td = document.createElement("td");
          td.innerHTML = "&nbsp;";
          tr.appendChild(td);
          tbody.appendChild(tr);
        }
      }
      if (pager) {
        pager.prev.disabled = page === 0;
        pager.next.disabled = page === pages - 1;
        pager.where.textContent = `${from + 1}–${Math.min(from + HS_PAGE, rows.length)} of ${rows.length}`;
      }
    };
    let pager: { prev: HTMLButtonElement; next: HTMLButtonElement; where: HTMLElement } | undefined;
    if (pages > 1) {
      const foot = document.createElement("tfoot");
      const tr = document.createElement("tr");
      const td = document.createElement("td");
      td.colSpan = board.races ? 4 : 3;
      td.className = "pager";
      const button = (label: string, title: string, step: number) => {
        const b = document.createElement("button");
        b.type = "button";
        b.textContent = label;
        b.title = title;
        b.setAttribute("aria-label", `${title}: ${board.name}`);
        b.addEventListener("click", () => {
          page = Math.min(pages - 1, Math.max(0, page + step));
          show();
        });
        return b;
      };
      const where = document.createElement("span");
      where.className = "where";
      pager = { prev: button("‹", "Previous page", -1), next: button("›", "Next page", 1), where };
      const bar = document.createElement("div");
      bar.append(pager.prev, where, pager.next);
      td.appendChild(bar);
      tr.appendChild(td);
      foot.appendChild(tr);
      table.appendChild(foot);
    }
    show();
    body.appendChild(table);
  }
  showScores(true);
}

// --- character pages ----------------------------------------------------------

/**
 * A character's page: who they are, their deaths and the players they
 * killed, newest first, and the monsters they have hunted. It is read
 * over HTTP from /character, like the boards from the login screen, so it
 * opens the same way logged in or not. It sits over the highscores, and
 * shutting it goes back to them. A name on the page with a page of its
 * own opens that one in its place.
 */
const charPage = $("charpage");

/** Whose page is open, or last was: what Copy link links to. */
let charShown = "";

function showCharacter(open: boolean) {
  charPage.hidden = !open;
  if (open) {
    // Opened from a link there may be no highscores under it to go back to.
    $("charclose").textContent = scores.hidden ? "Close" : "Back to the highscores";
    $("charclose").focus({ preventScroll: true });
    return;
  }
  // Shut, the address is the plain site again, so a reload or a copy of
  // it is not the page just closed.
  if (characterInHash(location.hash) !== null) history.replaceState(history.state, "", location.pathname + location.search);
  if (!scores.hidden) $("scoresclose").focus();
  else if ($("login").hidden) canvas.focus();
  else $<HTMLInputElement>("username").focus();
}

/** charLink is a name that opens its character's page when pressed. */
function charLink(name: string): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = "charlink";
  b.textContent = name;
  b.title = `${name}'s deaths and kills`;
  b.setAttribute("aria-haspopup", "dialog");
  b.addEventListener("click", () => openCharacter(name));
  return b;
}

function openCharacter(name: string) {
  // The address says whose page this is while it is open (charlinks.ts),
  // so it can be copied and sent: whoever opens it sees the same page.
  charShown = name;
  history.replaceState(history.state, "", location.pathname + location.search + characterHash(name));
  fetch(`character?name=${encodeURIComponent(name)}`)
    .then((r) => (r.ok ? r.json() : r.status === 404 ? "gone" : null))
    .then((page: Character | "gone" | null) => {
      if (page && page !== "gone") fillCharacter(page);
      else fillCharacterNote(name, page === "gone" ? `${name} is not on the highscores any more.` : `${name}'s page is out of reach just now.`);
    })
    .catch(() => fillCharacterNote(name, `${name}'s page is out of reach just now.`));
}

/** fillCharacterNote opens the page with only a line saying why there is nothing on it. */
function fillCharacterNote(name: string, text: string) {
  $("chartitle").textContent = name;
  const p = document.createElement("p");
  p.className = "none";
  p.textContent = text;
  $("charbody").replaceChildren(p);
  showCharacter(true);
}

function fillCharacter(c: Character) {
  $("chartitle").textContent = c.name;
  const body = $("charbody");
  body.replaceChildren();
  const facts = document.createElement("dl");
  facts.className = "facts";
  const fact = (k: string, v: string) => {
    const dt = document.createElement("dt");
    dt.textContent = k;
    const dd = document.createElement("dd");
    dd.textContent = v;
    facts.append(dt, dd);
  };
  fact("Level", num(c.level));
  if (c.race) fact("Race", c.race);
  fact("Deaths", num(c.deaths));
  fact("Player kills", num(c.kills));
  fact("Time online", c.online);
  if (c.seen) fact("Last online", c.seen);
  body.appendChild(facts);

  const section = (title: string, lines: CharLine[], none: string, unlisted?: string) => {
    const h = document.createElement("h3");
    h.textContent = title;
    body.appendChild(h);
    const ul = document.createElement("ul");
    for (const l of lines) {
      const li = document.createElement("li");
      const who = l.link ? charLink(l.who) : document.createElement("span");
      if (!l.link) who.textContent = l.who;
      who.classList.add("who");
      li.appendChild(who);
      if (l.note) li.append(` ${l.note}`);
      const sub = document.createElement("span");
      sub.className = "sub";
      sub.textContent = `${l.where}, ${l.when}`;
      li.appendChild(sub);
      ul.appendChild(li);
    }
    if (lines.length) body.appendChild(ul);
    const tail = !lines.length && !unlisted ? none : unlisted;
    if (tail) {
      const p = document.createElement("p");
      p.className = "none";
      p.textContent = tail;
      body.appendChild(p);
    }
  };
  section("Deaths", c.deathlog ?? [], "Never died.", c.deathsunlisted);
  section("Player kills", c.killlog ?? [], "Has killed nobody.", c.killsunlisted);

  const h = document.createElement("h3");
  h.textContent = "Monsters killed";
  body.appendChild(h);
  const hunted = c.hunted ?? [];
  if (hunted.length) {
    const table = document.createElement("table");
    for (const r of hunted) {
      const tr = document.createElement("tr");
      const n = document.createElement("td");
      n.textContent = r.name;
      const v = document.createElement("td");
      v.className = "val";
      v.textContent = num(r.value);
      tr.append(n, v);
      table.appendChild(tr);
    }
    body.appendChild(table);
  } else {
    const p = document.createElement("p");
    p.className = "none";
    p.textContent = "None yet.";
    body.appendChild(p);
  }
  body.scrollTop = 0;
  $("charbox").scrollTop = 0;
  showCharacter(true);
}

$("charclose").addEventListener("click", () => showCharacter(false));
$("charcopy").addEventListener("click", () => {
  const b = $<HTMLButtonElement>("charcopy");
  const url = location.origin + location.pathname + characterHash(charShown);
  void copyText(url).then((ok) => {
    b.textContent = ok ? "Link copied" : "Could not copy";
    setTimeout(() => (b.textContent = "Copy link"), 1500);
  });
});
// An address ending #character=<name> opens that page as the page comes
// up, over the login screen or the game, and so does one typed or pasted
// over the address of a page already open.
{
  const fromHash = () => {
    const name = characterInHash(location.hash);
    if (name !== null) openCharacter(name);
  };
  fromHash();
  window.addEventListener("hashchange", fromHash);
}
charPage.addEventListener("mousedown", (ev) => {
  if (ev.target === charPage) showCharacter(false);
});

// --- changes ------------------------------------------------------------------

const changes = $("changes");

function showChanges(open: boolean) {
  changes.hidden = !open;
  if (open) {
    held.clear();
    $("changesclose").focus();
  } else canvas.focus();
}

$("changesbtn").addEventListener("click", () => {
  if (game.ready) net.send({ t: "changes" });
});
$("changesclose").addEventListener("click", () => showChanges(false));
changes.addEventListener("mousedown", (ev) => {
  if (ev.target === changes) showChanges(false);
});

/**
 * The trade window and the ask that comes before it. Both sit over the
 * view alone, the way the counter does and for a stronger reason: the bag
 * beside the view is how things are put on the table, so it has to stay
 * in reach the whole time the window is up. Neither is modal and neither
 * swallows the keyboard — walking away is one of the ways of saying no,
 * and the server ends the trade when you do.
 *
 * The ask is a small bar rather than a window, since the only question it
 * asks is yes or no. Ignoring it sends nothing: the server never hears
 * from you, and the asker is left to walk on.
 *
 * Everything shown is the server's last word; the panel keeps only which
 * message came before it, so the window can say when an accept that was
 * given is gone. The bag's own part — a click offers, the outline marks
 * what is on the table — is in ui.ts, with the columns.
 */
const trade = $("trade");
const tradeAsk = $("tradeask");
const death = $("death");
const respawnBtn = $<HTMLButtonElement>("respawnbtn");

/**
 * The death screen, which is shown for exactly as long as the server says
 * the character is dead and is taken away the moment it says otherwise.
 *
 * It is driven off `you.dead` rather than latched on a death and cleared
 * on a click, because the server is the one that knows: a body can be put
 * back on its feet by something other than this button — logging out while
 * dead saves a character the temple has already taken — and a screen
 * latched here would still be up over a character who is standing.
 *
 * The button takes focus when it appears, so Enter or Space answers it,
 * and the canvas takes focus back afterwards so the arrow keys walk again
 * without anybody having to click the world first.
 */
function showDeath(open: boolean) {
  if (death.hidden !== open) return; // already in the state asked for
  death.hidden = !open;
  if (open) {
    held.clear(); // nothing is being walked any more
    respawnBtn.focus();
  } else {
    canvas.focus();
  }
}

/**
 * The race chooser (2026-09-29: "implement elf, man, dwarf and hobbit as
 * player choices. if a player hasn't chosen a race at login, make them
 * choose").
 *
 * Like the death screen it is driven off the state rather than latched:
 * it is up for exactly as long as `you.race` is absent, which is a new
 * character, one saved before there were races, and one an admin has
 * taken the race off. It has no Close, the backdrop does nothing, and the
 * keyboard is swallowed while it is up (see the keydown handler), so the
 * only way on is one of the four buttons. Held keys and a finger's drag
 * are let go of as it opens, so nobody walks off behind it.
 *
 * A button is asked, not done: it goes out as a "race" message, the four
 * are greyed so a second press cannot send a second choice, and the
 * dialog goes when the state comes back with the race in it. A refusal
 * is written inside the dialog, since the console is behind it, and
 * lets the buttons be pressed again.
 */
const raceDlg = $<HTMLDivElement>("racedlg");
const raceBtns = Array.from(raceDlg.querySelectorAll<HTMLButtonElement>("button[data-race]"));
const raceErr = $("raceerr");

/**
 * A picture of each race on its button (2026-09-29: "Include a sample
 * picture with each race when you choose"): your own character, in your
 * own colour, drawn as that race facing you, by the sprite code that draws
 * everybody in the world. It is cropped to the figure and shown at twice
 * the size, square pixels and all.
 */
function paintRacePictures() {
  const me = game.me();
  for (const b of raceBtns) {
    const race = b.dataset.race;
    if (!race) continue;
    let pic = b.querySelector<HTMLCanvasElement>("canvas.racepic");
    if (!pic) {
      pic = document.createElement("canvas");
      pic.className = "racepic";
      pic.setAttribute("aria-hidden", "true");
      b.prepend(pic);
    }
    const spr = creatureSprite(me?.outfit ?? 1, 2, 0, NO_GEAR, me?.color, me?.level ?? 1, false, false, race, me?.hair);
    const top = Math.max(0, inkTop(spr) - 1);
    const w = 34;
    pic.width = w;
    pic.height = spr.height - top;
    const ctx = keptContext(pic);
    if (!ctx) continue;
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, pic.width, pic.height);
    ctx.drawImage(spr, (spr.width - w) / 2, top, w, pic.height, 0, 0, w, pic.height);
  }
}

/**
 * Whether the server is holding this character at the door until a race is
 * chosen: welcomed, but sent a chooserace rather than a state, and in no
 * world yet to be ready in. The chooser is all there is until the choice
 * brings the first state.
 */
let atDoor = false;

function raceAsked(asked: boolean) {
  for (const b of raceBtns) b.disabled = asked;
}

function showRaceChooser(open: boolean) {
  if (raceDlg.hidden !== open) return; // already in the state asked for
  raceDlg.hidden = !open;
  raceAsked(false);
  raceErr.textContent = "";
  if (open) {
    paintRacePictures();
    held.clear(); // don't keep walking while the dialog is up
    stickDir = null;
    // The race they are now, marked and focused, so keeping it is one
    // press (or Enter), for somebody back after /race; none marked for a
    // character yet to choose.
    const now = game.you?.race ?? "";
    for (const b of raceBtns) b.classList.toggle("current", b.dataset.race === now);
    const racenow = $("racenow");
    racenow.hidden = !now;
    racenow.textContent = now ? `You are ${/^[aeiou]/.test(now) ? "an" : "a"} ${now}. Choose again, or press ${now} to stay one.` : "";
    (raceBtns.find((b) => b.dataset.race === now) ?? raceBtns[0])?.focus();
  } else if (login.hidden) canvas.focus();
}

for (const b of raceBtns) {
  b.addEventListener("click", () => {
    const race = b.dataset.race;
    if (!race || (!game.ready && !atDoor)) return;
    raceErr.textContent = "";
    raceAsked(true);
    net.send({ t: "race", text: race });
  });
}

respawnBtn.addEventListener("click", () => {
  // Asked for, not done: the temple takes you when the server says so, and
  // the screen goes when the state comes back saying you are standing.
  net.send({ t: "respawn" });
});

/**
 * The trade window, and with it the bag.
 *
 * The bag opens with the window and closes with it, unless it was already
 * open, in which case it is left alone and left open. That is not a
 * flourish: the window's own note says "click a square in your bag to put
 * one of it on the table", and back when the bag was a panel down the
 * side it was simply there to be clicked. Now that it is a box behind an
 * icon (asked for on 2026-09-22: "remove the inventory panel and make it
 * a bag icon in the bottom right corner. clicking it pops up the
 * inventory") a trade that did not open it would turn a sentence of
 * instruction into a puzzle, and the one thing a trade must never be is a
 * puzzle — the other person is standing there waiting.
 *
 * Closing it again at the end is the other half of the same argument: it
 * was opened by the trade rather than by the player, so it goes when the
 * reason for it goes. Somebody who had it open before the ask arrived
 * keeps it, since that one they opened themselves.
 *
 * While it is up the bag cannot be put away (2026-10-27: "when in the
 * trade screen, the bag panel should always be shown"), and the shop's
 * window holds it the same way, since its goods are bought there and sold
 * from the bag: see holdBag.
 *
 * It acts on the change and not on the call. showTrade(false) is said
 * again on the way out of the world, and on a trade that was never open,
 * and a bag put away by one of those would be a bag that closed itself
 * for no reason anybody could see.
 */
function showTrade(open: boolean) {
  const was = !trade.hidden;
  trade.hidden = !open;
  if (open === was) return;
  if (open) held.clear();
  else canvas.focus();
  holdBag();
}

/**
 * Whether the bag was open of the player's own accord when a trade or the
 * shop began holding it open, or null while nothing holds it.
 */
let bagBeforeHeld: boolean | null = null;
/** The trade window or the shop is up, so the bag stays shown. */
const bagHeld = () => !trade.hidden || !vendorDlg.hidden;

/**
 * Open the bag while a trade or the shop is up, on a phone as on a wide
 * screen, and put it back as it was once neither is: shut again if the
 * window opened it, left open if the player had. showPanel will not shut
 * it in between.
 */
function holdBag() {
  const bag = $("invpanel");
  if (bagHeld()) {
    bagBeforeHeld ??= bag.classList.contains("open");
    if (!bag.classList.contains("open")) showPanel(bag, true, false);
  } else if (bagBeforeHeld !== null) {
    const was = bagBeforeHeld;
    bagBeforeHeld = null;
    if (!was) showPanel(bag, false, false);
  }
}

function showTradeAsk(open: boolean) {
  tradeAsk.hidden = !open;
  if (!open) canvas.focus();
}

function onTrade(msg: Trade) {
  const prev = game.trade;
  game.onTrade(msg);
  switch (msg.state) {
    case "asked":
      $("tradeasktext").textContent = `${msg.with} wants to trade.`;
      showTradeAsk(true);
      break;
    case "open":
      showTradeAsk(false);
      ui.onTrade(msg, prev);
      showTrade(true);
      break;
    case "closed":
      showTradeAsk(false);
      showTrade(false);
      ui.onTrade(msg, prev);
      // The reason is the world speaking, so it goes in the log as the
      // world's own kind of line: done, cancelled, walked away.
      if (msg.reason) ui.addLine(msg.reason, "server");
      break;
  }
}

$("tradeaskyes").addEventListener("click", () => {
  const t = game.trade;
  if (t?.state === "asked") net.send({ t: "trade", id: t.withid });
  showTradeAsk(false);
});
$("tradeaskno").addEventListener("click", () => {
  // Nothing is sent: an ask nobody answers is an ask that lapses.
  game.trade = null;
  showTradeAsk(false);
});
$("tradeaccept").addEventListener("click", () => {
  if (game.trading) net.send({ t: "tradeaccept" });
});
$("tradecancel").addEventListener("click", () => {
  if (game.trading) net.send({ t: "tradecancel" });
});

function fillChanges(msg: Changes) {
  const body = $("changesbody");
  body.replaceChildren();
  if (msg.entries.length === 0) {
    const none = document.createElement("div");
    none.className = "none";
    none.textContent = "Nothing has changed yet.";
    body.appendChild(none);
  }
  // Oldest at the top, newest at the bottom, against the order git log
  // and the wire send them.
  //
  // The window reads the way the log reads: you come down the column and
  // arrive at what just happened, with the Close button right under it
  // instead of a scroll's worth of history away. It also puts the marked
  // entries — the ones that landed while you were gone, which are by
  // definition the newest — at the end, which is where the scroll below
  // drops you.
  //
  // Each entry is a commit's title, and they are grouped under the day
  // they landed (the server has already said which day, in Sydney), so a
  // run of entries with the same day is one heading and a list under it.
  let day: HTMLUListElement | null = null;
  let dayName = "";
  for (const e of [...msg.entries].reverse()) {
    if (!day || e.when !== dayName) {
      const div = document.createElement("div");
      div.className = "day";
      const when = document.createElement("h3");
      when.className = "when";
      when.textContent = e.when;
      day = document.createElement("ul");
      div.append(when, day);
      body.appendChild(div);
      dayName = e.when;
    }
    const li = document.createElement("li");
    if (e.new) li.className = "new";
    // The time it landed in front of the title (2026-10-20: "include a
    // timestamp with the changes listed"), Sydney's like the day above it.
    if (e.time) {
      const at = document.createElement("span");
      at.className = "at";
      at.textContent = e.time;
      li.append(at);
    }
    li.append(e.title);
    day.appendChild(li);
  }
  // Reading them is what clears the mark on the button.
  markChanges(0);
  showChanges(true);
  // And opened at the bottom, where the newest are. After showChanges and
  // not before: the box is hidden until then, a hidden element has no box
  // to measure, and scrollHeight on one reads zero — so a scroll written
  // above this line would scroll nothing and look like it had worked.
  //
  // scrollTop rather than scrollIntoView, so the window opens at the end
  // of its own scroll and nothing else on the page moves.
  const box = $("changesbox");
  box.scrollTop = box.scrollHeight;
}

/** Put the waiting count on the button, or take it off. */
function markChanges(n: number) {
  const btn = $<HTMLButtonElement>("changesbtn");
  // The word only, beside the drawing: see the ⋯ menu's glyphs.
  btn.querySelector(".word")!.textContent = n > 0 ? `Changes (${n})` : "Changes";
  btn.classList.toggle("unread", n > 0);
  // The button is in the ⋯ menu since 2026-09-30, out of sight until it is
  // opened, so the ⋯ says there is something waiting. By id: this can run
  // before the menu's own consts are reached.
  $("morebtn").classList.toggle("unread", n > 0);
}

// --- logout -------------------------------------------------------------------

// The server decides whether a logout is allowed; when it is, it acknowledges
// with "bye" and closes the socket. A refusal arrives as an ordinary error line
// instead, so the flag is only ever set by the acknowledgement — that way the
// disconnect that follows reads as leaving rather than as something breaking.
let loggedOut = false;
/** Why the server put us out, when it bothered to say. See net.onclose. */
let byeReason = "";

$("logoutbtn").addEventListener("click", () => {
  if (game.ready) net.send({ t: "logout" });
});

// --- ping ---------------------------------------------------------------------

// What the panel shows is the round trip through the world's own loop: the
// ping goes into the same queue as a step and is answered by the same loop
// that moves you, so the number is the wait a player actually feels rather
// than how long a packet takes to cross the room.
//
// Every two seconds is often enough to notice a connection going bad and
// rare enough to cost nothing. A ping that has gone unanswered for longer
// than the stale window is never coming back: it is dropped, and the panel
// goes back to a dash rather than standing on a number from a minute ago.
const PING_EVERY = 2000;
const PING_STALE = 6000;
let pingSeq = 0;
let lastPong = 0;
const pingSent = new Map<number, number>();

/**
 * When the first ping went out that nothing has been heard since, or 0 when
 * the connection has said something since the last one we sent.
 *
 * This is how a dead socket is told from a live one. The server answers
 * every ping, through the same loop that moves the world, so a connection
 * that has gone PING_STALE without a word after being asked — not a pong,
 * not a state, not anything — is not slow. It is gone, however OPEN the
 * browser says it is, and it is given up on the way a close is: the
 * reconnect starts, and the dash on the ping panel and the banner saying
 * so arrive together. Before this, a socket that died without its close
 * reaching the browser left the world frozen for as long as TCP took to
 * notice, which can be a quarter of an hour, and nothing on the screen but
 * the dash said anything was wrong (asked for on 2026-09-22: "When I leave
 * the tab idle for a period, when I come back the game has frozen, I think
 * the network or websocket is dead. Ping reads -").
 *
 * It is measured from a ping and not from the last thing heard, because a
 * quiet world is not a dead one: a player standing alone in a field may be
 * sent nothing between one pong and the next ping, and in a tab behind
 * another one the browser may run this timer only once a minute.
 */
let awaitingSince = 0;
/** When ping last ran, to tell the connection going quiet from our own clock stopping. */
let lastTick = 0;

function ping() {
  const now = performance.now();
  const gap = lastTick ? now - lastTick : 0;
  lastTick = now;
  for (const [seq, at] of pingSent) if (now - at > PING_STALE) pingSent.delete(seq);
  // Closed under us and never said so: whatever swallowed the close, this
  // is the close, and it starts the reconnect like one.
  if (net.lost) {
    awaitingSince = 0;
    net.drop("The connection closed.");
    return;
  }
  if (!net.connected) {
    pingSent.clear();
    awaitingSince = 0;
    ui.setPing(null);
    return;
  }
  // Heard from since we started waiting: it was answering all along.
  if (awaitingSince && net.heard > awaitingSince) awaitingSince = 0;
  // Our own clock stopped. A tab put in the background, or a machine that
  // slept, runs this late or not at all, and a ping that went out just
  // before may have its answer sitting in a queue behind this very call.
  // The silence is ours and not the server's, so nothing is judged on it:
  // the wait starts again from the ping about to go out.
  if (gap > PING_EVERY * 3) awaitingSince = 0;
  if (awaitingSince && now - awaitingSince > PING_STALE) {
    awaitingSince = 0;
    net.drop("The server stopped answering.");
    return;
  }
  if (lastPong && now - lastPong > PING_STALE) ui.setPing(null);
  pingSent.set(++pingSeq, now);
  net.send({ t: "ping", seq: pingSeq });
  if (!awaitingSince) awaitingSince = now;
}
setInterval(ping, PING_EVERY);

// Coming back to the tab is when a dead connection matters, and the timer
// may not have run for a minute: ask now, so the wait for an answer starts
// the moment somebody is looking rather than whenever the next tick falls.
// The network coming back is the same moment for the same reason, and so
// is a page thawed after the browser froze it, or brought back out of the
// back-forward cache.
document.addEventListener("visibilitychange", () => {
  if (!document.hidden) ping();
});
document.addEventListener("resume", () => ping());
addEventListener("pageshow", (ev) => {
  if (ev.persisted) ping();
});
addEventListener("online", () => ping());

// --- losing the connection ----------------------------------------------------

/**
 * A connection that drops out from under somebody who is playing is tried
 * again for three seconds, and then they go back to the login screen.
 * Asked for on 2026-09-22: "if the connection drops, try to reconnect to
 * the same session within 3s, then take the player back to the login
 * screen".
 *
 * What this is, said plainly, because "reconnect to the same session" is
 * more flattering than the truth: it is a silent re-login. Nothing is held
 * open for us on the other end. The server takes a character out of the
 * world the instant the socket closes — dropClient in world.go saves them
 * and calls leaveWorld — so there is no session left to rejoin by the time
 * the client has noticed. What goes out on the new socket is the same
 * login message the Enter Game button sends, with the same name and the
 * same password, and what comes back is a new session on a character
 * loaded from the checkpoint that the drop itself wrote a moment earlier.
 *
 * That checkpoint is why this is worth doing at all: the character comes
 * back on the tile they were standing on, with their wounds, their mana
 * and their bag. What does not come back is everything the world ends when
 * somebody leaves it — a trade, a blood bond, a player fight's grudges, a
 * target — and everybody else sees them leave and enter again. All of that
 * was already true of any dropped connection; this does not make it worse,
 * it just makes it happen faster and without the password being retyped.
 *
 * The one case where it really is the same body is the one the world
 * already keeps: a skulled character stays standing when their connection
 * goes, and logging in again takes that body back up exactly as it stands,
 * wounds and skull and all (abandon and reclaim in world.go). This goes in
 * by the same door as the login button, so it gets that for nothing.
 *
 * A grace period on the server — holding the character in the world for a
 * few seconds against a return — was the other way to do this, and it was
 * rejected. It would leave bodies in the world with nobody at the reins,
 * which the world does today for exactly one reason and deliberately: a
 * skull, which is a punishment, and which is what makes a standing statue
 * fair to everyone who has to walk past it and can kill it. Doing the same
 * for every dropped connection would mean a body that can be attacked by
 * somebody whose player cannot see it coming, bought for one line of chat
 * and a walk of no tiles at all. It is also code in the world's loop — a
 * second kind of half-present player, with a clock on it — to save the
 * client a login message it already knows how to send.
 *
 * Three seconds is what was asked for and it fits what it catches: a wifi
 * handover, a phone waking up, a proxy dropping a socket it thought was
 * idle. It is not meant to ride out a server restart. That arrives as a
 * bye, which is not retried at all, because the server has said what
 * happened and the login screen is where that answer belongs.
 */
const RECONNECT_FOR = 3000; // ms of trying before the login screen
const RECONNECT_EVERY = 400; // ms between one attempt and the next

/**
 * What the reconnect logs back in with, held in memory and nowhere else:
 * not in sessionStorage, not in localStorage. That is the difference
 * between this and the reload after a deploy, which deliberately does not
 * carry the password across (see checkServer) — surviving a page load
 * would mean writing a credential down somewhere, and this survives
 * nothing. A reload, a closed tab, or giving up loses it.
 */
let session: { name: string; password: string } | null = null;
let reconnecting = false;
let retryTimer = 0;
let giveUpTimer = 0;
const reconnectNote = $("reconnecting");

/** Stop trying, leaving no timer running and no banner on screen. */
function stopReconnect() {
  reconnecting = false;
  clearTimeout(retryTimer);
  clearTimeout(giveUpTimer);
  retryTimer = 0;
  giveUpTimer = 0;
  reconnectNote.hidden = true;
}

/**
 * The connection went while somebody was playing. Say so on the screen —
 * a frozen world with no explanation is the thing this is for — and start
 * the clock that ends in the login screen.
 *
 * The three seconds are counted from here rather than from the last
 * attempt, so the promise made to the player is the one they were told:
 * three seconds, whatever happens inside them, and then the form.
 */
function beginReconnect(reason: string) {
  reconnecting = true;
  reconnectNote.hidden = false;
  ui.addLine("Connection lost: " + reason + " Reconnecting…", "error");
  giveUpTimer = setTimeout(() => {
    // Whatever is half-open is let go of rather than left to land. A
    // socket still opening at this instant would otherwise be greeted a
    // moment later and quietly draw a world behind the login screen the
    // player is already typing into.
    net.close();
    ui.addLine("Could not reconnect.", "error");
    loginErr.textContent = "The connection dropped and could not be recovered. " + reason;
    backToLogin();
  }, RECONNECT_FOR);
  void reconnectNow();
}

/**
 * What followed the last /password typed, kept in memory until the
 * server says whether it took (2026-10-07: "add a resetpassword command,
 * for admins it can be used with a name, /resetpassword <name>
 * <password>"). The chat box's history leaves the line out (see
 * rememberSaid) and it is never shown
 * in the log — the server answers commands to whoever typed them and
 * never speaks them — so this is the one place the new password is held,
 * and only so that the reconnect and the login form are not left holding
 * the old one. The server's "password" message never carries it back.
 */
let resetTyped = "";

function notePassword(text: string): void {
  const m = /^\/password(?:\s+([\s\S]*))?$/i.exec(text);
  if (m) resetTyped = (m[1] ?? "").trim();
}

/**
 * The server has changed this character's password. Whatever is kept to
 * log back in with — the reconnect's session, the form's box and what
 * backToLogin puts in it — becomes the new one, read off the line as the
 * server read it (see password.go). Changed by an admin, there is no
 * new one here to keep, and the old one is forgotten rather than offered
 * to a login it would fail.
 */
function passwordChanged(typed: string | undefined): void {
  let pw = "";
  if (typed === "rest") pw = resetTyped;
  else if (typed === "named") {
    const i = resetTyped.indexOf(" ");
    pw = i < 0 ? "" : resetTyped.slice(i + 1).trim();
  }
  resetTyped = "";
  if (session) {
    if (pw) session.password = pw;
    else session = null; // a reconnect with the old one could only be refused
  }
  if (lastLogin) lastLogin.password = pw;
  $<HTMLInputElement>("password").value = pw;
}

/** One attempt: a fresh socket, and then the login that got us here. */
async function reconnectNow() {
  const s = session;
  if (!reconnecting || !s) return;
  try {
    await net.connect();
  } catch {
    // Refused, or given up on from under us. Either way the window is
    // what decides when this stops, so it goes round again and the
    // give-up timer has the last word.
    if (reconnecting) retryTimer = setTimeout(() => void reconnectNow(), RECONNECT_EVERY);
    return;
  }
  if (!reconnecting) {
    net.close(); // the three seconds ran out while this one was opening
    return;
  }
  net.send({ t: "login", name: s.name, password: s.password, build: BUILD, proto: PROTOCOL_VERSION });
}

/**
 * Back in front of the login form with nothing of the last session left
 * running. Every way out of a session comes through here — a logout, a
 * goodbye from a server that is stopping, a drop that could not be
 * recovered — because the list of things to put away is the same list
 * every time, and a second copy of it is a second copy to forget something
 * from.
 *
 * The reconnect is stopped first and unconditionally, so that a session
 * ending for any other reason cannot leave a timer behind that would open
 * a socket under the login screen a moment later.
 */
function backToLogin() {
  loginPending = false;
  stopReconnect();
  // The form is put back as it was logged in with, whatever happened to
  // it in between, so Enter logs straight back in (2026-09-23). See
  // lastLogin.
  if (lastLogin) {
    $<HTMLInputElement>("username").value = lastLogin.name;
    $<HTMLInputElement>("password").value = lastLogin.password;
  }
  session = null;
  byeReason = "";
  login.hidden = false;
  // Back in front of the form, and the first thing anybody wants to know
  // is whether there is anything to log back in to. Checking starts again
  // at once rather than waiting out an interval, because a server that
  // just shut down is exactly the case this line exists for.
  watchServer(true);
  showDeath(false); // whatever state the session ended in, it is over
  showRaceChooser(false); // and the next login's state says whether it is needed
  atDoor = false;
  game.you = null;
  // No music on the login screen (2026-10-28: "don't play the music on the
  // login screen"): the piece of the place you left stops, and the next
  // one starts with the next session's first word of where you stand.
  sounds.ambient(undefined);
  // A trade does not survive the connection: the server has already told
  // the other side, and a window left over from a world you are no longer
  // in would be waiting for a message that cannot come.
  game.trade = null;
  showTrade(false);
  showTradeAsk(false);
  // Nor is anybody on a list of a world you are no longer in. The next
  // session's arrives with its first tick.
  players.clear();
}

// --- network ------------------------------------------------------------------

net.onmessage = (msg) => {
  const now = performance.now();
  switch (msg.t) {
    case "welcome":
      game.onWelcome(msg);
      loggedOut = false;
      // The build check at the door makes this all but impossible, since a
      // bundle and its server are built together; a page served by one
      // server and talking to another is the one way, and it says so
      // rather than misreading what comes down.
      if (msg.proto !== undefined && msg.proto !== PROTOCOL_VERSION) {
        ui.addLine(`The server speaks protocol version ${msg.proto} and this page speaks ${PROTOCOL_VERSION}. Reload the page.`, "error");
      }
      // A welcome is the only proof a reconnect worked: the socket being
      // open proves nothing, since the login on it can still be refused.
      // The line is worth saying because the log above it says the
      // connection was lost, and a player told the first half and not the
      // second is left wondering what they are looking at.
      if (reconnecting) {
        stopReconnect();
        ui.addLine("Reconnected.", "server");
      }
      // This bundle is the one the server wants, so the next stale refusal
      // — after a deploy, in this same tab — gets its reload back.
      try {
        sessionStorage.removeItem(RELOADED);
      } catch {
        // Storage is off; the reload was never armed in the first place.
      }
      login.hidden = true;
      // The login went over the socket, which a browser's password manager
      // cannot see answered: a same-page navigation with the form gone is
      // how Chromium's guide says to tell it the login worked, so it offers
      // to save or update the password that did (2026-10-24, see the form
      // in index.html). The address does not change.
      try {
        history.replaceState(history.state, "", location.href);
      } catch {
        // Nothing to tell it with; the game goes on all the same.
      }
      loginPending = false;
      watchServer(false); // the socket is the better answer from here on
      markChanges(msg.newchanges ?? 0);
      canvas.focus();
      break;
    case "bye":
      loggedOut = true;
      // A bye with words on it is the server putting everybody out rather
      // than the player leaving, and the words are why. They are kept for
      // the close that follows a beat later, which is where the session
      // actually ends and where there is a login screen to write them on.
      byeReason = msg.text ?? "";
      break;
    case "pong": {
      const sent = pingSent.get(msg.seq);
      if (sent !== undefined) {
        pingSent.delete(msg.seq);
        lastPong = now;
        ui.setPing(Math.round(now - sent));
      }
      break;
    }
    case "steps":
      game.onSteps(msg, now);
      break;
    case "map":
      // The whole map afresh, by an admin's /fullmap or /nomap.
      game.onMapReset(msg);
      break;
    case "marks":
      // The places quests have sent you to, afresh (2026-10-08: "when npcs
      // give quests, add the locations they mention to the map").
      game.onMarks(msg);
      break;
    case "seen":
      // Ground seen for the first time, ahead of the state that shows it
      // (2026-09-28: "don't send players the entire map. make them have
      // to explore and record it as they go").
      game.onSeen(msg);
      break;
    case "chooserace":
      // Held out of the world until a race is chosen: the chooser, and
      // nothing behind it to walk in.
      atDoor = true;
      showRaceChooser(true);
      break;
    case "state":
      atDoor = false;
      game.onState(msg, now);
      ui.update(msg.you);
      paintBagLoad(msg.you.cn ?? 0, msg.you.maxcn ?? 0);
      showDeath(msg.you.dead);
      // Over the death screen too: a character with no race chooses one
      // before anything else, lying down or standing up.
      // chooserace was true at every login from 2026-09-30 ("Allow players
      // to choose their race each time at login"); since 2026-10-07 ("save
      // the race a player chooses") the server keeps the race and asks only
      // when there is none, or at the login after the player typed /race.
      showRaceChooser(!msg.you.race || !!msg.you.chooserace);
      // The server sends a market at any depot (2026-10-13: "Allow the
      // market whenever the depot is available"), and only there since
      // 2026-10-19, when Grubb began sending you to the depot for it.
      market.show(msg.you.market);
      // At a depot the market is in the depot screen, and has no icon of
      // its own (2026-10-17, the depot screen).
      $("marketpanel").hidden = !msg.you.market || !!msg.you.indepot;
      paintDepot(msg.you);
      paintVendor(msg.you);
      // A depot, vendor or market panel shown or hidden brings its icon with it.
      syncPanelBar();
      sounds.ambient(game.me()?.z, msg.you.music);
      break;
    case "text":
      game.onText(msg, now);
      ui.onText(msg);
      break;
    case "reached": {
      // Within reach of somebody clicked: their menu, now and not before.
      const c = msg.id === npcMenuFor ? game.creatures.get(msg.id) : undefined;
      npcMenuFor = 0;
      if (c) showNpcMenu(c);
      break;
    }
    case "effect":
      game.onEffect(msg, now);
      sounds.effect(msg, game.me());
      break;
    case "highscores":
      fillScores(msg);
      break;
    case "players":
      players.set(msg.rows ?? [], now);
      break;
    case "changes":
      fillChanges(msg);
      break;
    case "trade":
      onTrade(msg);
      break;
    case "password":
      passwordChanged(msg.typed);
      break;
    case "error":
      // A refusal while the race chooser is up is almost certainly an
      // answer to it, and the console is behind the dialog: say it where
      // it can be read, and let the buttons be pressed again.
      if (!raceDlg.hidden) {
        raceErr.textContent = msg.text;
        raceAsked(false);
      }
      loginErr.textContent = msg.text;
      loginPending = false;
      if (game.ready) ui.addLine(msg.text, "error");
      // A refusal for being the wrong build is one the page can act on by
      // itself: the bundle is served with an ETag and no-cache, so a reload
      // fetches the current one. Once only — if the new bundle is refused
      // too, something else is wrong and a reload loop would hide it.
      if (msg.text.startsWith(STALE_CLIENT)) reloadForNewBuild(msg.text);
      break;
  }
};

net.onclose = (reason) => {
  if (byeReason) {
    // The server said why it was stopping before it stopped. Without this
    // the only thing it could have said is that the connection ended,
    // which is indistinguishable from a dropped wifi connection and sends
    // people to look at their router over a server that will be back in
    // twenty seconds. It goes on the login screen as well as in the log,
    // since the log is about to be behind it.
    //
    // It is asked before anything else, and it is never retried: a server
    // that has said goodbye is one there is nothing to reconnect to, and
    // three seconds of trying would bury the reason it gave under a
    // failure of our own making.
    ui.addLine(byeReason, "event");
    loginErr.textContent = byeReason;
    backToLogin();
    return;
  }
  if (loggedOut) {
    // Asked for and granted. Nobody wants the client arguing them back
    // into a world they just left.
    ui.addLine("You have logged out.", "server");
    loginErr.textContent = "";
    backToLogin();
    return;
  }
  if (reconnecting) {
    // An attempt got as far as a socket and then lost it, or the server
    // would not have the login on it. What ends this is the window, not a
    // count of tries, so it simply goes round again and the give-up timer
    // has the last word.
    retryTimer = setTimeout(() => void reconnectNow(), RECONNECT_EVERY);
    return;
  }
  if (session && login.hidden) {
    // Playing a moment ago, and nobody asked for this: worth three
    // seconds before the world is taken away.
    beginReconnect(reason);
    return;
  }
  ui.addLine("Disconnected: " + reason, "error");
  if (!loginErr.textContent) loginErr.textContent = "Disconnected. " + reason;
  backToLogin();
};

/**
 * Whether a login has been sent and not yet answered. A second submit while
 * it is — Enter pressed again, or the button twice — is let go, rather than
 * reaching the server as a second login on a connection that is already in
 * the world (2026-10-01: "what is "19:12 unknown command login""). The
 * welcome, a refusal or the connection closing answers it.
 */
let loginPending = false;

$<HTMLFormElement>("loginform").addEventListener("submit", async (e) => {
  e.preventDefault();
  if (loginPending) return;
  loginErr.textContent = "";
  const name = nameAsSpelt($<HTMLInputElement>("username").value.trim());
  const password = $<HTMLInputElement>("password").value;
  if (!NAME_RE.test(name)) {
    loginErr.textContent = NAME_HINT;
    return;
  }
  try {
    if (!net.connected) await net.connect();
  } catch (err) {
    loginErr.textContent = (err as Error).message;
    return;
  }
  // Remembered here rather than on the welcome, because this is the login
  // a reconnect would repeat and these are the words it would repeat: the
  // welcome carries a character id and nothing that could be sent back. A
  // refusal leaves it set and that is harmless — nothing is retried while
  // the login screen is up, which is where a refusal leaves us.
  session = { name, password };
  lastLogin = { name, password };
  loginPending = true;
  net.send({ t: "login", name, password, build: BUILD, proto: PROTOCOL_VERSION });
});

restoreLoginAfterReload();

// --- input --------------------------------------------------------------------

const KEY_DIRS: Record<string, string> = {
  ArrowUp: "n", ArrowDown: "s", ArrowLeft: "w", ArrowRight: "e",
  w: "n", s: "s", a: "w", d: "e", W: "n", S: "s", A: "w", D: "e",
  q: "nw", e: "ne", z: "sw", c: "se", Q: "nw", E: "ne", Z: "sw", C: "se",
  Numpad8: "n", Numpad2: "s", Numpad4: "w", Numpad6: "e", Numpad7: "nw", Numpad9: "ne", Numpad1: "sw", Numpad3: "se",
};
/**
 * The way each key held down walks, by the key itself (KeyboardEvent.code,
 * where it sits on the keyboard) rather than by what it types. What it
 * types can change between the press and the let-go: Option on a Mac turns
 * A into å, an input method can report the release as "Process", and the
 * let-go of A held through Shift is an "A". Looked up by that, the release
 * found nothing to let go of and the character walked on by itself until
 * the page was reloaded (2026-10-27: "users are reporting that their
 * characters started walking to the left like htey were holding an arrow
 * key down but they weren't"). Keyed by the key, too, A and the left arrow
 * held together no longer stop when either one is let go.
 */
const held = new Map<string, string>();
// The way a finger dragging on the world after a double tap points, or two
// fingers dragged together, while they are down: see the touch handlers
// below. It walks exactly as a held key does.
let stickDir: string | null = null;

function currentDir(): string | null {
  if (stickDir) return stickDir;
  // Combine orthogonal keys into a diagonal, like most clients do.
  let dx = 0, dy = 0;
  for (const d of held.values()) {
    if (d.includes("n")) dy = -1;
    if (d.includes("s")) dy = 1;
    if (d.includes("w")) dx = -1;
    if (d.includes("e")) dx = 1;
  }
  if (!dx && !dy) return null;
  return (dy < 0 ? "n" : dy > 0 ? "s" : "") + (dx < 0 ? "w" : dx > 0 ? "e" : "");
}

/**
 * How often a held direction is sent again: often enough that the server's
 * 150 ms input buffer always holds a fresh press when a step ends (which,
 * since 2026-09-28, is the moment it ends rather than the tick after), and
 * no oftener.
 */
const MOVE_EVERY = 90;
/**
 * The least time between two moves sent, even for a new way taken. A new
 * key or a turn of the touch stick goes at once rather than waiting out
 * MOVE_EVERY, but a thumb resting on the line between two of the stick's
 * ways turns it with every touchmove, sixty or a hundred and twenty times a
 * second, and each of those was a message. Fifty milliseconds is less than
 * the 90 a held key waits anyway, and well inside the server's 150 ms input
 * buffer, so a turn still lands on the next step; and it holds the moves to
 * twenty a second however the keys or the stick go: half the limit every
 * connection shares (internal/server/limit.go, 2026-10-08).
 */
const MOVE_FLOOR = 50;
let lastMoveSent = -Infinity;
/** A new way was taken: the next move goes after MOVE_FLOOR, not MOVE_EVERY. */
let newWay = false;
function pumpMovement(now: number) {
  const d = currentDir();
  if (!d || !game.ready) return;
  if (now - lastMoveSent < (newWay ? MOVE_FLOOR : MOVE_EVERY)) return;
  lastMoveSent = now;
  newWay = false;
  // A key is a change of mind about any walk still being planned.
  planning = null;
  net.send({ t: "move", dir: d });
}

/**
 * What you have said, oldest first, for Shift+Up and Shift+Down to step
 * back through in the chat box, readline style (2026-10-09: "make
 * shift+up/down traverse through previously sent messages in the chat log,
 * readline style or whatever"). Shift, because the plain arrows already move
 * the cursor along the line. It is kept in localStorage, so it outlasts a
 * reload, up to CHAT_HISTORY_MAX lines; a line the same as the one before it
 * is kept once. A /password line is never kept: the new password in it
 * is held nowhere but resetTyped.
 */
const CHAT_HISTORY_KEY = "tinytibia.chatHistory";
const CHAT_HISTORY_MAX = 100;
const chatHistory: string[] = (() => {
  try {
    const saved = JSON.parse(localStorage.getItem(CHAT_HISTORY_KEY) ?? "[]") as unknown;
    return Array.isArray(saved) ? saved.filter((l): l is string => typeof l === "string").slice(-CHAT_HISTORY_MAX) : [];
  } catch {
    return [];
  }
})();
/** Where Shift+Up has got to; chatHistory.length is the line being typed. */
let chatHistoryAt = chatHistory.length;
/** The line being typed when Shift+Up first left it, for Shift+Down to bring back. */
let chatDraft = "";

function rememberSaid(text: string): void {
  chatHistoryAt = chatHistory.length;
  chatDraft = "";
  if (/^\/password\b/i.test(text) || chatHistory[chatHistory.length - 1] === text) return;
  chatHistory.push(text);
  if (chatHistory.length > CHAT_HISTORY_MAX) chatHistory.splice(0, chatHistory.length - CHAT_HISTORY_MAX);
  chatHistoryAt = chatHistory.length;
  try {
    localStorage.setItem(CHAT_HISTORY_KEY, JSON.stringify(chatHistory));
  } catch {
    // Storage full or refused: the history still lasts until the tab goes.
  }
}

/** Shift+Up (-1) or Shift+Down (+1) in the chat box. */
function stepChatHistory(by: -1 | 1): void {
  const to = chatHistoryAt + by;
  if (to < 0 || to > chatHistory.length) return;
  if (chatHistoryAt === chatHistory.length) chatDraft = chat.value;
  chatHistoryAt = to;
  chat.value = to === chatHistory.length ? chatDraft : chatHistory[to];
  chat.setSelectionRange(chat.value.length, chat.value.length);
}

// Leaving the box, by Enter, Esc or a click elsewhere, ends a walk back
// through the history: the next Shift+Up starts from the newest line again,
// and whatever is in the box then counts as the line being typed.
chat.addEventListener("blur", () => {
  chatHistoryAt = chatHistory.length;
  chatDraft = "";
});

window.addEventListener("keydown", (e) => {
  // The race chooser swallows the keyboard, the help's F1 and Escape
  // included: it is the one window with no way out but its buttons, which
  // Tab, Enter and Space still reach because nothing here stops them.
  if (!raceDlg.hidden) return;
  const inChat = document.activeElement === chat;
  if (!inChat && (e.key === "F1" || e.key === "?")) {
    e.preventDefault();
    showHelp(!!help.hidden); // hidden reads as boolean | "until-found"
    return;
  }
  if (!help.hidden) {
    if (e.key === "Escape") showHelp(false);
    return;
  }
  // Whichever window is open swallows the keyboard, and Escape shuts it.
  // These used to be one test with two closes in it, so Escape shut the
  // changelog only while the highscores happened to be open as well.
  // The trade window is left out on purpose: it is not modal, and a
  // keyboard swallowed while it is up could not walk you away, which is
  // one of the ways of saying no. Escape while trading is the ask's
  // ignore and nothing else; the window has its own Cancel.
  for (const [el, show] of [[sellDlg, showHowMany], [slotMenu, hideSlotMenu], [playerMenu, hidePlayerMenu], [filtersMenu, showFilters], ...panelHelps, [charPage, showCharacter], [scores, showScores], [changes, showChanges], [marketDlg, shutMarket], [depotDlg, shutDepot], [vendorDlg, shutVendor]] as const) {
    if (!el.hidden) {
      if (e.key === "Escape") show(false);
      return;
    }
  }
  if (!game.ready) return;
  if (e.key === "Escape" && moreOpen()) {
    showMore(false);
    return;
  }
  if (e.key === "Escape" && drawerShows()) {
    showDrawer(false);
    return;
  }
  if (e.key === "Escape" && !tradeAsk.hidden) {
    game.trade = null;
    showTradeAsk(false);
    return;
  }
  if (e.key === "Enter") {
    e.preventDefault();
    if (inChat) {
      const text = chat.value.trim();
      if (text) {
        net.send(yelling && !text.startsWith("/") ? { t: "say", text, on: true } : { t: "say", text });
        rememberSaid(text);
      }
      notePassword(text);
      chat.value = "";
      chat.blur();
      canvas.focus();
    } else chat.focus();
    return;
  }
  if (inChat) {
    if (e.shiftKey && !e.metaKey && !e.ctrlKey && !e.altKey && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
      // Not the browser's Shift+arrow, which would select to the line's end.
      e.preventDefault();
      stepChatHistory(e.key === "ArrowUp" ? -1 : 1);
      return;
    }
    if (e.key === "Escape") {
      chat.blur();
      canvas.focus();
    }
    return;
  }
  // A key held with Cmd, Ctrl or Alt is a shortcut, the browser's or the
  // system's, and never a step: C is south-east, and taking Cmd+C for one
  // swallowed the copy out of the message log (2026-10-01: "make it so i
  // can cmd+c/copy from the message log when i select it").
  if (e.metaKey || e.ctrlKey || e.altKey) return;
  const dirKey = KEY_DIRS[e.code.startsWith("Numpad") ? e.code : e.key];
  if (dirKey) {
    e.preventDefault();
    // Shift turns without stepping, Tibia's ctrl+arrow: face something to
    // look at it, or aim a bow, without giving ground.
    if (e.shiftKey) {
      if (!e.repeat) net.send({ t: "turn", dir: dirKey });
      return;
    }
    held.set(e.code || e.key, dirKey);
    if (!e.repeat) {
      newWay = true;
      pumpMovement(performance.now());
    }
    return;
  }
  if (e.key === "Escape") {
    stopEverything();
  } else if (e.key === "Tab") {
    e.preventDefault();
    if (e.shiftKey) cyclePlayer();
    else cycleTarget();
  }
});
window.addEventListener("keyup", (e) => {
  held.delete(e.code || e.key);
  // A Mac sends no keyup for a key let go while Cmd is down, so whatever
  // was held then may be up by now: stop, rather than walk on unasked.
  if (e.key === "Meta") held.clear();
});
// Anything that takes the keyboard away can keep the keyup from us: a
// switch of window or tab, or the page hidden or put away.
function letGoOfKeys() {
  held.clear();
  stickDir = null;
}
window.addEventListener("blur", letGoOfKeys);
window.addEventListener("pagehide", letGoOfKeys);
document.addEventListener("visibilitychange", () => {
  if (document.hidden) letGoOfKeys();
});

// Tab on a keyboard, or the side-panel button for touch screens, which have
// no Tab key and where tapping a monster is the only other way to pick one.
// With a single monster on screen it lands on the same one again, which is a
// second pick and starts the chase, the same as a second tap would.
$("targetbtn").addEventListener("click", () => {
  if (!game.ready) return;
  cycleTarget();
  canvas.focus();
});
// Shift+Tab, or its button (2026-10-16: "Add a next player button, similar
// to next target but it picks players to target"): the same round, kept to
// players, for picking somebody out from among the monsters.
$("playerbtn").addEventListener("click", () => {
  if (!game.ready) return;
  cyclePlayer();
  canvas.focus();
});
// Esc for touch screens: with targets chased and players followed
// automatically, letting go of one has to be as easy as picking it.
$("stopbtn").addEventListener("click", () => {
  if (!game.ready) return;
  stopEverything();
  canvas.focus();
});

/**
 * Stop, from the button or Esc: the target and the chase go, and so does
 * every way the character was moving (2026-10-07: "the stop button in the
 * controls should also stop movement"). The server drops a clicked walk and
 * a buffered step on the same "attack" with no one picked (see setTarget);
 * here a held key and the touch stick are let go, so nothing goes on
 * sending steps, and a walk still being planned is never sent.
 */
/**
 * A pick of somebody to fight or follow. With chase on it is chased at once
 * and any clicked walk is dropped for it (2026-10-13: "When setting a
 * manual target and chase is enabled, cancel any map click/existing route
 * in favour of chasing"), which the server does for a walk already under
 * way; a walk still being planned here is never sent, since it would
 * arrive after the pick and, as a click on the ground, let it go again.
 */
function pickTarget(id: number): void {
  if (game.you?.chaseon) planning = null;
  net.send({ t: "attack", id });
}

function stopEverything(): void {
  held.clear();
  stickDir = null;
  planning = null;
  net.send({ t: "attack", id: 0 });
}

// The stance (2026-10-08: "make our pvp stances white, yellow or red"): a
// press steps round white, yellow, red and back to white. The server has
// the state; the button only asks for the next one round from what the
// last state said.
$("pvpbtn").addEventListener("click", () => {
  if (!game.ready || !game.you) return;
  net.send({ t: "stance", text: nextStance(game.you.stance) });
  canvas.focus();
});

// The auto stance (2026-10-08: "add auto stances: none, aware, all"): a
// press steps round none, aware, all and back to none, asking for the next
// one round from what the last state said, as the PvP stance does.
$("autobtn").addEventListener("click", () => {
  if (!game.ready || !game.you) return;
  net.send({ t: "autostance", text: nextAutoStance(game.you.autostance) });
  canvas.focus();
});

$("chasebtn").addEventListener("click", () => {
  if (!game.ready || !game.you) return;
  net.send({ t: "chase", on: !game.you.chaseon });
  canvas.focus();
});

// Ride (2026-10-12: "Add mounts"): on the horse or off it. Shown only to
// somebody with a horse; the server says why when it will not. A drawing
// by the info button since 2026-10-13 ("Move the mount button out of
// controls as a button next to the drawer icon, icon only"), under it
// since later that day ("show the mount button below the info drawer
// button"), so its name
// is on the aria-label and on a hold, as the other marks over the world
// have theirs; ui.ts keeps the title and the label up to date.
const rideBtn = $<HTMLButtonElement>("ridebtn");
const ride = () => {
  if (!game.ready || !game.you) return;
  net.send({ t: "ride", on: !game.you.riding });
  canvas.focus();
};
rideBtn.replaceChildren(viewGlyph("ride"));
rideBtn.setAttribute("aria-label", "Ride");
rideBtn.addEventListener("click", ride);
longPress(rideBtn, () => {
  const name = rideBtn.getAttribute("aria-label")!;
  showSlotMenu({ title: name, look: rideBtn.title, actions: [{ label: name, go: ride }] });
});

$("dbgbtn").addEventListener("click", () => {
  if (!game.ready || !game.you) return;
  net.send({ t: "debug", on: !game.you.debug });
  canvas.focus();
});

function cycleTarget() {
  const me = game.me();
  if (!me || !game.you) return;
  // Monsters only (2026-10-26: "make it only target the next monster,
  // never players"); Next player, Shift+Tab, picks people. Until then it
  // took players your stance let you fight as well. Never one of the
  // world's people, who was picked here, and so followed, until
  // 2026-10-07 ("don't allow npcs to be followed").
  const monsters = [...game.creatures.values()]
    .filter((c) => !c.npc && !c.ally && c.kind !== "player")
    .sort((a, b) => dist(a, me) - dist(b, me) || a.id - b.id);
  if (!monsters.length) return;
  const idx = monsters.findIndex((c) => c.id === game.you!.target);
  const next = monsters[(idx + 1) % monsters.length];
  pickTarget(next.id);
}

/**
 * Next player: as cycleTarget, but players only. Those the server says are
 * fair game (hostile: your stance, their level, whether either of you can
 * see the other) come first, nearest first, and a pick of one is an
 * attack; then everybody else, nearest first, and a pick of one of them
 * follows them (2026-10-17: "The next player button should allow following
 * a player if they're not a valid attack target"). With nobody in sight it
 * says so rather than doing nothing.
 */
function cyclePlayer() {
  const me = game.me();
  if (!me || !game.you) return;
  const people = [...game.creatures.values()].filter((c) => c.kind === "player" && c.id !== game.myId && !c.npc);
  if (!people.length) {
    ui.addLine("Nothing to target: there are no other players in sight.", "server");
    return;
  }
  const players = people.sort((a, b) => Number(!!b.hostile) - Number(!!a.hostile) || dist(a, me) - dist(b, me) || a.id - b.id);
  const idx = players.findIndex((c) => c.id === game.you!.target);
  pickTarget(players[(idx + 1) % players.length].id);
}

function dist(a: { x: number; y: number }, b: { x: number; y: number }) {
  return Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y));
}

canvas.addEventListener("mousemove", (ev) => {
  if (!game.ready) return;
  const hit = renderer.tileFromEvent(ev);
  // Somebody up on a roof the pointer looks through is what it is on.
  game.hoverTile = game.upOnRoof(hit) ?? hit;
});
/**
 * A look at the tile picked, or at the roof over it when somebody or
 * something is up there (see Game.upOnRoof). The roof's level goes with
 * it, so the server looks up there and not at whoever is under it.
 */
function lookAt(hit: Picked) {
  const up = game.upOnRoof(hit);
  net.send(up ? { t: "look", x: up.x, y: up.y, z: up.z } : { t: "look", x: hit.x, y: hit.y });
}
/** Who a click is on: somebody up on the roof it looks through, else whoever is on the tile. */
function creaturePicked(hit: Picked): ClientCreature | undefined {
  const up = game.upOnRoof(hit);
  return (up && game.creatureAt(up.x, up.y, up.z)) || game.creatureAt(hit.x, hit.y);
}
canvas.addEventListener("mouseleave", () => (game.hoverTile = null));
// The right action on a tile: another player gets the menu of what can be
// done with them; something lying there is picked up, at any distance —
// the server walks you to the tile beside it first when it is out of
// reach, and takes it the moment you arrive, so the button is "get that"
// wherever that is; anything else is a look, and so is scenery that does
// nothing when picked up, a bed or an anvil (see idleSceneryAt). It hangs off contextmenu
// rather than mousedown so that a touch long-press — Android's native
// contextmenu, or the timer standing in for it on iOS — is the same
// action as a desktop right-click. The undercroft's door is worked this
// way and no other: the pickup it sends is what the server swings the
// door on, and the left button walks to it like any other ground (see
// mousedown).
// A right-click with Ctrl held, from the real right button. Ctrl and not
// Shift (2026-09-24: "make extra sure that shift+right click is not allowed
// on some browsers, and if so move it to ctrl+right click"): Firefox opens
// its own menu on a shift+right-click and sends the page no contextmenu at
// all, so nothing a page does can answer one. The button is asked because
// on a Mac a ctrl+click with the left button is itself a right-click.
const ctrlRight = (at: { ctrlKey?: boolean; button?: number }) => !!at.ctrlKey && at.button === 2;
const rightAction = (at: { clientX: number; clientY: number; ctrlKey?: boolean; button?: number }) => {
  if (!game.ready) return;
  const hit = renderer.tileFromEvent(at);
  const { x, y } = hit;
  const me = game.me();
  if (!me) return;
  const c = creaturePicked(hit);
  npcMenuFor = 0;
  // A person, and not yourself: the menu. A monster and an empty tile
  // keep the older answers, since neither can be traded with or followed
  // and a menu of one item is a click with a step added.
  if (c && c.kind === "player" && c.id !== game.myId) {
    showPlayerMenu(c);
    return;
  }
  // One of the world's people: what a left click does, the walk up and
  // the menu on arriving (2026-10-05: "Make clicking an NPC provide
  // options like starting quests, vendor, etc"; and 2026-10-08: "when an
  // npc is clicked, don't show the interaction menu until we arrive
  // within range"). See showNpcMenu.
  if (c?.npc) {
    clickNpc(c);
    return;
  }
  // The rest is Game.tileAction's table, the same one the left button
  // reads. Ctrl held, or a creature on the tile, is a look whatever lies
  // there.
  if (!ctrlRight(at) && !c && game.tileAction(x, y, "hold") === "pickup") {
    net.send({ t: "pickup", x, y });
    return;
  }
  lookAt(hit);
};
// The long press and the right-click are one listener since 2026-09-27
// ("make right click do whatever long press does"): see longPress. A
// finger's hold on the world waits for the finger to lift before it does
// anything, since 2026-10-12 ("Allow long pressing then dragging to move
// like a joystick"): a hold that is dragged on is the stick and never the
// look, pickup or menu, and that is not known until the finger either
// moves or lets go. So the hold is kept on the one-finger drag below, the
// ring comes up under the finger to say it was felt, and the release does
// what the hold always did. A right-click, which has a button and no
// finger, still acts at once, and so does a hold that comes during a pan
// or a double-tap walk, which are not a press to drag on.
longPress(canvas, (at) => {
  if (at.button === undefined && drag && !drag.moved && !drag.walk) {
    drag.held = at;
    showStick(drag.x0, drag.y0);
    return;
  }
  // A mouse's right-click while both buttons are, or were just, down is
  // part of a look and nothing of its own (see chord). One that comes with
  // the right button still held — Linux and the Mac send the contextmenu
  // on the press, Windows on the release — waits for the release, so a
  // left button joining it can still make it a look instead.
  if (at.button !== undefined) {
    if (chord || performance.now() - chordEnded < CHORD_ECHO_MS) return;
    if (rightHeld && at.button === 2) {
      heldRight = at;
      return;
    }
  }
  rightAction(at);
});
/**
 * The left button's action, which used to happen on the press and now
 * happens on the release (asked for on 2026-09-20: "Support panning the view").
 *
 * That move is the whole cost of dragging the view: the button cannot be
 * both a walk and a grab on the way down, because whether it was a grab
 * is not known until the pointer either moves or does not. So the press
 * arms it, a drag past the slop turns it into a pan and disarms it, and
 * a release that never moved does what the press always did. The delay
 * is a few milliseconds of a click nobody can feel; the alternative is
 * walking to wherever you started dragging from, every drag.
 *
 * It takes the position and the modifier from the press rather than the
 * release, so a click that drifts a pixel or two under the finger still
 * acts on the tile it was aimed at, and a shift held down and let go
 * between the two is the click it was pressed as.
 */
const leftAction = (at: { clientX: number; clientY: number; shiftKey: boolean }) => {
  if (!game.ready) return;
  const hit = renderer.tileFromEvent(at);
  const { x, y } = hit;
  const me = game.me();
  if (!me) return;
  const c = creaturePicked(hit);
  npcMenuFor = 0;
  if (at.shiftKey) {
    lookAt(hit);
    return;
  }
  if (c && c.id !== game.myId) {
    // One of the world's people walks you up to them and opens the menu
    // of what can be asked of them, quests first and Trade for a
    // shopkeeper (2026-10-05: "Make clicking an NPC provide options like
    // starting quests, vendor, etc"), once you are within reach of them
    // (2026-10-08: "when an npc is clicked, don't show the interaction
    // menu until we arrive within range"): at once from within three
    // tiles, and otherwise on arriving. Trade on it is what asks for the
    // Vendor panel now, as the click itself did. The walk is a walk,
    // planned once to within three tiles of them, and never the follow a
    // click on a player is (2026-10-07: "don't allow npcs to be
    // followed"): nobody is ever pointed at an NPC, so Stop has nothing to
    // let go of and the outline stays off them.
    if (c.npc) {
      clickNpc(c);
      return;
    }
    // A monster is attacked, a player is followed — or attacked like a
    // monster when they are hostile: your stance allows them (see Stance).
    // The server counts the clicks: a second one on the same monster starts
    // the chase and a third lets it go; a second on a followed player lets
    // go at once.
    pickTarget(c.id);
    return;
  }
  // The rest is Game.tileAction's table, the same one the right button and
  // the long press read: something beside you is taken, which is looting
  // with one click (2026-09-20: "Restore looting with a single left
  // click"), and out of reach a click walks there; a chest opens from any
  // distance, the server walking you up to it (2026-10-21: "clicking a
  // chest from a distance should open it when you arrive"); the
  // undercroft's door, a tile of only blood or a body, and scenery you
  // stand on, a stool or a high seat, are walked onto; and scenery that
  // blocks and does nothing, a bed or an anvil, is looked at from beside
  // it and walked up to from further off.
  switch (game.tileAction(x, y, "tap", dist(me, { x, y }) <= 1)) {
    case "pickup":
      net.send({ t: "pickup", x, y });
      return;
    case "look":
      lookAt(hit);
      return;
  }
  // Your own square with nothing worth taking on it is a walk of no
  // steps, and does nothing at all.
  planWalk(x, y);
};

/**
 * A click on the ground is a walk the page plans itself and sends as a
 * route. Asked for on 2026-09-28: "do "map walking" client side so the
 * server doesn't have to calculate it. raise the distance limit to maybe
 * 300 tiles, or on a timer that's short enough the interface doesn't lag".
 * It was {t: "walk", x, y}, and the server searched for the way, forty
 * tiles at most.
 *
 * The search is pathfind.ts, over the map this character has explored —
 * and, since 2026-09-28 ("allow map clicking into unexplored areas"), over
 * the black beyond it as if it were open ground, which the server finds
 * the real way through as the walk uncovers it — and it runs a slice a
 * frame, PLAN_SLICE_MS of it and the frame drawn after, so a click three
 * hundred tiles off never stops the page. The
 * character does not move until it is found; nearly every click is found
 * inside the first slice, before the frame it was clicked in is drawn.
 * One that runs past PLAN_MS is taken to have no way.
 *
 * What it steers round is what the server's own walk steers round and the
 * page can see: everybody on screen, the chest and the shut doors, and
 * with a skull the protection zones. A creature that steps into the way
 * later is the server's to go round (game/route.go).
 */
const PLAN_SLICE_MS = 4;
const PLAN_MS = 3000;
let planning: { planner: RoutePlanner; started: number } | null = null;

function planWalk(x: number, y: number) {
  const me = game.me();
  if (!me) return;
  planning = null;
  const z = me.z ?? 0;
  if (dist(me, { x, y }) > CLICK_RANGE) {
    ui.addLine(`That is too far to walk to in one go: click somewhere within ${CLICK_RANGE} tiles.`, "server");
    return;
  }
  const W = game.mapW;
  const taken = new Set<number>();
  const at = (tx: number, ty: number, tz: number) => ((tz + 16) * game.mapH + ty) * W + tx;
  // Nobody is in the way of an admin gone /invisible, and an admin gone
  // /invisible is in nobody's (2026-10-07: "allow them to coexist in the
  // location of another creature or player"): the server walks them
  // through, so the plan does too.
  if (!me.invisible) {
    for (const c of game.creatures.values()) {
      if (c.id !== game.myId && c.hp > 0 && !c.invisible) taken.add(at(c.x, c.y, c.z ?? 0));
    }
  }
  // The depot boxes the map has are in the way out of view too.
  for (const it of game.groundShown()) {
    if (game.itemDefs.get(it.id)?.blocking) taken.add(at(it.x, it.y, it.z ?? 0));
  }
  const locked = !!game.you?.locked;
  const planner = new RoutePlanner(game, {
    from: { x: me.x, y: me.y, z },
    to: { x, y },
    blocked: (tx, ty, tz) => taken.has(at(tx, ty, tz)),
    shut: (tx, ty, tz) => locked && game.protectedAt(tx, ty, tz),
    // Snow is grass to an elf, and the plan should know it (2026-09-29).
    race: game.you?.race,
  });
  planning = { planner, started: performance.now() };
  stepPlan();
}

function stepPlan() {
  const p = planning;
  if (!p) return;
  const now = performance.now();
  const state = p.planner.work(Infinity, now + PLAN_SLICE_MS, () => performance.now());
  if (state === "searching" && performance.now() - p.started < PLAN_MS) {
    requestAnimationFrame(stepPlan);
    return;
  }
  planning = null;
  if (state !== "found") {
    ui.addLine("There is no way.", "server");
    return;
  }
  const route = p.planner.route;
  if (route.length === 0) return; // already there
  if (route.length > MAX_ROUTE_STEPS) {
    ui.addLine(`That is too far to walk to in one go: click somewhere within ${CLICK_RANGE} tiles.`, "server");
    return;
  }
  net.send({ t: "route", steps: routeSteps(route) });
}

// A hand on a mouse is never perfectly still, and a click that panned the
// world a pixel and then refused to walk would read as a dead button. Past
// this much travel it was a drag and was never a click; under it, the
// other way about. Measured from where the press landed, not from the last
// event, so a slow crawl over the threshold still counts once.
const DRAG_SLOP = 6; // CSS pixels
let press: { at: { clientX: number; clientY: number; shiftKey: boolean }; lx: number; ly: number; dragged: boolean } | null = null;

/**
 * Both buttons pressed together on the world is a look, as in Tibia
 * (2026-10-13: "can we make simultaneous left+right click a look?"). It
 * happens when the second of the two goes down, whichever came first, on
 * the tile under it, and that press is a look and only a look: the left
 * button's walk, attack or pickup and the right button's pickup or menu
 * are let go, and nothing either button does counts until both are up
 * again. `chord` is that stretch.
 *
 * The right button acts on its release now, as it always did on Windows,
 * where the contextmenu comes on the release. Elsewhere it comes on the
 * press, and a right-click that had already picked something up could not
 * then become a look. `rightHeld` says the right button is down on the
 * world, and `heldRight` is the right-click waiting for it to come up.
 * Windows' contextmenu comes just after both are released, so a chord
 * swallows the right-clicks for CHORD_ECHO_MS after it ends as well.
 */
let chord = false;
let chordEnded = 0;
const CHORD_ECHO_MS = 300;
let rightHeld = false;
let heldRight: PressAt | null = null;

canvas.addEventListener("mousedown", (ev) => {
  if (!game.ready || ev.button !== 0) return;
  // Not while two fingers are on the glass: see the note on `pinch`. A
  // touch mints a mouse event of its own, and the first finger of a
  // pinch would walk you to wherever it landed.
  if (pinch) return;
  canvas.focus();
  press = { at: { clientX: ev.clientX, clientY: ev.clientY, shiftKey: ev.shiftKey }, lx: ev.clientX, ly: ev.clientY, dragged: false };
});
// After the left button's own listener, so that a left press making a
// chord takes back the press that listener has just armed.
canvas.addEventListener("mousedown", (ev) => {
  if (ev.button === 2) {
    rightHeld = true;
    heldRight = null;
  }
  // A chord whose release went unseen, off the window, is over by now.
  if ((ev.buttons & 3) !== 3) chord = false;
  if (!game.ready || pinch) return;
  if ((ev.button === 0 || ev.button === 2) && (ev.buttons & 3) === 3) {
    chord = true;
    press = null;
    heldRight = null;
    npcMenuFor = 0;
    lookAt(renderer.tileFromEvent(ev));
  }
});
// On the window rather than the canvas, both of them: a drag that leaves
// the canvas should go on panning while the button is held, and a button
// released over the chat log should end the drag rather than leaving it
// armed for the next time the pointer crosses the world.
addEventListener("mousemove", (ev) => {
  if (!press) return;
  const dx = ev.clientX - press.lx,
    dy = ev.clientY - press.ly;
  if (!press.dragged && Math.hypot(ev.clientX - press.at.clientX, ev.clientY - press.at.clientY) <= DRAG_SLOP) return;
  press.dragged = true;
  press.lx = ev.clientX;
  press.ly = ev.clientY;
  // Against the pointer: the ground is being dragged, so the hand and the
  // world go the same way and the camera goes the other. Grabbing a map
  // and pushing it left is the gesture every map in the world uses, and
  // the opposite sign is the one everybody notices immediately.
  renderer.dragPan(-dx, -dy);
});
addEventListener("mouseup", (ev) => {
  if (ev.button === 2) {
    rightHeld = false;
    const at = heldRight;
    heldRight = null;
    if (at && !chord) rightAction(at);
  }
  if (chord) {
    if ((ev.buttons & 3) === 0) {
      chord = false;
      chordEnded = performance.now();
    }
    return;
  }
  if (!press || ev.button !== 0) return;
  const p = press;
  press = null;
  if (!p.dragged) leftAction(p.at);
});

// How much of the world the window shows. The minimap panel is gone and
// this is what replaced it (asked for on 2026-09-20: "Remove the separate minimap
// panel, instead allow zooming the main window"), so the reading of the
// ground that used to happen in a 192-pixel square happens in the window
// itself, at a size where a road still reads as a road.
//
// The choice is the player's and is kept between sessions, since it is a
// way of looking at the world rather than a thing that happens to it.
// Nothing overrides it.
//
// Where the zoom centres is not a question here: the camera is on the
// player at every zoom and every frame, unless the view has been dragged
// off them, and a notch of zoom shows more or less of the world round
// wherever it is pointed. Nothing about the zoom moves the camera.
// The reset is the one view button left (asked for on 2026-09-20: "Remove the
// zoom in zoom out buttons"), and it is spent only when there is nothing
// to reset: the closest rung with the camera still on the player.
//
// Dragged away is the case that matters. The pan comes back on its own
// the moment you take a step, but a character standing still has no step
// to take, and somebody who dragged the view across the map and then
// wondered where they went needs a way back that is not "walk until the
// window catches up". The clamp keeps the view over the map; this is
// what puts it back on them.
//
// It is painted every frame because the pan changes under a finger and
// on a step, neither of which passes through here. The write is guarded
// by what was last written rather than by how it got that way, so the
// cost of a frame is a comparison.
// Where every change of zoom ends up, however it was asked for: the
// buttons, the wheel, the pinch and the reset. One place that paints the
// button and one that writes the choice down, so a path that quietly
// forgot either would have to skip this function to do it.
//
// What is written down is the tile size, not the rung's number. A number
// is a position in a list and the list is not fixed: adding 48 at the
// close end moved every rung by one, and every remembered 0 would have
// come back meaning 48 rather than 32 — for everybody who had ever
// touched the zoom, silently, on the next reload. Adding 64 above it
// would have done the same again. A size means the same thing whatever
// the ladder does, and a size the ladder no longer has lands on the
// nearest rung it does have.
//
// It is also where the zoom says what it is (2026-10-16: "When changing
// the zoom, show the tile size in pixels, and the dimensions of the view
// in tiles"): a small note in the middle of the world, the tile's size and
// how many tiles the window holds across and down, for ZOOM_TAG_MS after
// the last change and then fading like the area's name. A press at either
// end of the ladder puts it up too, which is how a wheel that has stopped
// doing anything says why. Scenery, like the area's name: it takes no
// clicks, and a zoom remembered from last session comes back without it.
const ZOOM_KEY = "tinytibia.zoompx";
const zoomTag = $("zoomtag");
const ZOOM_TAG_MS = 1500;
let zoomTagTimer = 0;
function zoomLanded() {
  const { w, h } = renderer.viewTiles();
  zoomTag.textContent = `${renderer.zoomPx()} px \u00b7 ${w}\u00d7${h} tiles`;
  zoomTag.classList.add("show");
  clearTimeout(zoomTagTimer);
  zoomTagTimer = window.setTimeout(() => zoomTag.classList.remove("show"), ZOOM_TAG_MS);
  try {
    localStorage.setItem(ZOOM_KEY, String(renderer.zoomPx()));
  } catch {
    /* storage unavailable: the choice lasts this session */
  }
}
function stepZoom(by: number) {
  renderer.zoomBy(by);
  zoomLanded();
}
/**
 * Look a floor up or down, from the strip in the top right of the world
 * (asked for on 2026-09-22: "move the up and down level map controls into
 * the main window top right like the fullscreen/windowed control").
 *
 * What they do is exactly what they did as two words in the Controls
 * panel, and deliberately so: each is one call to peekLevel, the window
 * draws that level, the character does not
 * move, and their next step brings the window back — see Game.viewZ,
 * which holds the look and clears it. Nothing about the behaviour changed
 * with the position, including the thing that is easiest to add while
 * moving a button and wrong to: neither greys out at the top or the
 * bottom of the range, because neither ever did. peekLevel clamps, so a
 * press at the roof is a press that leaves you at the roof, and a
 * disabled button would be the client deciding for itself which floors
 * exist — a question it answers off the level map it was sent, which is
 * the sparse one, and which will be wrong for any cave that has not been
 * walked into yet.
 *
 * They are drawings now rather than words, so both halves of what a word
 * used to do have to be said somewhere else: the title carries it for a
 * pointer, the aria-label for anything that reads aloud, and a hold
 * carries it for a finger. The titles are the ones the buttons have
 * always had and they are in the markup with the rest of the help; the
 * other two are here, since this is where the drawing goes on.
 *
 * The hold opens the same box a hold opens everywhere else on this page —
 * the one the bag squares, the spells and the full screen mark use — with
 * the name, the sentence, and a button that does the thing. It goes
 * through longPress in touch.ts rather than a timer of our own, because
 * that helper is what knows Android answers a hold with a contextmenu and
 * iOS answers it with nothing, and its swallow is what keeps the release
 * of the hold off the click listener. A hold must not change the floor:
 * that is a request for words answered by the ground moving under you.
 *
 * They keep working wherever the panels are, and that is a change worth
 * naming. They used to be two buttons in a panel that folds, that can be
 * dragged elsewhere in the column, and that on a phone is under the world
 * and reached by scrolling; fold the panel and the way to look down a
 * shaft folded with it. Here they are over the world, in a strip that
 * nothing folds, moves or scrolls. Since 2026-10-13 they are a press on
 * the floor chip away while you are on your own floor; see paintFloor.
 */
for (const [id, glyph, name, says] of [
  ["lookup", "levelup", "Look up", "Look at the floor above, from where you stand. You do not move, and any step of your own brings the window back to you"],
  ["lookdown", "leveldown", "Look down", "Look at the floor below, from where you stand. You do not move, and any step of your own brings the window back to you"],
] as const) {
  const btn = $<HTMLButtonElement>(id);
  const step = () => {
    game.peekLevel(id === "lookup" ? 1 : -1);
    // A look that lands back on your own floor keeps the marks out.
    floorOpen = true;
    paintFloor();
  };
  btn.replaceChildren(viewGlyph(glyph));
  btn.setAttribute("aria-label", name);
  btn.addEventListener("click", step);
  longPress(btn, () => showSlotMenu({ title: name, look: says, actions: [{ label: name, go: step }] }));
}

// The one mark that puts the view back, in the same strip. It was two
// marks side by side until 2026-09-29 ("Propose a way to reduce the map
// navigation button clutter", then "implement it"): Back to you, shown
// while the window is off you, and Reset view, shown while the zoom is
// anything but the everyday one. They take turns now, in that order.
// While the window is off you — dragged away, or looking at another floor
// — it is Back to you, which leaves the zoom alone, since a player who
// has zoomed out to see the country wants to come back to themselves at
// that size. Once the window is on you and only the zoom is off, it is
// Reset view. At home it is not there at all. Painted every frame, since
// the pan changes under a finger and on a step, neither of which passes
// through here; the write is guarded by what was last painted.
const recentreBtn = $<HTMLButtonElement>("recentre");
type BackMode = "recentre" | "zoomreset" | null;
let backMode: BackMode | undefined;
const BACK = {
  recentre: { name: "Back to you", says: "Your own floor, with the window centred on you. Any step of your own does the same" },
  zoomreset: {
    name: "Reset view",
    says: "Back to the everyday view, a tile at its own size. The mouse wheel over the world zooms, and a pinch on a touchscreen",
  },
};
function paintBack() {
  const mode: BackMode = game.offMe() ? "recentre" : !renderer.atDefaultZoom() ? "zoomreset" : null;
  if (mode === backMode) return;
  backMode = mode;
  recentreBtn.hidden = mode === null;
  if (mode === null) return;
  recentreBtn.replaceChildren(mode === "recentre" ? viewGlyph("recentre") : viewGlyph("zoomreset"));
  recentreBtn.title = `${BACK[mode].name}: ${BACK[mode].says}`;
  recentreBtn.setAttribute("aria-label", BACK[mode].name);
}
recentreBtn.addEventListener("click", () => {
  if (backMode === "recentre") {
    game.recentre();
    closeFloor();
  } else if (backMode === "zoomreset") {
    renderer.resetZoom();
    zoomLanded();
  }
  paintBack();
});
longPress(recentreBtn, () => {
  const mode = backMode;
  if (!mode) return;
  showSlotMenu({ title: BACK[mode].name, look: BACK[mode].says, actions: [{ label: BACK[mode].name, go: () => recentreBtn.click() }] });
});

/**
 * The floor chip's middle: the floor the window is showing, as a mark
 * short enough to sit between the two floor marks — G for the ground, +1
 * for a floor up, −2 for two down — and in the accent while it is a floor
 * you are only looking at, when a press on it brings you back. Its hover
 * is the floor and the area in words, which is what the line of text over
 * the world used to say all the time (2026-09-29: "Propose a way to
 * reduce ... the player location text in the screen").
 *
 * The area's name itself shows large over the world for AREA_TITLE_MS on
 * arriving somewhere new, or on a floor of your own that is new, and then
 * fades; a press on the chip's middle when you are on your own floor
 * shows it again.
 *
 * Look up and look down are only out while they are wanted (2026-10-13:
 * "Only show the level up down buttons when the current level isn't the
 * one the player is on. Otherwise just show the current level and expand
 * it out to protrude into the main view with up/down when clicked"). They
 * hang under the floor while the window is on another floor, since that
 * is when the next press is most likely another floor or the way back.
 * On your own floor a press on the floor brings them out, and another
 * puts them away. Once out they stay out across the looking, including a
 * look that lands back on your own floor, so a look up and back down
 * does not take them away between the two presses; what puts them away is
 * a step of your own, or a press that brings the window back to you,
 * since either is the look being over.
 */
const floorChip = $("floorchip");
const floorLabel = $<HTMLButtonElement>("floorlabel");
const areaTitle = $("areatitle");
const AREA_TITLE_MS = 3000;
let areaTitleTimer = 0;
let floorShown = "";
let whereWas = "";
let floorOpen = false;
let floorStood = "";
function floorMark(z: number): string {
  return z === 0 ? "G" : z > 0 ? `+${z}` : `\u2212${-z}`;
}
function showAreaTitle() {
  const me = game.me();
  const area = game.you?.area;
  if (!me || !area) return;
  areaTitle.querySelector("b")!.textContent = area;
  areaTitle.querySelector("i")!.textContent = levelName(me.z ?? 0);
  areaTitle.classList.add("show");
  clearTimeout(areaTitleTimer);
  areaTitleTimer = window.setTimeout(() => areaTitle.classList.remove("show"), AREA_TITLE_MS);
}
function paintFloor() {
  const me = game.me();
  if (!me) return;
  const own = me.z ?? 0;
  const z = game.viewZ ?? own;
  const area = game.you?.area ?? "";
  const peek = game.viewZ !== null;
  // A step of your own puts the marks away, as it ends a look.
  const stood = `${me.x}|${me.y}|${own}`;
  if (stood !== floorStood) {
    floorStood = stood;
    floorOpen = false;
  }
  const shown = `${z}|${peek}|${area}|${floorOpen}`;
  if (shown !== floorShown) {
    floorShown = shown;
    floorLabel.textContent = floorMark(z);
    floorChip.classList.toggle("peek", peek);
    floorChip.classList.toggle("open", floorOpen);
    floorLabel.setAttribute("aria-expanded", String(peek || floorOpen));
    const where = `${levelName(z)}${area && !peek ? ` \u00b7 ${area}` : ""}`;
    floorLabel.title = peek
      ? `${where}: you are only looking. Press here, or take a step, to come back to your own floor`
      : floorOpen
        ? `${where}. Press to put away looking up and down`
        : `${where}. Press to look up or down from here`;
    floorLabel.setAttribute("aria-label", peek ? `Looking at ${levelName(z)}: back to your own floor` : where);
  }
  // Somewhere new, or a floor of your own that is new: its name, large.
  const here = `${own}|${area}`;
  if (area && here !== whereWas) {
    whereWas = here;
    showAreaTitle();
  }
}
function closeFloor() {
  floorOpen = false;
  paintFloor();
}
floorLabel.addEventListener("click", () => {
  if (game.viewZ !== null) {
    game.recentre();
    closeFloor();
    return;
  }
  floorOpen = !floorOpen;
  if (floorOpen) showAreaTitle();
  paintFloor();
});

// The two gestures everybody already has in their hands: a wheel over the
// world, and a pinch on a touchscreen. Both end up in stepZoom, the same
// call the buttons make, so there is one answer to what the zoom is, one
// place that clamps it to the ends of the range, one place that writes it
// down, and the buttons grey themselves correctly however the zoom was
// reached. A gesture that reached into the renderer directly would be a
// second notion of zoom, and the two would drift the first time one of
// them learned something the other did not.
//
// A wheel's deltas are not one notch each: a mouse sends a lump of about
// a hundred at a time, a trackpad sends a stream of ones and twos, and
// stepping per event would fly a trackpad through the whole range in a
// flick. So the travel is added up, and a tally past the threshold buys
// one rung and is then cleared rather than having the threshold
// subtracted from it. Subtracting is the obvious thing and it is wrong
// here: a mouse notch is 120 against a threshold of 100, so every fifth
// notch would bank enough change to buy two rungs, and on a ladder this
// short a wheel that usually moves one and occasionally moves two reads
// as a bug. The tally is dropped whenever the direction reverses as well,
// so a scroll back the other way answers on the first push instead of
// having to undo what was banked.
// One notch, one rung — whatever the notch is worth (asked for on 2026-09-21:
// "make every 'notch' of mousewheel zoom in and out, it seems to take
// quite a few to get it to start zooming at the moment").
//
// It used to add the deltas up and buy a rung at a hundred, on the
// reasoning that a mouse notch is about 120. That reasoning has a hole in
// it: deltaY is not in pixels unless deltaMode says it is. A browser is
// free to report a notch as three *lines* (deltaMode 1) or as a page
// (deltaMode 2), and three against a threshold of a hundred is a wheel
// you have to spin thirty-four times before the world moves — which is
// exactly what it felt like. Normalising by deltaMode would fix that
// arithmetic, and the arithmetic is not worth having: what a wheel event
// means is "one notch", and the amount is the browser's opinion about how
// far a page should scroll, which is not a question being asked here.
//
// So the magnitude is thrown away and every event is a rung. What that
// costs is a trackpad, which does not send notches at all: it sends a
// stream of small deltas at the screen's refresh rate, and a stream of
// rungs would cross the whole ladder in a flick. The gap below is what
// keeps that usable — at most one rung per eighth of a second while the
// stream keeps coming, which a hand on a wheel never hits and a hand on a
// trackpad hits constantly. A change of direction answers at once, so a
// scroll back the other way is never waiting out a gap it did not earn.
const WHEEL_GAP_MS = 125;
let wheelAt = 0;
let wheelDir = 0;
canvas.addEventListener(
  "wheel",
  (ev) => {
    // Not passive, and it must not be: this line preventDefaults so that
    // a wheel over the world zooms it instead of scrolling the page out
    // from under the cursor. A passive listener cannot, and the browser
    // does not complain in any way a player would see — the page simply
    // scrolls and the zoom appears to do nothing.
    ev.preventDefault();
    if (!game.ready || ev.deltaY === 0) return;
    // Wheel down is away, which is out: more world, a higher rung.
    const dir = ev.deltaY > 0 ? 1 : -1;
    const now = performance.now();
    if (dir === wheelDir && now - wheelAt < WHEEL_GAP_MS) return;
    wheelDir = dir;
    wheelAt = now;
    stepZoom(dir);
  },
  { passive: false },
);

// Two fingers: dragged together they walk, and spread or drawn in they
// zoom (2026-10-05: "Two finger drag should step"; they panned before,
// from 2026-09-28's "Make two finger drag pan", and one finger pans now).
// The middle of the pair is a stick, as a finger after a double tap is:
// the character steps the way it points from where the pair landed, for as
// long as they are held there, exactly as a held arrow key walks, and
// bringing them back stops it. A gesture is a walk or a zoom and never
// both, settled by its first clear movement — see twoFingerMode — so a
// pinch does not walk you about while it zooms.
//
// `touch-action: none` on the canvas is what stops the browser taking two
// fingers for its own page zoom or scroll, and the second half is here:
// the touchstart is not passive and calls the gesture off the moment a
// second finger lands, because a touch on the canvas is otherwise turned
// into a compatibility mouse event, and that mouse event is a click that
// walks you to wherever the finger happened to be.
//
// The gesture is over when the LAST finger leaves rather than when the
// second does, so one that ends with one finger still resting on the glass
// does not leave that finger holding a walk or a pan it never asked for;
// the walking stops as soon as there are no longer two. Lifting one of the
// two and putting it back down starts the pair afresh at the touchstart,
// from where both fingers are then.
let pinch: { start: TwoFingers; gapFrom: number; mode: TwoFingerMode } | null = null;
canvas.addEventListener(
  "touchstart",
  (ev) => {
    if (ev.touches.length < 2) return;
    ev.preventDefault();
    const start = twoFingers(ev.touches[0], ev.touches[1]);
    pinch = { start, gapFrom: start.gap, mode: null };
    stickDir = null;
    // A walk the first finger's tap had started planning is not what two
    // fingers are for.
    planning = null;
  },
  { passive: false },
);
canvas.addEventListener(
  "touchmove",
  (ev) => {
    if (!pinch || ev.touches.length < 2) return;
    ev.preventDefault();
    const now = twoFingers(ev.touches[0], ev.touches[1]);
    if (!pinch.mode) pinch.mode = twoFingerMode(pinch.start, now);
    if (pinch.mode === "zoom") {
      const step = twoFingerStep(pinch.gapFrom, now);
      if (step.zoom) stepZoom(step.zoom);
      pinch.gapFrom = step.gapFrom;
    } else if (pinch.mode === "walk") {
      const d = stickDirFor(now.mx - pinch.start.mx, now.my - pinch.start.my);
      if (d !== stickDir) {
        stickDir = d;
        // A new way is taken at once rather than at the next pump.
        if (d) {
          newWay = true;
          pumpMovement(performance.now());
        }
      }
    }
  },
  { passive: false },
);
for (const end of ["touchend", "touchcancel"] as const) {
  canvas.addEventListener(end, (ev) => {
    if (pinch && ev.touches.length < 2) stickDir = null;
    if (ev.touches.length === 0) pinch = null;
  });
}

// One finger dragged on the world pans the map, the ground coming with the
// finger, and a double tap held down walks: the finger is a stick, and the
// character steps the way it points from where it landed, for as long as it
// is held there, exactly as a held arrow key walks. That was first asked
// for the other way round (2026-09-28: "Make single tap and drag make the
// player step in the direction. Pan the map on double tap and drag on
// mobile") and swapped on 2026-10-05: "Can we pan on single tap drag, and
// move manually on double tap and drag?". Eight ways, each a slice of the
// circle a quarter of a right angle either side of its direction, so a drag
// has to lean well over before it goes diagonal. Pulling the finger back to
// where it landed stops the walking without lifting it.
//
// Neither can ride on the mouse events a touch mints: a browser sends those
// in a lump after the finger lifts, not while it moves, so a drag would
// arrive as a single click at the place the finger let go — a walk across
// the room. So the drag is read from the touches themselves, and the lump
// that follows is called off at touchend, once and only when the finger
// actually travelled. A tap still falls through to the compatibility mouse
// events and is the click it always was, which keeps one answer to what a
// tap does instead of two that can disagree; the first tap of a double tap
// is one of those, and a walk it started still being planned is dropped
// by the stick's first step, as a key's is.
//
// The slop is a shade under the long press's, so a hold that drifts is
// still a hold: the two gestures start identically and this is the only
// place they can be told apart.
// How soon, and how near the last tap, a second touch must land to be the
// second half of a double tap.
const DOUBLE_TAP_MS = 350;
const DOUBLE_TAP_PX = 40;
// `held` is the long press the finger has made and not yet let go of: see
// the note on longPress(canvas) above.
let drag: { x0: number; y0: number; x: number; y: number; moved: boolean; walk: boolean; held: PressAt | null } | null = null;
/**
 * How far a finger that has been held must go before it is the stick
 * rather than the hold it was: twice the plain drag's slop, since a finger
 * that has rested long enough to feel the buzz has also had longer to
 * creep, and a creep that walks you off instead of opening the menu is the
 * worse of the two mistakes.
 */
const HELD_SLOP = 2 * TOUCH_SLOP;
/**
 * The ring under a held finger: where it was pressed, which is the middle
 * of the stick, and a knob that follows the finger once it drags, held to
 * the ring's edge. It is drawn over everything and catches nothing.
 */
const stickRing = document.createElement("div");
stickRing.id = "stickring";
stickRing.hidden = true;
stickRing.setAttribute("aria-hidden", "true");
const stickKnob = document.createElement("div");
stickKnob.id = "stickknob";
stickRing.append(stickKnob);
document.body.append(stickRing);
const STICK_REACH = 30; // CSS px the knob may stray from the ring's middle
function showStick(x: number, y: number) {
  stickRing.style.left = `${x}px`;
  stickRing.style.top = `${y}px`;
  stickKnob.style.transform = "";
  stickRing.classList.remove("walking");
  stickRing.hidden = false;
}
function moveStick(dx: number, dy: number) {
  const r = Math.hypot(dx, dy);
  const k = r > STICK_REACH ? STICK_REACH / r : 1;
  stickKnob.style.transform = `translate(${dx * k}px, ${dy * k}px)`;
  stickRing.classList.add("walking");
}
function hideStick() {
  stickRing.hidden = true;
}
let lastTap: { at: number; x: number; y: number } | null = null;
canvas.addEventListener(
  "touchstart",
  (ev) => {
    if (ev.touches.length !== 1) {
      drag = null;
      stickDir = null;
      hideStick();
      return;
    }
    const t = ev.touches[0];
    const walk = !!lastTap && performance.now() - lastTap.at <= DOUBLE_TAP_MS && Math.hypot(t.clientX - lastTap.x, t.clientY - lastTap.y) <= DOUBLE_TAP_PX;
    drag = { x0: t.clientX, y0: t.clientY, x: t.clientX, y: t.clientY, moved: false, walk, held: null };
  },
  { passive: true },
);
canvas.addEventListener(
  "touchmove",
  (ev) => {
    if (pinch || !drag || ev.touches.length !== 1) return;
    const t = ev.touches[0];
    if (!drag.moved && Math.hypot(t.clientX - drag.x0, t.clientY - drag.y0) <= (drag.held ? HELD_SLOP : TOUCH_SLOP)) return;
    ev.preventDefault();
    drag.moved = true;
    // A hold dragged on is the stick, from where the finger first came
    // down, and the hold's own action is dropped.
    if (drag.held) {
      drag.held = null;
      drag.walk = true;
    }
    if (!stickRing.hidden) moveStick(t.clientX - drag.x0, t.clientY - drag.y0);
    if (drag.walk) {
      const d = stickDirFor(t.clientX - drag.x0, t.clientY - drag.y0);
      if (d !== stickDir) {
        stickDir = d;
        // A new way is taken at once rather than at the next pump.
        if (d) {
          newWay = true;
          pumpMovement(performance.now());
        }
      }
      return;
    }
    renderer.dragPan(-(t.clientX - drag.x), -(t.clientY - drag.y));
    drag.x = t.clientX;
    drag.y = t.clientY;
  },
  { passive: false },
);
for (const end of ["touchend", "touchcancel"] as const) {
  canvas.addEventListener(
    end,
    (ev) => {
      // The click a finished drag would otherwise mint, swallowed here
      // rather than guessed at from the mouse side: by the time those
      // events arrive there is nothing left to say they came from a
      // finger that had been travelling.
      if (drag?.moved) ev.preventDefault();
      // A hold let go of without a drag: what it always did, now. The
      // echo of the release is swallowed with the rest, so a menu that
      // opens under the finger is not clicked by it.
      const held = drag?.held;
      if (held && ev.touches.length === 0) {
        ev.preventDefault();
        drag!.held = null;
        if (end === "touchend") rightAction(held);
      }
      if (drag && !drag.moved && !drag.walk && !held && ev.changedTouches.length) {
        const t = ev.changedTouches[0];
        lastTap = { at: performance.now(), x: t.clientX, y: t.clientY };
      } else {
        lastTap = null;
      }
      if (ev.touches.length === 0) {
        drag = null;
        stickDir = null;
        hideStick();
      }
    },
    { passive: false },
  );
}
try {
  const saved = localStorage.getItem(ZOOM_KEY);
  if (saved !== null) renderer.setZoomPx(Number(saved));
} catch {
  /* storage unavailable: the default zoom it is */
}

// --- folding the panels -------------------------------------------------------

// Every panel down the side folds away under its own title, and which ones
// you left folded is remembered. What is written down is the list of the
// folded ones rather than a verdict on every panel, the same way the log's
// filters store what is switched off: a panel added to the page later then
// opens by itself, an empty or missing entry means the page as it was laid
// out, and there is nothing to migrate.
//
// The state lives on each button's aria-expanded and nowhere else — the
// stylesheet hides the body off the same attribute — so there is no second
// copy to fall out of step with what a screen reader is saying.
const FOLDED_KEY = "tinytibia.folded";
const panelToggles = () => Array.from(document.querySelectorAll<HTMLButtonElement>(".paneltoggle"));
const panelOf = (toggle: HTMLElement) => toggle.closest<HTMLElement>(".panel")!;
/**
 * The panels the shelf holds when the page loads: the ones shown and hidden
 * by their icons. Taken once, because on a wide screen they move off the
 * shelf into the side columns (see placePanels) and are still the same
 * panels there.
 */
const SHELF_IDS = new Set(Array.from($("panelshelf").querySelectorAll<HTMLElement>(":scope > .panel")).map((p) => p.id));
{
  const folded = new Set<string>();
  try {
    const saved = localStorage.getItem(FOLDED_KEY);
    if (saved) for (const id of JSON.parse(saved) as string[]) folded.add(id);
  } catch {
    /* storage unavailable, or nonsense in it: every panel opens */
  }
  for (const toggle of panelToggles()) {
    // A panel is remembered by its own id, which is what everything else on
    // the page calls it; its place in the column would go wrong the first
    // time one moved, and the depot's comes and goes with the depot.
    toggle.setAttribute("aria-expanded", String(!folded.has(panelOf(toggle).id)));
    toggle.addEventListener("click", () => {
      toggle.setAttribute("aria-expanded", String(toggle.getAttribute("aria-expanded") !== "true"));
      // Read back off the page rather than kept in a set beside it, so what
      // is saved is what is actually on the screen.
      const shut = panelToggles()
        .filter((t) => t.getAttribute("aria-expanded") === "false" && !SHELF_IDS.has(panelOf(t).id))
        .map((t) => panelOf(t).id);
      try {
        localStorage.setItem(FOLDED_KEY, JSON.stringify(shut));
      } catch {
        /* storage unavailable: the fold lasts this session */
      }
    });
  }
}
/**
 * The panels and their icons (2026-10-01: "Show each of the panels as an
 * icon along the bottom (or top). Clicking it displays the panel alongside
 * the icon so that it's clear what the panel is for and clicking it again
 * hides it. The panels can be reordered by dragging the icons"). It
 * replaced the panel areas — the columns either side of the world and the
 * areas above and below it on a narrow screen — with their strips.
 *
 * Laid out since later that day as asked for then: "on narrow screens,
 * panels should stack vertically and push the map in. the icons for the
 * panels should be on the right hand side of the screen starting at the
 * bottom. when a panel is shown, it is highlighted like it currently is but
 * the icon should appear next to the panel so it identifies it and acts as
 * a way to hide it again. remaining unexpanded icons move further up to
 * stay over the map. on wider screens, the panels stack horizontally from
 * the left or right, with the icons along the bottom (justified left or
 * right depending on whether panels will show on that side)".
 *
 * So every panel has a side and a place in one order. Wider than the
 * narrow breakpoint the open ones stand side by side in their side's row
 * beside the world, the left row from the left edge and the right row from
 * the right, and the icons run along the bottom in two groups, the left
 * side's from the left and the right side's from the right. On a narrow
 * screen the open ones stack under the world, each with its icon beside
 * it, and the shut ones' icons go up the right edge of the world from its
 * bottom. Either way the world gives up the room the panels take. An open
 * panel beside the world wears its icon at its foot since 2026-10-07 (see
 * layoutPanels).
 *
 * A press on an icon opens its panel and lights it, and a second press
 * shuts it; so does the panel's title, which wears the icon's drawing. On
 * a wide screen, dragging an icon along the bar or a panel by its title
 * moves it, to the other side if it is dropped there; on a narrow one,
 * dragging an icon up or down moves it (see startIconDrag); with the keyboard,
 * Alt and the left or right arrow on a focused icon moves it along the
 * order. The order, the sides and which panels are open are remembered in
 * this browser.
 *
 * The depot's and the vendor's icons are there only while their panels
 * apply — standing at a depot, or beside a shopkeeper — and shut their
 * panels on leaving. Neither opens by itself on arriving: the vendor's
 * opens on Trade from the shopkeeper's menu (2026-10-01), and the depot's
 * icon is marked urgent whenever you stand at a depot and its screen is
 * shut (2026-10-08, and 2026-10-19 after shutting it), and opens on a press. The
 * market's is there at any depot, where it opens on a press (2026-10-13:
 * "Allow the market whenever the depot is available"); it came from
 * Market on Grubb's menu too (2026-10-07: "add a market feature Grubb")
 * until 2026-10-19. Which of those is open is not remembered.
 */
const PANEL_ICONS: Record<string, string> = {
  invpanel: "bag",
  equippanel: "equip",
  spellspanel: "spells",
  buttons: "controls",
  depotpanel: "depot",
  vendorpanel: "vendor",
  marketpanel: "market",
};
const PANELBAR_KEY = "tinytibia.panelbar";
const PANELOPEN_KEY = "tinytibia.panelopen";
/** The panels that belong to a place, whose being open is not remembered. */
const PANELS_BY_PLACE = new Set(["depotpanel", "vendorpanel", "marketpanel"]);
const panelBar = $("panelbar");
const barLeft = $("panelbarleft");
const barRight = $("panelbarright");
const panelFloat = $("panelfloat");
const panelShelf = $("panelshelf");
const SHELF_PANELS = [...SHELF_IDS].map((id) => $(id));
const shelfPanels = () => SHELF_PANELS;
const icons = new Map<string, HTMLButtonElement>();
const iconOf = (panel: HTMLElement) => icons.get(panel.id)!;
/** Every panel, in order: see layoutPanels. */
let panelOrder: string[] = [];
/** The end of a drag on an icon, so the click its release mints is not a press. */
let iconDragged = false;

/**
 * Which side of the world each panel stands on when the screen is wider
 * than the narrow breakpoint (2026-10-01: "when the screen is not narrow
 * ... show the inventory, spell, and other panels on the left or right
 * (draggable), not the bottom"). It starts as the spells and the controls
 * on the left and the rest on the right, and is whatever the panel was last
 * dragged to after that.
 */
const PANELSIDE_KEY = "tinytibia.panelside";
type PanelSide = "left" | "right";
const FIRST_SIDE: Record<string, PanelSide> = { spellspanel: "left", buttons: "left" };
let panelSides: Record<string, PanelSide> = {};
try {
  panelSides = JSON.parse(localStorage.getItem(PANELSIDE_KEY) ?? "{}") as Record<string, PanelSide>;
} catch {
  /* nonsense in storage: the first sides */
}
const sideOf = (id: string): PanelSide => panelSides[id] ?? FIRST_SIDE[id] ?? "right";
const columnOf = (side: PanelSide) => $(side === "left" ? "panelcolleft" : "panelcolright");
const groupOf = (side: PanelSide) => (side === "left" ? barLeft : barRight);

function savePanelBar() {
  try {
    const open = shelfPanels()
      // A bag held open by a trade is remembered as the player left it.
      .filter((p) => (p.id === "invpanel" && bagBeforeHeld !== null ? bagBeforeHeld : p.classList.contains("open")) && !PANELS_BY_PLACE.has(p.id))
      .map((p) => p.id);
    localStorage.setItem(PANELBAR_KEY, JSON.stringify(panelOrder));
    localStorage.setItem(PANELOPEN_KEY, JSON.stringify(open));
    localStorage.setItem(PANELSIDE_KEY, JSON.stringify(panelSides));
  } catch {
    /* storage unavailable: the order lasts this session */
  }
}

/**
 * Put every panel and its icon where this width keeps them, in order:
 * wider than the breakpoint the panel in its side's row, with its icon at
 * its foot if it is open, and the icon in its side's group on the bar if it
 * is not; narrower, the panel on the shelf under the world
 * with its icon beside it if it is open, and the icon up the edge of the
 * world if it is not. A box with nothing open in it is marked, so it takes
 * no room. Run on every open and shut, every move, and whenever the window
 * crosses the breakpoint.
 */
function layoutPanels() {
  const narrow = narrowScreen.matches;
  for (const id of panelOrder) {
    const panel = $(id);
    const icon = iconOf(panel);
    // An open panel wears its icon (2026-10-01: "the icons should be inline
    // with their contents in the wide view just like for the narrow view"):
    // beside it on a narrow screen, and on a wide one at its foot, under its
    // contents, which run down from its top (2026-10-07: "When I said shown
    // them at the bottom in horizontal screen, I meant at the bottom of the
    // panel, vertically aligning the contents of the panel from top to
    // bottom"); it was at its head until then. A shut one's icon floats
    // over the world, up its right edge on a narrow screen and in the
    // bottom corner of its side on a wide one.
    const open = panel.classList.contains("open");
    if (id === "depotpanel") {
      // The depot is a screen over the world since 2026-10-17 (see
      // showDepotDlg), and its icon stays where a shut panel's stands, lit
      // while the screen is up, as the Market's does.
      if (!open) {
        if (narrow) panelShelf.append(panel);
        else columnOf(sideOf(id)).append(panel);
      } else depotBox.append(panel);
      if (narrow) panelFloat.append(icon);
      else groupOf(sideOf(id)).append(icon);
      showDepotDlg(open && !panel.hidden);
      continue;
    }
    if (id === "vendorpanel") {
      // The shop is a window over the world since 2026-10-19 (see
      // showVendorDlg), and its icon stays where a shut panel's stands, lit
      // while the window is up, as the depot's does.
      if (!open) {
        if (narrow) panelShelf.append(panel);
        else columnOf(sideOf(id)).append(panel);
      } else vendorBox.append(panel);
      if (narrow) panelFloat.append(icon);
      else groupOf(sideOf(id)).append(icon);
      showVendorDlg(open && !panel.hidden);
      continue;
    }
    if (id === "marketpanel") {
      // The Market is a dialog over the whole screen rather than a panel
      // (2026-10-17: "Make the market a full screen dialog instead"): a
      // list of lots, a filter and a form want the room a side or the
      // shelf could not give them. Its icon comes and goes as a panel's
      // does, and stays where a shut panel's stands, lit while the dialog
      // is up, so pressing it again shuts it.
      if (open) marketBox.append(panel);
      else if (narrow) panelShelf.append(panel);
      else columnOf(sideOf(id)).append(panel);
      if (narrow) panelFloat.append(icon);
      else groupOf(sideOf(id)).append(icon);
      showMarketDlg(open && !panel.hidden);
      continue;
    }
    if (narrow) {
      panelShelf.append(panel);
      if (open) panel.prepend(icon);
      else panelFloat.append(icon);
    } else {
      const side = sideOf(id);
      columnOf(side).append(panel);
      if (open) panel.append(icon);
      else groupOf(side).append(icon);
    }
  }
  for (const box of [panelShelf, columnOf("left"), columnOf("right")]) {
    box.classList.toggle("hasopen", Array.from(box.children).some((p) => p.classList.contains("open") && !(p as HTMLElement).hidden));
  }
}
narrowScreen.addEventListener("change", layoutPanels);

/**
 * Under the world on a narrow screen each panel is one row that scrolls
 * sideways (2026-10-17: "When the panels are below, the scrolling should
 * be horizontal"). A finger or a trackpad scrolls it sideways as it is; a
 * mouse wheel turns up and down, so over a row with more to show that way
 * the wheel scrolls it along instead, and past either end it goes back to
 * scrolling the shelf.
 */
panelShelf.addEventListener(
  "wheel",
  (ev) => {
    if (ev.ctrlKey || Math.abs(ev.deltaX) >= Math.abs(ev.deltaY)) return;
    const body = (ev.target as HTMLElement).closest<HTMLElement>("#panelshelf > .panel > .panelbody");
    if (!body || getComputedStyle(body).overflowX !== "auto") return;
    const room = body.scrollWidth - body.clientWidth;
    if (room <= 0) return;
    if (ev.deltaY > 0 ? body.scrollLeft >= room - 1 : body.scrollLeft <= 0) return;
    ev.preventDefault();
    body.scrollLeft += ev.deltaY;
  },
  { passive: false },
);

/**
 * Each side is as wide as the panels open in it (2026-10-17: "the size of
 * the panels should be fixed since they always scroll. so we should be
 * able to automatically fit the panelcol/panelshelf"): every panel is one
 * column that scrolls down, so its width does not change with what is in
 * it, and the side fits them in style.css with no grip to drag. Until then
 * a side was a width the player dragged, remembered in this browser, and
 * what did not fit across it scrolled sideways. It still does past 45% of
 * the row, the most a side may take.
 */
const PANEL_STEP_PX = 16;
const worldRow = $("worldrow");
for (const side of ["left", "right"] as const) {
  const col = columnOf(side);
  // The right side's panels run leftwards from its right edge.
  const sign = side === "left" ? 1 : -1;
  // The wheel scrolls a side along, since it only scrolls sideways: a
  // mouse with one wheel would otherwise have no way to it but the bar.
  // Not over something inside that scrolls up and down itself, the
  // market's list, which keeps the wheel for its own.
  col.addEventListener(
    "wheel",
    (ev) => {
      if (ev.ctrlKey || Math.abs(ev.deltaX) >= Math.abs(ev.deltaY) || col.scrollWidth <= col.clientWidth) return;
      for (let el = ev.target as HTMLElement | null; el && el !== col; el = el.parentElement) {
        const oy = getComputedStyle(el).overflowY;
        if ((oy === "auto" || oy === "scroll") && el.scrollHeight > el.clientHeight) return;
      }
      ev.preventDefault();
      // Down is on to the panels further from the edge: leftwards on the
      // right side, whose panels run from its right edge.
      col.scrollLeft += sign * ev.deltaY;
    },
    { passive: false },
  );
}

/**
 * The order and sides read back off the page after a drag, side by side:
 * the open panels in that side's row, each wearing its icon, then the shut
 * panels' icons in that side's corner. A panel whose icon has been dragged
 * out of it goes where its icon went. On a narrow screen it is the open
 * panels down the shelf, then the shut ones' icons up the edge of the
 * world, and the sides are left as they were.
 */
function readOrder() {
  const order: string[] = [];
  if (narrowScreen.matches) {
    for (const el of Array.from(panelShelf.children)) {
      if (icons.has(el.id) && iconOf(el as HTMLElement).parentElement === el) order.push(el.id);
    }
    for (const el of Array.from(panelFloat.children)) {
      const id = (el as HTMLElement).dataset.panel ?? "";
      if (icons.has(id) && !order.includes(id)) order.push(id);
    }
    panelOrder = [...order, ...panelOrder.filter((id) => !order.includes(id))];
    return;
  }
  for (const side of ["left", "right"] as const) {
    for (const el of Array.from(columnOf(side).children)) {
      if (!icons.has(el.id) || iconOf(el as HTMLElement).parentElement !== el) continue;
      order.push(el.id);
      panelSides[el.id] = side;
    }
    for (const el of Array.from(groupOf(side).children)) {
      const id = (el as HTMLElement).dataset.panel ?? "";
      if (!icons.has(id) || order.includes(id)) continue;
      order.push(id);
      panelSides[id] = side;
    }
  }
  // Anything in neither (on the shelf, at a narrow width) keeps its place at the end.
  panelOrder = [...order, ...panelOrder.filter((id) => !order.includes(id))];
}

/** Open a panel or shut it, and light its icon to match. */
function showPanel(panel: HTMLElement, open: boolean, save = true) {
  // The bag stays shown while a trade or the shop is up: see holdBag.
  if (!open && panel.id === "invpanel" && bagHeld()) return;
  panel.classList.toggle("open", open);
  // Lit to match, and calmed if it was calling: see urgenticon.ts. The
  // Depot's calls again on shutting while you still stand at the box.
  markOpen(iconOf(panel), open, panel.id === "depotpanel" && !panel.hidden);
  layoutPanels();
  if (save) savePanelBar();
  // The bag shows what the vendor pays only while his panel is open
  // (2026-10-08), so opening or shutting it redraws the bag at once rather
  // than at the next state.
  if (panel.id === "vendorpanel" && game.you) ui.update(game.you);
}

/**
 * An icon is there only while its panel can be: the depot's and the
 * vendor's come and go with the places they belong to, and shut their
 * panels on leaving. Called on every state, after the panels have been
 * told whether they apply.
 */
function syncPanelBar() {
  for (const panel of shelfPanels()) {
    const icon = iconOf(panel);
    if (icon.hidden === panel.hidden) continue;
    icon.hidden = panel.hidden;
    if (panel.id === "vendorpanel") {
      // Not on arriving: on asking (2026-10-01: "don't automatically open
      // a vendor panel when standing near a vendor. require clicking on
      // them"). Arriving beside the vendor that was clicked opens it;
      // leaving shuts it and forgets the asking. See askVendor.
      showPanel(panel, !panel.hidden && vendorAsked === game.you?.vendorname, false);
      if (panel.hidden) vendorAsked = "";
    } else if (panel.id === "depotpanel") {
      // Not on arriving either (2026-10-08: "don't automatically open the
      // depot when standing next to it. show the panelicon for it with an
      // urgent appearance of some kind so it's obvious"): the icon comes
      // marked urgent and the panel stays shut until it is pressed, which
      // calms it while it is open (showPanel). Leaving hides the icon and
      // shuts the panel.
      showPanel(panel, placeIconFollows(panel, icon), false);
    } else if (panel.id === "marketpanel") {
      // Never opened by arriving: it opened from Grubb's menu until
      // 2026-10-19, and the market is in the depot screen now.
      showPanel(panel, false, false);
    } else if (PANELS_BY_PLACE.has(panel.id)) showPanel(panel, !panel.hidden, false);
    else layoutPanels();
  }
}



/** The shopkeeper last clicked, whose shop opens on reaching them; "" for none. */
let vendorAsked = "";

/**
 * Clicking a shopkeeper asks for their shop: beside them it opens now, and
 * otherwise when the click (which follows them) has brought you beside
 * them, in syncPanelBar.
 */
function askVendor(name: string) {
  vendorAsked = name;
  if (!$("vendorpanel").hidden && game.you?.vendorname === name) showPanel($("vendorpanel"), true, false);
}

/**
 * Where a dragged thing goes in a row or group laid out left to right, or
 * right to left for the right side's, or in a column on a narrow screen,
 * down the shelf or up the edge of the world: before the first other one
 * whose middle the pointer is short of, reading the way the box reads.
 */
function dropBefore(box: HTMLElement, moving: Element, at: { x: number; y: number }, skip: (el: Element) => boolean): Element | null {
  const dir = getComputedStyle(box).flexDirection;
  const down = dir.startsWith("column");
  const reversed = dir.endsWith("-reverse");
  const p = down ? at.y : at.x;
  for (const other of Array.from(box.children)) {
    if (other === moving || skip(other)) continue;
    const r = other.getBoundingClientRect();
    const mid = down ? r.top + r.height / 2 : r.left + r.width / 2;
    if (reversed ? p > mid : p < mid) return other;
  }
  return null;
}

/**
 * Dragging an icon: past a few pixels a press is a drag. On a screen wider
 * than the narrow breakpoint the half of the world's row the pointer is
 * over is the side it goes to. A shut panel's icon goes before whichever
 * icon in that side's corner the pointer is short of, and its panel
 * follows when it is let go; an open panel's icon, at the panel's foot,
 * carries the panel with it into that side's row.
 *
 * On a narrow screen, a phone's, it moves up and down (2026-10-09: "check
 * that iphone can reorder the panels by dragging the panel icons", which
 * it could not: a drag did nothing at that width). A shut panel's icon
 * moves among the others up the edge of the world, and an open one's,
 * beside its panel, carries the panel up or down the shelf under it.
 */
function startIconDrag(icon: HTMLButtonElement, ev: PointerEvent) {
  if (ev.button !== 0) return;
  const x0 = ev.clientX;
  const y0 = ev.clientY;
  let moving = false;
  // The open panel this icon stands at the foot of, if it is in one.
  const panel = icon.parentElement?.classList.contains("panel") ? icon.parentElement : null;
  const move = (m: PointerEvent) => {
    if (!moving) {
      if (Math.abs(m.clientX - x0) + Math.abs(m.clientY - y0) < 6) return;
      moving = true;
      iconDragged = true;
      icon.classList.add("dragging");
    }
    const at = { x: m.clientX, y: m.clientY };
    if (narrowScreen.matches) {
      // Within its own box: an open panel stays on the shelf and a shut
      // one's icon over the world, and a press is what moves one between.
      const box = panel ? panelShelf : panelFloat;
      const moved = panel ?? icon;
      if (moved.parentElement !== box) return;
      const before = dropBefore(box, moved, at, (el) => (panel ? !el.classList.contains("open") : (el as HTMLElement).hidden));
      if (moved.nextElementSibling !== before) box.insertBefore(moved, before);
      return;
    }
    const r = $("worldrow").getBoundingClientRect();
    const side: PanelSide = m.clientX < r.left + r.width / 2 ? "left" : "right";
    if (panel) {
      // An open panel's icon carries its panel: across to the row on the
      // side the pointer is over, before whichever open panel there the
      // pointer is short of.
      const col = columnOf(side);
      col.classList.add("hasopen");
      const before = dropBefore(col, panel, at, (el) => !el.classList.contains("open"));
      if (panel.parentElement !== col || panel.nextElementSibling !== before) col.insertBefore(panel, before);
    } else {
      const group = groupOf(side);
      const before = dropBefore(group, icon, at, (el) => (el as HTMLElement).hidden);
      if (icon.parentElement !== group || icon.nextElementSibling !== before) group.insertBefore(icon, before);
    }
  };
  const end = () => {
    window.removeEventListener("pointermove", move);
    window.removeEventListener("pointerup", end);
    window.removeEventListener("pointercancel", end);
    if (!moving) return;
    icon.classList.remove("dragging");
    readOrder();
    layoutPanels();
    savePanelBar();
    // The click the release mints lands in the same turn: let it pass first.
    setTimeout(() => (iconDragged = false));
  };
  window.addEventListener("pointermove", move);
  window.addEventListener("pointerup", end);
  window.addEventListener("pointercancel", end);
}

/**
 * The bag's icon says how heavy the bag is (2026-10-07: "Can we show the
 * inventory encumbrance "fill" on the icon and show more detail by
 * hovering?", and "As part of that, don't show the numbers in the panel
 * anymore"). A fill rises from the icon's foot, behind the drawing, as far
 * up it as the bag is full of what can be lifted: muted, amber from four
 * fifths, and red and hatched past the line, where it stops at the top.
 * style.css colours it off the icon's data-load. The numbers and what the
 * weight does are on the hover, after the sentence every icon has, and at
 * the head of the small menu a long press or a right-click opens. The
 * words and the arithmetic are bagweight.ts's.
 *
 * That menu is how the bag is sorted (2026-10-08: "the bag sort by weight
 * should be a context option on the bag panelicon itself, not on the items
 * in the bag."): Sort by kind and Sort by weight, the one in force ticked.
 * A plain press still shows or hides the bag, so the menu holds nothing
 * else; the sort used to be a button in every item's dialog.
 *
 * It is painted on every state, which is how a pickup, a drop, a sale and
 * a level all arrive, and it touches the page only when the numbers change.
 */
let bagIcon: HTMLButtonElement | null = null;
let bagIconTitle = "";
let bagLoadAt = "";
let bagWeight = { cn: 0, maxcn: 0 };

function wireBagLoad(icon: HTMLButtonElement, panel: HTMLElement) {
  bagIcon = icon;
  bagIconTitle = icon.title;
  const fill = document.createElement("span");
  fill.className = "bagfill";
  fill.setAttribute("aria-hidden", "true");
  // Before the drawing, so the drawing is painted over it.
  icon.prepend(fill);
  longPress(icon, () => {
    // A finger that has started dragging the icon is moving it, not asking.
    if (iconDragged) return;
    const name = icon.getAttribute("aria-label") ?? "Inventory";
    const by = ui.bagSortedBy;
    const sort = (to: BagSort, label: string, title: string): PopItem => ({ label, title, checked: by === to, go: () => ui.sortBag(to) });
    showPopMenu(
      {
        label: name,
        head: bagWeight.maxcn > 0 ? bagWeightTip(bagWeight.cn, bagWeight.maxcn) : undefined,
        items: [
          sort("kind", "Sort by kind", "Potions, rings, weapons (two-handed, ranged, one-handed), shields, armor, cloaks, helms and everything else, then food and gold, the most valuable first in each"),
          sort("weight", "Sort by weight", "The heaviest pile first: what one weighs times how many"),
          ...lineItems(panel),
        ],
      },
      icon.getBoundingClientRect(),
      { back: icon, on: icon },
    );
  });
  paintBagLoad(0, 0);
}

/**
 * How many columns the bag and the spells take beside the world, and how
 * many rows under it (2026-11-02: "add an option to the bag and spell
 * panels to show different numbers of rows (or columns)"). Beside the
 * world a panel is one column that scrolls down, and under it on a narrow
 * screen one row that scrolls along (see style.css); this lets either be
 * up to LINES_MAX wide or tall instead, so a big bag or a long spell list
 * shows more at once for the room it takes from the world.
 *
 * It is chosen from the panel icon's long press or right-click, which for
 * the bag is the menu that sorts it, and the choice is the one for where
 * the panel is now: the columns on a wide screen, the rows on a narrow
 * one. The two are kept apart, so a tablet turned on its side keeps both.
 * They are kept in this browser, by panel, and style.css reads them off
 * the panel as --sidelines and --shelflines.
 */
const PANELLINES_KEY = "tinytibia.panellines";
const LINED_PANELS = ["invpanel", "spellspanel"];
const LINES_MAX = 4;
type LinesAt = "side" | "shelf";
let panelLines: Record<string, number> = {};
try {
  panelLines = JSON.parse(localStorage.getItem(PANELLINES_KEY) ?? "{}") as Record<string, number>;
} catch {
  /* nonsense in storage: one line each */
}

function linesOf(id: string, at: LinesAt): number {
  const n = panelLines?.[`${id}.${at}`];
  return typeof n === "number" && Number.isInteger(n) && n >= 1 && n <= LINES_MAX ? n : 1;
}

function paintLines(panel: HTMLElement) {
  panel.style.setProperty("--sidelines", String(linesOf(panel.id, "side")));
  panel.style.setProperty("--shelflines", String(linesOf(panel.id, "shelf")));
}

function setLines(panel: HTMLElement, at: LinesAt, n: number) {
  panelLines = { ...panelLines, [`${panel.id}.${at}`]: n };
  paintLines(panel);
  try {
    localStorage.setItem(PANELLINES_KEY, JSON.stringify(panelLines));
  } catch {
    /* storage unavailable: the choice lasts this session */
  }
}

/** The menu's choices for how many lines the panel takes where it is now. */
function lineItems(panel: HTMLElement): PopItem[] {
  const at: LinesAt = narrowScreen.matches ? "shelf" : "side";
  const word = at === "shelf" ? "row" : "column";
  const now = linesOf(panel.id, at);
  const items: PopItem[] = [];
  for (let n = 1; n <= LINES_MAX; n++) {
    const words = n === 1 ? `1 ${word}` : `${n} ${word}s`;
    items.push({
      label: words,
      title: at === "shelf" ? `Show the panel ${words} tall under the world, scrolling along` : `Show the panel ${words} wide beside the world, scrolling down`,
      checked: n === now,
      go: () => setLines(panel, at, n),
    });
  }
  return items;
}

/** The spells' icon opens the same choice on a long press or a right-click. */
function wireSpellLines(icon: HTMLButtonElement, panel: HTMLElement) {
  longPress(icon, () => {
    if (iconDragged) return;
    showPopMenu({ label: icon.getAttribute("aria-label") ?? "Spells", items: lineItems(panel) }, icon.getBoundingClientRect(), { back: icon, on: icon });
  });
}

function paintBagLoad(cn: number, maxcn: number) {
  const at = `${cn}/${maxcn}`;
  if (!bagIcon || at === bagLoadAt) return;
  bagLoadAt = at;
  bagWeight = { cn, maxcn };
  const { fill, load } = bagFill(cn, maxcn);
  bagIcon.style.setProperty("--fill", String(fill));
  bagIcon.dataset.load = load;
  bagIcon.title = maxcn > 0 ? `${bagIconTitle}\n${bagWeightTip(cn, maxcn)}` : bagIconTitle;
}

{
  let order: string[] = [];
  try {
    order = JSON.parse(localStorage.getItem(PANELBAR_KEY) ?? "[]") as string[];
  } catch {
    /* nonsense in storage: the order the panels were written in */
  }
  const ids = shelfPanels().map((p) => p.id);
  panelOrder = [...order.filter((id) => ids.includes(id)), ...ids.filter((id) => !order.includes(id))];
  for (const id of panelOrder) {
    const panel = $(id);
    // The panel has no title since 2026-10-01 ("Remove the title text,
    // extra icon and help button on on the panels"): its icon is its name,
    // and the name the icon is read out by is the panel's own label.
    const name = panel.getAttribute("aria-label") ?? id;
    const glyph = PANEL_ICONS[id] ?? "more";
    const icon = document.createElement("button");
    icon.type = "button";
    icon.className = "panelicon";
    icon.dataset.panel = id;
    icon.setAttribute("aria-controls", id);
    icon.setAttribute("aria-label", name);
    icon.title = `${name}: press to show or hide it. Drag it to move the panel along the others, or on a wide screen to the other side of the world`;
    icon.append(viewGlyph(glyph));
    icons.set(id, icon);
    icon.addEventListener("pointerdown", (ev) => startIconDrag(icon, ev));
    icon.addEventListener("click", () => {
      if (iconDragged) return;
      showPanel(panel, !panel.classList.contains("open"));
    });
    if (LINED_PANELS.includes(id)) {
      icon.title += ". Hold it, or right-click it, to choose how many columns it shows beside the world or how many rows under it";
      paintLines(panel);
    }
    if (id === "invpanel") wireBagLoad(icon, panel);
    else if (id === "spellspanel") wireSpellLines(icon, panel);
    icon.addEventListener("keydown", (ev) => {
      if (!ev.altKey || (ev.key !== "ArrowLeft" && ev.key !== "ArrowRight")) return;
      ev.preventDefault();
      ev.stopPropagation();
      const i = panelOrder.indexOf(id);
      const j = ev.key === "ArrowLeft" ? i - 1 : i + 1;
      if (j < 0 || j >= panelOrder.length) return;
      [panelOrder[i], panelOrder[j]] = [panelOrder[j], panelOrder[i]];
      layoutPanels();
      icon.focus();
      savePanelBar();
    });
  }
  // Open as they were left, or the bag alone the first time.
  let open = ["invpanel"];
  try {
    const saved = localStorage.getItem(PANELOPEN_KEY);
    if (saved !== null) open = JSON.parse(saved) as string[];
  } catch {
    /* nonsense in storage: the bag alone */
  }
  for (const panel of shelfPanels()) showPanel(panel, open.includes(panel.id) && !PANELS_BY_PLACE.has(panel.id), false);
  layoutPanels();
  syncPanelBar();
}

// --- how much of the screen is log ---------------------------------------------

/**
 * The height of the console, and the strip along its top that changes it.
 *
 * Asked for on 2026-09-19: "Make the chat log area resizable." It shipped at a flat
 * 150 pixels, which is four or five lines — plenty while you are walking
 * somewhere and nothing like enough while you are reading back through a
 * fight, or watching a shopkeeper's answers, or following a conversation.
 * Nobody wants the same log all the time, and a number that suits everybody
 * does not exist, so the number becomes theirs.
 *
 * The console is a flex item with flex: 0 0 auto, so what it takes is
 * exactly its height and the view above it — flex: 1 — gets the rest. That
 * is the whole mechanism: setting a height on the console makes the world
 * shorter, and the renderer sizes its backing store off the canvas every
 * frame, so the view follows without being told.
 *
 * What it cannot be allowed to do is destroy either end of that trade,
 * which is what the two bounds are for:
 *
 *   - LOG_MIN_PX is the chat box and a couple of lines over it. Below that
 *     the console is a text field with a sliver of scrollback, and a log
 *     you cannot read is the same as no log. It was 72 for an hour, which
 *     measured out at eight pixels of actual log — half a line — because
 *     the strip, the filters and the chat box are most of a short console.
 *   - the maximum is the smaller of what leaves VIEW_MIN_PX of world and
 *     LOG_MAX_SHARE of the window. The first is the one that actually
 *     binds, since it is measured against the wrap the two of them share,
 *     and it is the one that keeps a phone honest: there the panels stand
 *     in the same wrap, between the world and the console, so the height
 *     the console is competing for is nothing like the window's. The share is the
 *     second opinion — on a tall desktop window a console that left 140
 *     pixels of world would technically be legal and would not be a game —
 *     and either can be the tighter one, so both are asked.
 *
 * The height is read back with offsetHeight and never clientHeight, and
 * that is not fussiness: the console wears a two-pixel top border, and with
 * border-box sizing the height set on it includes that border while
 * clientHeight does not. Reading the one and writing the other loses two
 * pixels every time, which a drag hides and the arrow keys do not — a press
 * of ArrowUp moved the boundary fourteen pixels and the next one fourteen
 * more, so the log crept the wrong way under a key that says grow.
 *
 * The bounds move with the window: rotate a phone, or let the browser's own
 * chrome slide away, and what was legal may not be. So the clamp is applied
 * again on every resize rather than only on the drag, and what is written
 * down is what the clamp allowed rather than what the hand asked for — a
 * console dragged to the stop on a short window should not spring past it
 * on a tall one, and a stored 4000 is a number nobody meant.
 */
const LOG_HEIGHT_KEY = "tinytibia.logheight";
const LOG_MIN_PX = 96; // the chat box and a couple of lines of log over it
const VIEW_MIN_PX = 140; // and this much of the world is never taken
const LOG_MAX_SHARE = 0.7; // nor more than this much of the window, however tall it is
const LOG_STEP_PX = 16; // what one press of an arrow is worth: about a line

const gamewrap = $("gamewrap");
const consoleBox = $("console");
const logGrip = $("loggrip");

/**
 * Where the log stands (2026-10-07: "the message log tile under the main
 * window always", and of the two ways it was shown, "tile the chat log
 * under the main window only per option A"). Wider than the narrow
 * breakpoint it is in the row the world stands in, under the world and as
 * wide as it, with the side panel rows running down beside the two of them;
 * style.css lays the row out as a grid for it. On a narrow screen it is
 * under the shelf of open panels, at the foot of #gamecol, where it was.
 *
 * It is moved rather than written into the row in index.html because on a
 * narrow screen the row is a column with the shelf at its foot, and the
 * log goes under the shelf. Written into the row, it would also count
 * towards the height the shelf takes its share of: a phone's world lost
 * seventy pixels to that when it was tried.
 *
 * Moving it has to leave it as it was. Its listeners go with it — the
 * long press and right click that copy a line, the strip that drags its
 * height, the way back to the newest line — since they are on the elements
 * and not on where they stand. What a move does lose is put back: the line
 * being read, by holdLogView, as for any change to the log's box, and the
 * focus, which a browser takes off an element that leaves the page even
 * for an instant, so a chat line half typed as the window crosses the
 * breakpoint is still being typed.
 */
function placeLog() {
  const home = narrowScreen.matches ? $("gamecol") : $("worldrow");
  if (consoleBox.parentElement === home) return;
  const focused = document.activeElement;
  ui.holdLogView(() => home.append(consoleBox));
  if (focused instanceof HTMLElement && focused !== document.activeElement && consoleBox.contains(focused)) focused.focus({ preventScroll: true });
}
placeLog();
narrowScreen.addEventListener("change", placeLog);

/** The height asked for, before the window has had its say about it. */
let logWanted = consoleBox.offsetHeight;

function logMaxPx(): number {
  // What is left of the world, once the bars over it and the panel icons
  // under it, which stand in the same box, are paid for.
  const room = gamewrap.clientHeight - $("topbars").offsetHeight - $("panelbar").offsetHeight - VIEW_MIN_PX;
  const share = window.innerHeight * LOG_MAX_SHARE;
  // The floor is the minimum and not zero: on a window too short for both
  // bounds at once the clamp would otherwise invert, and a maximum under
  // the minimum is a console of nothing.
  return Math.max(LOG_MIN_PX, Math.min(room, share));
}

/**
 * Put `logWanted` on the console, within the bounds, and say so.
 *
 * The log is put back on its newest line if it was following new lines,
 * for the same reason the filters do it: growing the box shows more of what
 * is above where you were reading, and a log that was pointing at the
 * newest line should still be pointing at it afterwards. A log scrolled
 * somewhere on purpose keeps the line being read where it was (see
 * holdLogView in ui.ts).
 */
function applyLogHeight() {
  const max = logMaxPx();
  const h = Math.round(Math.min(Math.max(logWanted, LOG_MIN_PX), max));
  ui.holdLogView(() => (consoleBox.style.height = `${h}px`));
  // The separator reports itself in pixels, bounds and all, because the
  // bounds are not constants — they are whatever this window leaves.
  logGrip.setAttribute("aria-valuenow", String(h));
  logGrip.setAttribute("aria-valuemin", String(LOG_MIN_PX));
  logGrip.setAttribute("aria-valuemax", String(Math.round(max)));
}

function saveLogHeight() {
  try {
    localStorage.setItem(LOG_HEIGHT_KEY, String(Math.round(logWanted)));
  } catch {
    /* storage unavailable: this height lasts the session */
  }
}

/**
 * Dragging the strip, by mouse, pen and finger alike.
 *
 * One path for all three, which is the thing pointer events are for, and
 * unlike the panels there is no long press in front of the touch. A panel
 * is picked up out of a column that scrolls under a thumb, so the first
 * pixels of a touch there are genuinely ambiguous and the hold is how the
 * ambiguity is settled. This strip is a few pixels of furniture that
 * scrolls nothing and does nothing else at all: a press on it can only
 * mean the drag, so making somebody wait 600ms to be believed would be a
 * ceremony with no question behind it. `touch-action: none` in the
 * stylesheet is the other half — it stops the browser taking the gesture
 * back for a pan, which is what a pointercancel mid-drag would be.
 *
 * The listeners go on the window rather than the strip, the way the panel
 * drag's do, so that a hand that outruns an 8-pixel target keeps the drag:
 * the pointer leaves the strip on the first fast move otherwise. (The panel
 * drag has a second reason — a captured element that is re-inserted loses
 * its capture — which does not apply here, since this strip never moves.)
 */
function startLogDrag(ev: PointerEvent) {
  if (ev.button !== 0) return;
  ev.preventDefault(); // no text selection dragged out of the page behind it
  const startY = ev.clientY;
  const startH = consoleBox.offsetHeight;
  logGrip.classList.add("dragging");
  const move = (m: PointerEvent) => {
    logWanted = startH + (startY - m.clientY); // the strip goes up, the log grows
    applyLogHeight();
  };
  const end = () => {
    logGrip.classList.remove("dragging");
    window.removeEventListener("pointermove", move);
    window.removeEventListener("pointerup", end);
    window.removeEventListener("pointercancel", end);
    logWanted = consoleBox.offsetHeight; // what the bounds allowed, not what was asked
    saveLogHeight();
  };
  window.addEventListener("pointermove", move);
  window.addEventListener("pointerup", end);
  window.addEventListener("pointercancel", end);
}
logGrip.addEventListener("pointerdown", startLogDrag);

// Without a pointer: the arrows on the focused strip, a line at a time. It
// is worth the six lines it costs — a console stuck at whatever the last
// mouse left it at is a console somebody cannot change at all — and it is
// cheap precisely because the element is already a separator with a value,
// so there is nothing to invent.
logGrip.addEventListener("keydown", (ev) => {
  const step = ev.key === "ArrowUp" ? LOG_STEP_PX : ev.key === "ArrowDown" ? -LOG_STEP_PX : 0;
  if (!step) return;
  ev.preventDefault(); // the page does not scroll out from under the strip
  logWanted = consoleBox.offsetHeight + step;
  applyLogHeight();
  logWanted = consoleBox.offsetHeight;
  saveLogHeight();
});

// The window is not the size it was: a phone turned on its side, a desktop
// window dragged smaller, the browser's own chrome sliding away. The height
// is asked for again rather than left alone, because the bounds it was
// legal under have moved.
addEventListener("resize", applyLogHeight);

try {
  const saved = Number(localStorage.getItem(LOG_HEIGHT_KEY));
  if (Number.isFinite(saved) && saved > 0) logWanted = saved;
} catch {
  /* storage unavailable: the height the stylesheet gives it */
}
applyLogHeight();

/**
 * The shelf on a narrow screen is as tall as the panels open on it, each
 * one row that scrolls along (2026-10-17: "we should be able to
 * automatically fit the panelcol/panelshelf"), up to what leaves the
 * world its share (style.css). Until then its height was dragged by a
 * strip on its top edge and remembered in this browser. SHELF_MIN_PX is
 * what the drawer leaves it while it is open.
 */
const SHELF_MIN_PX = 64;

/**
 * The info drawer's height on a narrow screen, dragged by the strip under
 * it or moved by the up and down arrows with the strip focused, and
 * remembered in this browser (2026-10-13: "Make the drawer resizable").
 * Wider than that the drawer is a column whose right edge drags its width
 * (setDrawerWidth); here it stands above the world the screen's width, and
 * was capped at a fixed 35% of the page with nothing to change it by. The
 * strip is #drawergrip, the width's grip, laid across the drawer's foot by
 * the narrow rules in style.css and drawn like the shelf's and the log's.
 *
 * What is dragged is the cap, not the height: held upright the drawer is
 * as tall as what is unfolded in it and no taller (2026-10-10: "resize the
 * drawer to fit the contents"), up to the cap, so folding a panel still
 * gives the world back what it took. Like the shelf it is kept as a share
 * of the page, so it keeps its part as the phone turns, and its bounds are
 * in pixels: at least DRAWER_MIN_H_PX, and never so much that the row
 * under it is left less than VIEW_MIN_PX of world under the bars (and the
 * shelf's least, while the shelf is open). They are asked again whenever
 * the row changes size, and what is written down is what they allowed.
 */
const DRAWER_H_KEY = "tinytibia.drawerheight";
const DRAWER_MIN_H_PX = 64;
/** The share asked for: what style.css gave the drawer before any drag. */
let drawerHWanted = 0.35;
function drawerHeightBoundsPx(): [number, number] {
  const shelf = panelShelf.classList.contains("hasopen") ? SHELF_MIN_PX : 0;
  const rowMin = $("topbars").offsetHeight + VIEW_MIN_PX + shelf;
  // The drawer and the row share what the page has between them.
  const max = drawer.offsetHeight + worldRow.clientHeight - rowMin;
  return [DRAWER_MIN_H_PX, Math.max(DRAWER_MIN_H_PX, max)];
}
/** Put `drawerHWanted` on the drawer, within the bounds, and say so. */
function applyDrawerHeight() {
  const page = drawerPage.clientHeight;
  let share = drawerHWanted;
  // Shut, or wider than the breakpoint, there is nothing to measure
  // against, and the share stands as asked until there is.
  if (narrowScreen.matches && drawerOpen && page > 0) {
    const [min, max] = drawerHeightBoundsPx();
    const h = Math.round(Math.min(Math.max(drawerHWanted * page, min), max));
    share = h / page;
    drawerGrip.setAttribute("aria-valuemin", String(min));
    drawerGrip.setAttribute("aria-valuemax", String(Math.round(max)));
    drawerGrip.setAttribute("aria-valuenow", String(h));
  }
  drawerPage.style.setProperty("--drawermaxh", `${(share * 100).toFixed(2)}%`);
}
/** Ask for `px` of the page, and keep what the bounds allowed of it. */
function setDrawerHeight(px: number, save: boolean) {
  drawerHWanted = px / drawerPage.clientHeight;
  applyDrawerHeight();
  if (!save) return;
  // What the bounds allowed, not what was asked.
  drawerHWanted = Number(drawerGrip.getAttribute("aria-valuenow")) / drawerPage.clientHeight;
  try {
    localStorage.setItem(DRAWER_H_KEY, String(drawerHWanted));
  } catch {
    /* storage unavailable: the height lasts the session */
  }
}
/** Say which way the grip drags: across on a wide screen, down on a narrow one. */
function paintDrawerGrip() {
  const narrow = narrowScreen.matches;
  drawerGrip.setAttribute("aria-orientation", narrow ? "horizontal" : "vertical");
  drawerGrip.setAttribute("aria-label", `${narrow ? "Height" : "Width"} of the stats, skills and players panel`);
  drawerGrip.title = narrow
    ? "Drag to make the info panel taller or shorter. With the keyboard, the up and down arrows"
    : "Drag to make the info panel wider or narrower. With the keyboard, the left and right arrows";
  if (narrow) applyDrawerHeight();
  else if (drawerWAsked !== undefined) setDrawerWidth(drawerWAsked, false);
  else for (const a of ["aria-valuemin", "aria-valuemax", "aria-valuenow"]) drawerGrip.removeAttribute(a);
}
// The width's own listeners step aside here (see setDrawerWidth), and the
// strip goes down, the drawer grows.
drawerGrip.addEventListener("pointerdown", (ev) => {
  if (ev.button !== 0 || !narrowScreen.matches) return;
  ev.preventDefault();
  const startY = ev.clientY;
  const startH = drawer.offsetHeight;
  drawerGrip.classList.add("dragging");
  const move = (m: PointerEvent) => setDrawerHeight(startH + (m.clientY - startY), false);
  const end = () => {
    window.removeEventListener("pointermove", move);
    window.removeEventListener("pointerup", end);
    window.removeEventListener("pointercancel", end);
    drawerGrip.classList.remove("dragging");
    setDrawerHeight(Number(drawerGrip.getAttribute("aria-valuenow")), true);
  };
  window.addEventListener("pointermove", move);
  window.addEventListener("pointerup", end);
  window.addEventListener("pointercancel", end);
});
drawerGrip.addEventListener("keydown", (ev) => {
  if (!narrowScreen.matches) return;
  const step = ev.key === "ArrowDown" ? PANEL_STEP_PX : ev.key === "ArrowUp" ? -PANEL_STEP_PX : 0;
  if (!step) return;
  ev.preventDefault();
  ev.stopPropagation(); // the arrows move the edge here, not the character
  setDrawerHeight(drawer.offsetHeight + step, true);
});
try {
  const saved = Number(localStorage.getItem(DRAWER_H_KEY));
  if (Number.isFinite(saved) && saved > 0 && saved < 1) drawerHWanted = saved;
} catch {
  /* storage unavailable: the stylesheet's 35% */
}
paintDrawerGrip();
narrowScreen.addEventListener("change", paintDrawerGrip);
// The row under it changes size with the window, the log and the shelf,
// and the drawer comes and goes: the bounds are asked again each time.
new ResizeObserver(applyDrawerHeight).observe(worldRow);
new MutationObserver(applyDrawerHeight).observe(drawer, { attributes: true, attributeFilter: ["class"] });

// --- main loop ----------------------------------------------------------------

/*
 * Whether a screen over the world hides every pixel of it, as the depot
 * does on a phone (2026-10-19: "on phone, the depot interface show over
 * eveyrthing except the chat log") and the Market does: a world nobody can
 * see is not drawn (2026-10-23: "The game is getting laggy on phone,
 * optimise it"), which on a phone with a full depot open was the whole
 * cost of a frame for nothing. Asked of the page when one of them comes
 * up, when the window changes size and every couple of seconds while one
 * is up (asking is a hit test of the whole page, and a full depot makes
 * that dear), and never while none is: points across the world each land
 * on something of a screen with a solid background, or the world is drawn
 * as ever. Shut, the next frame draws it.
 */
const COVER_SCREENS = [depotDlg, marketDlg, vendorDlg];
const COVER_RECHECK_MS = 2000;
let coverWas = "";
let coverAt = -Infinity;
let covered = false;
window.addEventListener("resize", () => (coverWas = ""));
function worldCovered(now: number): boolean {
  const up = COVER_SCREENS.filter((d) => !d.hidden);
  if (!up.length) {
    coverWas = "";
    return (covered = false);
  }
  const was = up.map((d) => d.id).join(",");
  if (was === coverWas && now - coverAt < COVER_RECHECK_MS) return covered;
  coverWas = was;
  coverAt = now;
  const r = canvas.getBoundingClientRect();
  covered = r.width > 0 && r.height > 0;
  for (let i = 0; i <= 2 && covered; i++) {
    for (let j = 0; j <= 2 && covered; j++) {
      const x = r.left + 1 + ((r.width - 2) * i) / 2, y = r.top + 1 + ((r.height - 2) * j) / 2;
      covered = solidOver(document.elementFromPoint(x, y), up);
    }
  }
  return covered;
}
/** Whether `el` is inside one of the screens, on something solid of it. */
function solidOver(el: Element | null, screens: HTMLElement[]): boolean {
  const screen = screens.find((d) => d.contains(el));
  if (!screen) return false;
  for (let at = el; at; at = at.parentElement) {
    const bg = getComputedStyle(at).backgroundColor;
    const alpha = /^rgba\((?:[^,]+,){3}\s*([\d.]+)\)$/.exec(bg);
    if (bg !== "transparent" && (!alpha || Number(alpha[1]) >= 1)) return true;
    if (at === screen) break;
  }
  return false;
}

/*
 * A phone draws the world sixty times a second at most (2026-10-31:
 * "Implement the other improvements", the 60 fps cap among them). A
 * 90 or 120 Hz screen asks for a frame every 11 or 8 ms, and drawing
 * each one is twice the work for a smoothness a phone pays for in heat
 * and dropped frames. The frames it asks for still run everything but
 * the drawing, so a held key or a step comes due exactly when it did.
 * Time owed adds up across frames, so a 90 Hz screen draws two of
 * every three and a 60 Hz one every frame; a little early counts as on
 * time, since a 60 Hz screen's frames come a hair either side of 16.7
 * ms. A mouse (pointer: fine) is a computer, which draws every frame.
 */
const PHONE_FRAME_MS = 1000 / 60;
const FRAME_SLACK_MS = 2;
const touchScreen = window.matchMedia("(pointer: coarse)");
let frameLast = 0;
let frameOwed = PHONE_FRAME_MS;
function drawDue(now: number): boolean {
  const dt = frameLast ? now - frameLast : PHONE_FRAME_MS;
  frameLast = now;
  if (!touchScreen.matches) return true;
  frameOwed += dt;
  if (frameOwed < PHONE_FRAME_MS - FRAME_SLACK_MS) return false;
  // A long frame is not paid back with a burst of short ones.
  frameOwed = Math.min(frameOwed - PHONE_FRAME_MS, PHONE_FRAME_MS);
  return true;
}

/*
 * Frames a second on the stats panel (2026-11-02: "add FPS in the info
 * stats panel"): the frames drawn, not the frames the screen asked for, so
 * a phone held to 60 reads 60 on a 120 Hz screen. Counted over each second
 * and written once a second; a hidden tab draws nothing and says so.
 */
const fpsEl = $("fps");
let fpsSince = 0;
let fpsDrawn = 0;
function countFrame(now: number, drew: boolean) {
  if (drew) fpsDrawn++;
  if (!fpsSince) fpsSince = now;
  const span = now - fpsSince;
  if (span < 1000) return;
  const fps = Math.round((fpsDrawn * 1000) / span);
  fpsEl.textContent = String(fps);
  fpsEl.className = fps >= 50 ? "good" : fps >= 30 ? "fair" : "bad";
  fpsSince = now;
  fpsDrawn = 0;
}

/*
 * Where you stand, on the stats panel with debug on (2026-11-02: "show
 * player position in the info panel when debug is on"): across, down and
 * the floor, written only when one of them changes. The row is hidden by
 * the debugon class, so it is kept up to date whether it shows or not.
 */
const posEl = $("pos");
let posShown = "";
function paintPos() {
  const me = game.me();
  if (!me) return;
  const z = me.z ?? 0;
  const pos = `${me.x}, ${me.y}, ${z > 0 ? `+${z}` : z < 0 ? `\u2212${-z}` : "0"}`;
  if (pos === posShown) return;
  posShown = pos;
  posEl.textContent = pos;
}

function frame(now: number) {
  pumpMovement(now);
  // Whatever steps have come due are begun before anything is drawn: see
  // Game.onSteps.
  game.advance(now);
  game.prune(now);
  const drew = drawDue(now);
  if (drew) {
    if (!worldCovered(now)) renderer.draw(now);
    paintBack();
    paintFloor();
    paintPos();
  }
  countFrame(now, drew);
  requestAnimationFrame(frame);
}
requestAnimationFrame(frame);

// Remember the last name used, purely as a convenience.
try {
  const last = localStorage.getItem("tinytibia.name");
  if (last && NAME_RE.test(last)) $<HTMLInputElement>("username").value = last;
  $<HTMLInputElement>("username").addEventListener("change", (e) => localStorage.setItem("tinytibia.name", (e.target as HTMLInputElement).value));
} catch {
  /* storage unavailable */
}
